import React, { useState, useMemo, useEffect, Component } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Shield,
  Bell,
  Users,
  Trophy,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Save,
  RefreshCw,
  BarChart3,
  Layers,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Search,
  Plus,
  Edit,
  Trash2,
  Eye,
  Settings,
  X,
  Coins,
  Landmark,
  ArrowUpCircle,
  Upload,
  Image,
  Megaphone,
  ExternalLink,
  Tv,
  Receipt,
  History,
  TrendingUp,
  Calculator,
  Info,
  Zap,
  Clock
} from 'lucide-react'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  Cell
} from 'recharts'

import client from '../api/client'
import { parseBoliviaDate } from '../api/dateUtils'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Card from '../components/ui/Card'
import { useToast } from '../components/ui/Toast'
import Flag from '../components/ui/Flag'
import Skeleton from '../components/ui/Skeleton'
import useAuthStore from '../store/useAuthStore'

const FASE_NOMBRES = {
  grupos: 'Fase de Grupos',
  dieciseisavos: 'Dieciseisavos de Final',
  octavos: 'Octavos de Final',
  cuartos: 'Cuartos de Final',
  semifinal: 'Semifinales',
  tercer_puesto: 'Tercer Puesto',
  final: 'Final'
}

// Componente de tarjeta KPI del dashboard
function KPICard({ icon: Icon, label, value, color, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.3 }}
    >
      <Card className="p-5">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl bg-${color}/10 flex items-center justify-center`}>
            <Icon className={`w-6 h-6 text-${color}`} />
          </div>
          <div>
            <p className="text-text-muted text-sm">{label}</p>
            <p className="font-display font-bold text-2xl countdown-digit">{value}</p>
          </div>
        </div>
      </Card>
    </motion.div>
  )
}

// ==========================================
// UTILIDADES DE TABLAS (PAGINACIÓN, BÚSQUEDA, ORDENACIÓN)
// ==========================================
function useDataTable({
  data = [],
  searchFields = [],
  defaultSortField = '',
  defaultSortOrder = 'asc',
  defaultPageSize = 10,
  customFilter = null
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState(defaultSortField)
  const [sortOrder, setSortOrder] = useState(defaultSortOrder)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(defaultPageSize)

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, pageSize])

  const filteredData = useMemo(() => {
    let result = [...data]
    if (customFilter) {
      result = result.filter(customFilter)
    }
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase()
      result = result.filter(item => {
        return searchFields.some(field => {
          const value = field.split('.').reduce((obj, key) => obj?.[key], item)
          return String(value ?? '').toLowerCase().includes(term)
        })
      })
    }
    return result
  }, [data, searchTerm, searchFields, customFilter])

  const sortedData = useMemo(() => {
    if (!sortField) return filteredData
    const sorted = [...filteredData]
    sorted.sort((a, b) => {
      let valA = sortField.split('.').reduce((obj, key) => obj?.[key], a)
      let valB = sortField.split('.').reduce((obj, key) => obj?.[key], b)

      if (valA === null || valA === undefined) valA = ''
      if (valB === null || valB === undefined) valB = ''

      const numA = Number(valA)
      const numB = Number(valB)
      if (!isNaN(numA) && !isNaN(numB) && valA !== '' && valB !== '') {
        return sortOrder === 'asc' ? numA - numB : numB - numA
      }

      const dateA = Date.parse(valA)
      const dateB = Date.parse(valB)
      if (!isNaN(dateA) && !isNaN(dateB) && typeof valA === 'string' && valA.includes('-')) {
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA
      }

      const strA = String(valA).toLowerCase()
      const strB = String(valB).toLowerCase()
      if (strA < strB) return sortOrder === 'asc' ? -1 : 1
      if (strA > strB) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
    return sorted
  }, [filteredData, sortField, sortOrder])

  const paginatedData = useMemo(() => {
    if (pageSize === 'all') return sortedData
    const size = Number(pageSize)
    const start = (currentPage - 1) * size
    return sortedData.slice(start, start + size)
  }, [sortedData, currentPage, pageSize])

  const totalRecords = filteredData.length
  const totalPages = pageSize === 'all' ? 1 : Math.ceil(totalRecords / Number(pageSize))

  const requestSort = (field) => {
    let order = 'asc'
    if (sortField === field && sortOrder === 'asc') {
      order = 'desc'
    }
    setSortField(field)
    setSortOrder(order)
  }

  const startIndex = totalRecords === 0 ? 0 : (currentPage - 1) * (pageSize === 'all' ? totalRecords : Number(pageSize)) + 1
  const endIndex = pageSize === 'all' ? totalRecords : Math.min(currentPage * Number(pageSize), totalRecords)

  return {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    searchTerm,
    setSearchTerm,
    sortField,
    sortOrder,
    requestSort,
    paginatedData,
    totalPages,
    totalRecords,
    startIndex,
    endIndex,
    originalTotal: data.length
  }
}

function DataTableControls({
  pageSize,
  setPageSize,
  searchTerm,
  setSearchTerm,
  placeholder = "Buscar...",
  children = null
}) {
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
      <div className="flex items-center gap-2">
        <span className="text-xs text-text-muted">Mostrar</span>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(e.target.value)}
          className="bg-surface-card border border-border-default rounded-xl px-3 py-1.5 text-xs text-text-primary focus:border-cyan focus:outline-none cursor-pointer"
        >
          <option value="10">10</option>
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="100">100</option>
          <option value="all">Todos</option>
        </select>
        <span className="text-xs text-text-muted">registros</span>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
        {children}
        <div className="relative w-full sm:w-64">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-surface-card border border-border-default rounded-xl pl-9 pr-4 py-1.5 text-xs text-text-primary focus:border-cyan focus:outline-none transition-colors"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function DataTablePagination({
  currentPage,
  setCurrentPage,
  totalPages,
  startIndex,
  endIndex,
  totalRecords
}) {
  if (totalRecords === 0) return null

  const getPages = () => {
    const pages = []
    const start = Math.max(1, currentPage - 2)
    const end = Math.min(totalPages, currentPage + 2)
    for (let i = start; i <= end; i++) {
      pages.push(i)
    }
    return pages
  }

  const pages = getPages()

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-4 pt-4 border-t border-border-subtle">
      <div className="text-xs text-text-muted">
        Mostrando <span className="font-semibold text-text-secondary">{startIndex}</span> a{' '}
        <span className="font-semibold text-text-secondary">{endIndex}</span> de{' '}
        <span className="font-semibold text-text-secondary">{totalRecords}</span> registros
      </div>

      {totalPages > 1 && (
        <div className="flex items-center gap-1 self-end sm:self-auto select-none">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-2.5 py-1.5 rounded-lg border border-border-default text-xs font-medium hover:bg-surface-card-hover transition-colors disabled:opacity-50 disabled:hover:bg-transparent cursor-pointer"
          >
            Anterior
          </button>
          
          {pages[0] > 1 && (
            <>
              <button
                onClick={() => setCurrentPage(1)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors cursor-pointer ${
                  currentPage === 1
                    ? 'bg-cyan/10 border-cyan/30 text-cyan'
                    : 'border-border-default hover:bg-surface-card-hover text-text-secondary'
                }`}
              >
                1
              </button>
              {pages[0] > 2 && <span className="text-text-muted px-1">...</span>}
            </>
          )}

          {pages.map(page => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors cursor-pointer ${
                currentPage === page
                  ? 'bg-cyan/10 border-cyan/30 text-cyan'
                  : 'border-border-default hover:bg-surface-card-hover text-text-secondary'
              }`}
            >
              {page}
            </button>
          ))}

          {pages[pages.length - 1] < totalPages && (
            <>
              {pages[pages.length - 1] < totalPages - 1 && <span className="text-text-muted px-1">...</span>}
              <button
                onClick={() => setCurrentPage(totalPages)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors cursor-pointer ${
                  currentPage === totalPages
                    ? 'bg-cyan/10 border-cyan/30 text-cyan'
                    : 'border-border-default hover:bg-surface-card-hover text-text-secondary'
                }`}
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-2.5 py-1.5 rounded-lg border border-border-default text-xs font-medium hover:bg-surface-card-hover transition-colors disabled:opacity-50 disabled:hover:bg-transparent cursor-pointer"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  )
}

function SortableHeader({ field, currentField, sortOrder, label, onSort, className = "" }) {
  const isSorted = field === currentField
  return (
    <th
      onClick={() => onSort(field)}
      className={`cursor-pointer hover:bg-navy-dark/60 select-none group transition-colors px-4 py-3 ${className}`}
    >
      <div className="flex items-center gap-1.5">
        <span>{label}</span>
        <span className={`transition-opacity duration-200 text-[10px] ${isSorted ? 'opacity-100' : 'opacity-20 group-hover:opacity-60'}`}>
          {isSorted ? (sortOrder === 'asc' ? '▲' : '▼') : '▲'}
        </span>
      </div>
    </th>
  )
}

// ==========================================
// SECCIÓN: DASHBOARD
// ==========================================
function DashboardSection({ setActiveTab }) {
  const { user: loggedInUser } = useAuthStore()
  const { data, isLoading } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: async () => {
      const response = await client.get('/admin/dashboard')
      return response.data?.datos
    },
  })

  const stats = data || {
    total_usuarios: 0,
    total_equipos: 0,
    total_partidos: 0,
    total_predicciones: 0,
    total_ligas: 0
  }

  const hasPermission = (tab) => {
    if (loggedInUser?.rol === 'superadmin' || loggedInUser?.rol === 'admin') {
      return true
    }
    const permissionMap = {
      'resultados': 'gestionar_resultados',
      'partidos': 'gestionar_partidos',
      'usuarios': 'gestionar_usuarios',
      'recargas': 'aprobar_recargas',
      'retiros': 'aprobar_retiros',
      'transmisiones': 'gestionar_canales',
      'publicidad': 'gestionar_publicidad',
      'configuracion': 'gestionar_configuracion',
    }
    const requiredPermission = permissionMap[tab]
    return requiredPermission && loggedInUser?.permisos && loggedInUser.permisos[requiredPermission] === 1
  }

  return (
    <div className="space-y-6">
      <h2 className="font-display font-bold text-xl text-text-primary">
        Resumen General
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={Users}
          label="Usuarios Registrados"
          value={stats.total_usuarios?.toLocaleString() || '0'}
          color="cyan"
          delay={0}
        />
        <KPICard
          icon={Layers}
          label="Equipos Participantes"
          value={stats.total_equipos?.toLocaleString() || '0'}
          color="success"
          delay={0.1}
        />
        <KPICard
          icon={CheckCircle2}
          label="Predicciones Guardadas"
          value={stats.total_predicciones?.toLocaleString() || '0'}
          color="warning"
          delay={0.2}
        />
        <KPICard
          icon={Trophy}
          label="Comunidades Privadas"
          value={stats.total_ligas?.toLocaleString() || '0'}
          color="error"
          delay={0.3}
        />
      </div>

      {/* Acciones rápidas del administrador */}
      <Card className="p-6">
        <h3 className="font-display font-semibold text-lg mb-4">Acciones Rápidas</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-3">
          {hasPermission('resultados') && (
            <button onClick={() => setActiveTab('resultados')} className="glass p-4 rounded-xl text-left hover:bg-surface-card-hover transition-colors group">
              <Trophy className="w-5 h-5 text-cyan mb-2 group-hover:scale-110 transition-transform" />
              <p className="font-display font-medium text-sm">Registrar Resultado</p>
              <p className="text-text-muted text-xs mt-1">Ingresar marcador de un partido</p>
            </button>
          )}
          {hasPermission('partidos') && (
            <button onClick={() => setActiveTab('partidos')} className="glass p-4 rounded-xl text-left hover:bg-surface-card-hover transition-colors group">
              <CalendarDays className="w-5 h-5 text-success mb-2 group-hover:scale-110 transition-transform" />
              <p className="font-display font-medium text-sm">Gestionar Partidos</p>
              <p className="text-text-muted text-xs mt-1">Crear o editar fixtures del torneo</p>
            </button>
          )}
          {hasPermission('usuarios') && (
            <button onClick={() => setActiveTab('usuarios')} className="glass p-4 rounded-xl text-left hover:bg-surface-card-hover transition-colors group">
              <Users className="w-5 h-5 text-warning mb-2 group-hover:scale-110 transition-transform" />
              <p className="font-display font-medium text-sm">Gestionar Usuarios</p>
              <p className="text-text-muted text-xs mt-1">Administrar cuentas y roles</p>
            </button>
          )}
          {hasPermission('recargas') && (
            <button onClick={() => setActiveTab('recargas')} className="glass p-4 rounded-xl text-left hover:bg-surface-card-hover transition-colors group">
              <Landmark className="w-5 h-5 text-emerald-400 mb-2 group-hover:scale-110 transition-transform" />
              <p className="font-display font-medium text-sm">Gestionar Recargas</p>
              <p className="text-text-muted text-xs mt-1">Aprobar depósitos de clientes</p>
            </button>
          )}
          {hasPermission('retiros') && (
            <button onClick={() => setActiveTab('retiros')} className="glass p-4 rounded-xl text-left hover:bg-surface-card-hover transition-colors group">
              <ArrowUpCircle className="w-5 h-5 text-orange-400 mb-2 group-hover:scale-110 transition-transform" />
              <p className="font-display font-medium text-sm">Gestionar Retiros</p>
              <p className="text-text-muted text-xs mt-1">Aprobar retiros de efectivo</p>
            </button>
          )}
          {hasPermission('transmisiones') && (
            <button onClick={() => setActiveTab('transmisiones')} className="glass p-4 rounded-xl text-left hover:bg-surface-card-hover transition-colors group">
              <Tv className="w-5 h-5 text-cyan mb-2 group-hover:scale-110 transition-transform" />
              <p className="font-display font-medium text-sm">Canales en Vivo</p>
              <p className="text-text-muted text-xs mt-1">Gestionar transmisiones y streaming</p>
            </button>
          )}
          {hasPermission('publicidad') && (
            <button onClick={() => setActiveTab('publicidad')} className="glass p-4 rounded-xl text-left hover:bg-surface-card-hover transition-colors group">
              <Megaphone className="w-5 h-5 text-purple-400 mb-2 group-hover:scale-110 transition-transform" />
              <p className="font-display font-medium text-sm">Publicidad</p>
              <p className="text-text-muted text-xs mt-1">Gestionar banners de auspiciadores</p>
            </button>
          )}
          {hasPermission('configuracion') && (
            <button onClick={() => setActiveTab('configuracion')} className="glass p-4 rounded-xl text-left hover:bg-surface-card-hover transition-colors group">
              <Settings className="w-5 h-5 text-error mb-2 group-hover:scale-110 transition-transform" />
              <p className="font-display font-medium text-sm">Configuración General</p>
              <p className="text-text-muted text-xs mt-1">Editar reglas de puntos y fecha límite</p>
            </button>
          )}
        </div>
      </Card>
    </div>
  )
}

// ==========================================
// SECCIÓN: REGISTRO DE MARCADORES (RESULTADOS)
// ==========================================
function ResultadosSection() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [partidoSeleccionado, setPartidoSeleccionado] = useState(null)
  const [golesLocal, setGolesLocal] = useState('')
  const [golesVisitante, setGolesVisitante] = useState('')
  const [estadoSeleccionado, setEstadoSeleccionado] = useState('finalizado')
  const [mostrarFinalizados, setMostrarFinalizados] = useState(false)
  const [ganadorPenalesId, setGanadorPenalesId] = useState('')

  const getTeamCode = (bandera, nombre) => {
    const codes = {
      'ma': 'MAR', 'pe': 'PER', 'nl': 'NED', 'ca': 'CAN',
      'be': 'BEL', 'ec': 'ECU', 'py': 'PAR', 'ci': 'CIV',
      'us': 'USA', 'cl': 'CHI', 'pa': 'PAN', 'nz': 'NZL',
      'fr': 'FRA', 'co': 'COL', 'sa': 'KSA', 'au': 'AUS',
      'br': 'BRA', 'tr': 'TUR', 'cn': 'CHN', 'bh': 'BHR',
      'ar': 'ARG', 'ng': 'NGA', 'dz': 'ALG', 
      'es': 'ESP', 'bo': 'BOL', 'al': 'ALB', 'qa': 'QAT',
      'pt': 'POR', 'ir': 'IRN', 'cr': 'CRC', 'dk': 'DEN',
      'de': 'GER', 'uy': 'URU', 'kr': 'KOR', 'cm': 'CMR',
      'en': 'ENG', 'sn': 'SEN', 'jp': 'JPN', 'hn': 'HON',
      'it': 'ITA', 'mx': 'MEX', 'hr': 'CRO', 'rs': 'SRB',
      'ch': 'SUI', 'gh': 'GHA', 'eg': 'EGY', 'tn': 'TUN',
      'iq': 'IRQ', 'no': 'NOR', 'se': 'SWE', 'tz': 'TAN'
    }
    const key = String(bandera || '').toLowerCase().trim();
    if (codes[key]) return codes[key];
    return nombre ? nombre.substring(0, 3).toUpperCase() : key.toUpperCase();
  }

  const incrementLocal = () => {
    const current = parseInt(golesLocal) || 0
    if (current < 20) setGolesLocal(String(current + 1))
  }
  const decrementLocal = () => {
    const current = parseInt(golesLocal) || 0
    if (current > 0) setGolesLocal(String(current - 1))
  }
  const incrementVisitante = () => {
    const current = parseInt(golesVisitante) || 0
    if (current < 20) setGolesVisitante(String(current + 1))
  }
  const decrementVisitante = () => {
    const current = parseInt(golesVisitante) || 0
    if (current > 0) setGolesVisitante(String(current - 1))
  }


  // Obtener todos los partidos del torneo
  const { data: matchesData, isLoading } = useQuery({
    queryKey: ['admin-all-matches'],
    queryFn: async () => {
      const response = await client.get('/matches')
      return response.data?.datos || []
    },
  })

  const matches = matchesData || []
  const partidosRaw = useMemo(() => {
    return matches.map(m => {
      const rawDate = parseBoliviaDate(m.fecha_partido);
      return {
        id: m.id,
        equipo_local: m.equipo_local_nombre,
        equipo_visitante: m.equipo_visitante_nombre,
        equipo_local_id: m.equipo_local_id,
        equipo_visitante_id: m.equipo_visitante_id,
        equipo_ganador_id: m.equipo_ganador_id,
        fase: m.fase,
        bandera_local: m.equipo_local_bandera,
        bandera_visitante: m.equipo_visitante_bandera,
        grupo: m.grupo_nombre || FASE_NOMBRES[m.fase] || m.fase,
        fecha_partido: m.fecha_partido,
        fecha: rawDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
        fechaRaw: rawDate,
        estado: m.estado,
        goles_local: m.goles_local,
        goles_visitante: m.goles_visitante
      };
    });
  }, [matches])

  const {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    searchTerm,
    setSearchTerm,
    sortField,
    sortOrder,
    requestSort,
    paginatedData: paginatedPartidos,
    totalPages,
    totalRecords,
    startIndex,
    endIndex
  } = useDataTable({
    data: partidosRaw,
    searchFields: ['equipo_local', 'equipo_visitante', 'grupo', 'estado'],
    defaultSortField: 'fechaRaw',
    defaultSortOrder: 'asc',
    customFilter: (p) => mostrarFinalizados || p.estado !== 'finalizado'
  })

  const registrarResultado = useMutation({
    mutationFn: async (data) => {
      const response = await client.post('/admin/results', data)
      return response.data
    },
    onSuccess: () => {
      toast.success('Resultado y estado registrados correctamente')
      setPartidoSeleccionado(null)
      setGolesLocal('')
      setGolesVisitante('')
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['admin-all-matches'] })
    },
    onError: (error) => {
      toast.error(error.response?.data?.mensaje || 'Error al registrar resultado')
    },
  })

  const recalcular = useMutation({
    mutationFn: async () => {
      const response = await client.post('/admin/recalculate')
      return response.data
    },
    onSuccess: () => {
      toast.success('Puntuaciones recalculadas con éxito')
      queryClient.invalidateQueries({ queryKey: ['groups'] })
    },
    onError: (error) => {
      toast.error(error.response?.data?.mensaje || 'Error al recalcular')
    },
  })

  const handleSelectMatch = (partido) => {
    setPartidoSeleccionado(partido)
    setGolesLocal(partido.goles_local !== null ? String(partido.goles_local) : '0')
    setGolesVisitante(partido.goles_visitante !== null ? String(partido.goles_visitante) : '0')
    setEstadoSeleccionado(partido.estado)
    setGanadorPenalesId(partido.equipo_ganador_id ? String(partido.equipo_ganador_id) : '')
  }

  const handleSubmitResultado = () => {
    if (!partidoSeleccionado) return
    
    const isKnockout = partidoSeleccionado.fase !== 'grupos'
    const isDraw = parseInt(golesLocal) === parseInt(golesVisitante)
    
    if (isKnockout && isDraw && estadoSeleccionado === 'finalizado') {
      if (!ganadorPenalesId) {
        toast.error('Es obligatorio seleccionar el equipo que clasifica en penales.')
        return
      }
    }

    registrarResultado.mutate({
      partido_id: partidoSeleccionado.id,
      goles_local: golesLocal === '' ? null : parseInt(golesLocal),
      goles_visitante: golesVisitante === '' ? null : parseInt(golesVisitante),
      estado: estadoSeleccionado,
      equipo_ganador_id: (isKnockout && isDraw && estadoSeleccionado === 'finalizado') ? parseInt(ganadorPenalesId) : null
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-64 rounded-xl" />
          <Skeleton className="h-10 w-36 rounded-xl" />
        </div>
        <Card className="p-6">
          <div className="space-y-4">
            <Skeleton className="h-12 w-full rounded-xl" />
            <Skeleton className="h-16 w-full rounded-xl" />
            <Skeleton className="h-16 w-full rounded-xl" />
            <Skeleton className="h-16 w-full rounded-xl" />
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="font-display font-bold text-xl text-text-primary">Registrar Resultados Reales</h2>
        <Button
          variant="secondary"
          size="sm"
          icon={RefreshCw}
          loading={recalcular.isPending}
          onClick={() => {
            if (window.confirm('¿Recalcular todas las puntuaciones? Esto procesará de nuevo todos los partidos finalizados.')) {
              recalcular.mutate()
            }
          }}
        >
          Recalcular Puntos
        </Button>
      </div>

      <AnimatePresence>
        {partidoSeleccionado ? (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <Card className="p-6 border-glow-cyan">
              <h3 className="font-display font-semibold text-lg mb-4">
                Ingresar Marcador
              </h3>
              <div className="flex items-center justify-center gap-2 md:gap-10 mb-8 select-none">
                <div className="flex flex-col items-center w-20 md:w-36 shrink-0">
                  <div className="w-16 h-16 md:w-28 md:h-28 flex items-center justify-center bg-navy-dark/50 border border-border-subtle rounded-xl md:rounded-2xl p-2 md:p-4 shadow-md transition-all hover:border-cyan/30">
                    <Flag code={partidoSeleccionado.bandera_local} className="w-12 h-8 md:w-20 md:h-13 object-cover rounded md:rounded-lg shadow-sm" />
                  </div>
                  <p className="font-display font-bold text-xs md:text-lg text-text-primary mt-2 text-center leading-tight">
                    {partidoSeleccionado.equipo_local}
                  </p>
                </div>
                <div className="flex flex-col items-center gap-1.5 md:gap-2">
                  <button type="button" onClick={incrementLocal} className="w-8 h-8 rounded-full bg-navy-dark/40 hover:bg-cyan/15 border border-border-subtle flex items-center justify-center"><ChevronUp className="w-4 h-4" /></button>
                  <div className="w-12 h-14 bg-navy-dark/60 border border-border-subtle rounded-xl flex items-center justify-center">
                    <input type="number" value={golesLocal} onChange={(e) => setGolesLocal(e.target.value)} className="w-full bg-transparent text-center text-2xl font-mono font-bold text-cyan focus:outline-none" />
                  </div>
                  <button type="button" onClick={decrementLocal} className="w-8 h-8 rounded-full bg-navy-dark/40 hover:bg-cyan/15 border border-border-subtle flex items-center justify-center"><ChevronDown className="w-4 h-4" /></button>
                </div>
                <div className="text-text-secondary font-display font-bold text-xl px-1">VS</div>
                <div className="flex flex-col items-center gap-1.5 md:gap-2">
                  <button type="button" onClick={incrementVisitante} className="w-8 h-8 rounded-full bg-navy-dark/40 hover:bg-cyan/15 border border-border-subtle flex items-center justify-center"><ChevronUp className="w-4 h-4" /></button>
                  <div className="w-12 h-14 bg-navy-dark/60 border border-border-subtle rounded-xl flex items-center justify-center">
                    <input type="number" value={golesVisitante} onChange={(e) => setGolesVisitante(e.target.value)} className="w-full bg-transparent text-center text-2xl font-mono font-bold text-cyan focus:outline-none" />
                  </div>
                  <button type="button" onClick={decrementVisitante} className="w-8 h-8 rounded-full bg-navy-dark/40 hover:bg-cyan/15 border border-border-subtle flex items-center justify-center"><ChevronDown className="w-4 h-4" /></button>
                </div>
                <div className="flex flex-col items-center w-20 md:w-36 shrink-0">
                  <div className="w-16 h-16 md:w-28 md:h-28 flex items-center justify-center bg-navy-dark/50 border border-border-subtle rounded-xl md:rounded-2xl p-2 md:p-4 shadow-md transition-all hover:border-cyan/30">
                    <Flag code={partidoSeleccionado.bandera_visitante} className="w-12 h-8 md:w-20 md:h-13 object-cover rounded md:rounded-lg shadow-sm" />
                  </div>
                  <p className="font-display font-bold text-xs md:text-lg text-text-primary mt-2 text-center leading-tight">
                    {partidoSeleccionado.equipo_visitante}
                  </p>
                </div>
              </div>
              <div className="max-w-xs mx-auto mb-6">
                <select value={estadoSeleccionado} onChange={(e) => setEstadoSeleccionado(e.target.value)} className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm font-semibold focus:border-cyan focus:outline-none text-center">
                  <option value="programado">📅 Programado</option>
                  <option value="en_juego">🔥 En Juego</option>
                  <option value="finalizado">🏆 Finalizado</option>
                </select>
              </div>
              {partidoSeleccionado.fase !== 'grupos' && parseInt(golesLocal) === parseInt(golesVisitante) && estadoSeleccionado === 'finalizado' && (
                <div className="max-w-xs mx-auto mb-6">
                  <label className="text-xs font-semibold text-warning block mb-1.5 text-center">
                    🏆 Clasificado en Penales (Obligatorio)
                  </label>
                  <select
                    value={ganadorPenalesId}
                    onChange={(e) => setGanadorPenalesId(e.target.value)}
                    className="w-full bg-surface-card border border-warning/35 rounded-xl p-2.5 text-sm font-semibold focus:border-cyan focus:outline-none text-center text-text-primary"
                  >
                    <option value="">Seleccione el ganador...</option>
                    <option value={partidoSeleccionado.equipo_local_id}>{partidoSeleccionado.equipo_local}</option>
                    <option value={partidoSeleccionado.equipo_visitante_id}>{partidoSeleccionado.equipo_visitante}</option>
                  </select>
                </div>
              )}
              <div className="flex gap-3 justify-center">
                <Button variant="primary" icon={Save} loading={registrarResultado.isPending} onClick={handleSubmitResultado}>Guardar</Button>
                <Button variant="ghost" onClick={() => setPartidoSeleccionado(null)}>Cancelar</Button>
              </div>
            </Card>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <Card className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border-subtle pb-4 mb-4">
          <h3 className="font-display font-semibold text-sm text-text-secondary uppercase tracking-wider">
            {mostrarFinalizados ? 'Todos los Partidos (Edición)' : 'Partidos Pendientes de Resultado'}
          </h3>
          <label className="flex items-center gap-2 text-xs font-semibold text-text-muted cursor-pointer hover:text-text-primary transition-colors select-none">
            <input type="checkbox" checked={mostrarFinalizados} onChange={(e) => { setMostrarFinalizados(e.target.checked); setPartidoSeleccionado(null); }} className="rounded border-border-default bg-surface-card text-cyan focus:ring-cyan focus:ring-offset-navy" />
            <span>Mostrar partidos finalizados</span>
          </label>
        </div>

        <DataTableControls
          pageSize={pageSize}
          setPageSize={setPageSize}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Buscar partidos..."
        />

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left font-body border-collapse">
            <thead className="bg-navy-dark/40 text-text-muted text-xs uppercase tracking-wider border-b border-border-subtle">
              <tr>
                <SortableHeader field="fechaRaw" currentField={sortField} sortOrder={sortOrder} label="Fecha" onSort={requestSort} />
                <SortableHeader field="grupo" currentField={sortField} sortOrder={sortOrder} label="Grupo / Fase" onSort={requestSort} />
                <SortableHeader field="equipo_local" currentField={sortField} sortOrder={sortOrder} label="Partido" onSort={requestSort} />
                <SortableHeader field="goles_local" currentField={sortField} sortOrder={sortOrder} label="Marcador" onSort={requestSort} className="text-center" />
                <SortableHeader field="estado" currentField={sortField} sortOrder={sortOrder} label="Estado" onSort={requestSort} />
                <th className="px-4 py-3 text-right">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle/50">
              {paginatedPartidos.map((partido) => (
                <tr key={partido.id} className={`hover:bg-surface-card-hover transition-colors cursor-pointer ${partidoSeleccionado?.id === partido.id ? 'bg-cyan/5 border-l-2 border-l-cyan' : ''}`} onClick={() => handleSelectMatch(partido)}>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-text-secondary font-mono">{partido.fecha}</td>
                  <td className="px-4 py-3 whitespace-nowrap"><span className="text-xs font-mono px-2 py-0.5 rounded bg-navy-dark text-text-muted">Grupo {partido.grupo || 'Eliminatorias'}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Flag code={partido.bandera_local} className="w-5 h-3.5 object-cover rounded shadow-sm shrink-0" />
                      <span className="font-display font-medium text-xs text-text-primary">{partido.equipo_local}</span>
                      <span className="text-text-muted text-xs mx-0.5">vs</span>
                      <span className="font-display font-medium text-xs text-text-primary">{partido.equipo_visitante}</span>
                      <Flag code={partido.bandera_visitante} className="w-5 h-3.5 object-cover rounded shadow-sm shrink-0" />
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-xs font-bold text-center">
                    {partido.estado === 'en_juego' || partido.estado === 'finalizado' || (partido.goles_local !== null && partido.goles_local !== undefined) ? (
                      <span className={`px-2 py-0.5 rounded text-[11px] border inline-block ${partido.estado === 'finalizado' ? 'bg-success/5 border-success/20 text-success' : partido.estado === 'en_juego' ? 'bg-warning/10 border-warning/20 text-warning animate-pulse' : 'bg-navy-dark border-border-subtle text-text-muted'}`}>
                        {partido.goles_local ?? 0} - {partido.goles_visitante ?? 0}
                      </span>
                    ) : <span className="text-text-muted">-</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`text-[10px] font-semibold font-display uppercase px-2 py-0.5 rounded-full flex items-center gap-1 border w-fit ${partido.estado === 'finalizado' ? 'bg-success/5 border-success/20 text-success' : partido.estado === 'en_juego' ? 'bg-warning/10 border-warning/20 text-warning animate-pulse' : partido.estado === 'suspendido' ? 'bg-error/5 border-error/20 text-error' : 'bg-navy-dark border-border-subtle text-text-muted'}`}>
                      {partido.estado === 'en_juego' && <span className="w-1 h-1 rounded-full bg-warning animate-pulse" />}
                      {partido.estado === 'en_juego' ? 'En Vivo' : partido.estado.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <button onClick={(e) => { e.stopPropagation(); handleSelectMatch(partido); }} className="text-cyan hover:text-cyan-hover font-semibold text-xs flex items-center gap-1 transition-colors ml-auto w-fit">
                      <Edit className="w-3.5 h-3.5" /> Editar
                    </button>
                  </td>
                </tr>
              ))}
              {totalRecords === 0 && (
                <tr><td colSpan="6" className="text-center py-8 text-text-muted text-sm">No se encontraron partidos.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <DataTablePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          startIndex={startIndex}
          endIndex={endIndex}
          totalRecords={totalRecords}
        />
      </Card>
    </div>
  )
}

// ==========================================
// SECCIÓN: PARTIDOS (CRUD)
// ==========================================
function PartidosSection({ teams, groups }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [showModal, setShowModal] = useState(false)
  const [editingPartido, setEditingPartido] = useState(null)
  
  // Datos del formulario
  const [form, setForm] = useState({
    grupo_id: '',
    equipo_local_id: '',
    equipo_visitante_id: '',
    fecha_partido: '',
    fase: 'grupos',
    estado: 'programado',
    marcador_completo: 0
  })

  // Obtener todos los partidos ordenados
  const { data: matchesData, isLoading } = useQuery({
    queryKey: ['admin-all-matches'],
    queryFn: async () => {
      const response = await client.get('/matches')
      return response.data?.datos || []
    }
  })
  const partidos = matchesData || []

  // Ordenar partidos: 'en_juego' primero, luego 'programado', y por último finalizado/suspendido.
  // Dentro de 'en_juego'/'programado' se ordenan ascendentemente (más próximos primero).
  // Dentro de finalizados se ordenan descendentemente (más recientes primero).
  const sortedPartidos = useMemo(() => {
    return [...partidos].sort((a, b) => {
      const getPriority = (estado) => {
        if (estado === 'en_juego') return 0
        if (estado === 'programado') return 1
        return 2
      }
      
      const priorityA = getPriority(a.estado)
      const priorityB = getPriority(b.estado)
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB
      }
      
      const dateA = a.fecha_partido ? new Date(a.fecha_partido.replace(' ', 'T')).getTime() : 0
      const dateB = b.fecha_partido ? new Date(b.fecha_partido.replace(' ', 'T')).getTime() : 0
      
      if (priorityA === 0 || priorityA === 1) {
        return dateA - dateB
      }
      return dateB - dateA
    })
  }, [partidos])

  const {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    searchTerm,
    setSearchTerm,
    sortField,
    sortOrder,
    requestSort,
    paginatedData: paginatedPartidos,
    totalPages,
    totalRecords,
    startIndex,
    endIndex
  } = useDataTable({
    data: sortedPartidos,
    searchFields: ['equipo_local_nombre', 'equipo_visitante_nombre', 'fase', 'grupo_nombre', 'estado'],
    defaultSortField: '',
    defaultSortOrder: 'asc'
  })

  // Mutaciones CRUD
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      if (editingPartido) {
        return (await client.put(`/admin/matches/${editingPartido.id}`, data)).data
      }
      return (await client.post('/admin/matches', data)).data
    },
    onSuccess: () => {
      toast.success(editingPartido ? 'Partido actualizado' : 'Partido creado')
      setShowModal(false)
      setEditingPartido(null)
      queryClient.invalidateQueries({ queryKey: ['admin-all-matches'] })
      queryClient.invalidateQueries({ queryKey: ['groups'] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.mensaje || 'Error al guardar partido')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return (await client.delete(`/admin/matches/${id}`)).data
    },
    onSuccess: () => {
      toast.success('Partido eliminado')
      queryClient.invalidateQueries({ queryKey: ['admin-all-matches'] })
      queryClient.invalidateQueries({ queryKey: ['groups'] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.mensaje || 'Error al eliminar partido')
    }
  })

  const handleOpenCreate = () => {
    setEditingPartido(null)
    setForm({
      grupo_id: '',
      equipo_local_id: '',
      equipo_visitante_id: '',
      fecha_partido: '',
      fase: 'grupos',
      estado: 'programado',
      marcador_completo: 0
    })
    setShowModal(true)
  }

  const handleOpenEdit = (p) => {
    setEditingPartido(p)
    const date = new Date(p.fecha_partido)
    const offset = date.getTimezoneOffset()
    date.setMinutes(date.getMinutes() - offset)
    const formattedDate = date.toISOString().slice(0, 16)

    setForm({
      grupo_id: p.grupo_id || '',
      equipo_local_id: p.equipo_local_id,
      equipo_visitante_id: p.equipo_visitante_id,
      fecha_partido: formattedDate,
      fase: p.fase,
      estado: p.estado,
      marcador_completo: p.marcador_completo ? 1 : 0
    })
    setShowModal(true)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!form.equipo_local_id || !form.equipo_visitante_id || !form.fecha_partido) {
      toast.warning('Por favor completa todos los campos obligatorios.')
      return
    }
    if (form.equipo_local_id === form.equipo_visitante_id) {
      toast.warning('Un equipo no puede jugar contra sí mismo.')
      return
    }
    saveMutation.mutate(form)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-64 rounded-xl" />
          <Skeleton className="h-10 w-36 rounded-xl" />
        </div>
        <Card className="p-6">
          <div className="space-y-4">
            <Skeleton className="h-12 w-full rounded-xl" />
            <Skeleton className="h-16 w-full rounded-xl" />
            <Skeleton className="h-16 w-full rounded-xl" />
            <Skeleton className="h-16 w-full rounded-xl" />
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="font-display font-bold text-xl text-text-primary">Gestión de Partidos</h2>
        <Button size="sm" icon={Plus} onClick={handleOpenCreate}>Crear Partido</Button>
      </div>

      <Card className="p-4">
        <DataTableControls
          pageSize={pageSize}
          setPageSize={setPageSize}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Buscar partidos..."
        />

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-subtle">
                <SortableHeader field="fase" currentField={sortField} sortOrder={sortOrder} label="Fase / Grupo" onSort={requestSort} className="text-left px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase" />
                <SortableHeader field="equipo_local_nombre" currentField={sortField} sortOrder={sortOrder} label="Partido" onSort={requestSort} className="text-left px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase" />
                <SortableHeader field="fecha_partido" currentField={sortField} sortOrder={sortOrder} label="Fecha" onSort={requestSort} className="text-left px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase" />
                <SortableHeader field="estado" currentField={sortField} sortOrder={sortOrder} label="Estado" onSort={requestSort} className="text-left px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase" />
                <th className="text-right px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {paginatedPartidos.map((p) => (
                <tr key={p.id} className="hover:bg-surface-card-hover transition-colors">
                  <td className="px-4 py-3 text-sm font-medium">
                    <span className="px-2 py-0.5 rounded bg-navy text-cyan text-xs capitalize">
                      {FASE_NOMBRES[p.fase] || p.fase}
                    </span>
                    {p.grupo_nombre && <span className="ml-1 text-xs text-text-muted">Grupo {p.grupo_nombre}</span>}
                    {p.marcador_completo === 1 && (
                      <span className="ml-1 px-1.5 py-0.5 rounded bg-success/15 text-success text-[10px] font-bold" title="Bono requiere marcador exacto de ambos equipos">
                        Marcador Completo
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Flag code={p.equipo_local_bandera} className="w-4 h-3 object-cover rounded shadow-sm shrink-0" />
                      <span className="font-semibold">{p.equipo_local_nombre}</span>
                      <span className="mx-2 text-text-muted font-mono">
                        {p.estado === 'finalizado' ? `${p.goles_local} - ${p.goles_visitante}` : 'vs'}
                      </span>
                      <Flag code={p.equipo_visitante_bandera} className="w-4 h-3 object-cover rounded shadow-sm shrink-0" />
                      <span className="font-semibold">{p.equipo_visitante_nombre}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-text-secondary">
                    {parseBoliviaDate(p.fecha_partido).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-4 py-3 text-sm capitalize">
                    <span className={`w-2 h-2 inline-block rounded-full mr-1.5 ${
                      p.estado === 'finalizado' ? 'bg-success' : p.estado === 'en_juego' ? 'bg-warning animate-pulse' : 'bg-text-muted'
                    }`} />
                    {p.estado.replace('_', ' ')}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => handleOpenEdit(p)} className="p-1 hover:text-cyan transition-colors" title="Editar">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => window.confirm('¿Eliminar este partido?') && deleteMutation.mutate(p.id)} 
                        className="p-1 hover:text-error transition-colors" 
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {totalRecords === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-8 text-text-muted text-sm">
                    No se encontraron partidos.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <DataTablePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          startIndex={startIndex}
          endIndex={endIndex}
          totalRecords={totalRecords}
        />
      </Card>

      {/* Modal CRUD */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-navy-dark/80 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glass max-w-lg w-full rounded-2xl overflow-hidden shadow-2xl border border-border-default">
            <div className="p-5 border-b border-border-subtle flex justify-between items-center">
              <h3 className="font-display font-bold text-lg text-text-primary">
                {editingPartido ? 'Editar Partido' : 'Crear Partido'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-text-muted hover:text-text-primary">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-text-muted block mb-1">Equipo Local *</label>
                  <select 
                    value={form.equipo_local_id} 
                    onChange={e => setForm(prev => ({ ...prev, equipo_local_id: e.target.value }))}
                    className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none"
                  >
                    <option value="">Selecciona...</option>
                    {teams.map(t => (
                      <option key={t.id} value={t.id}>{t.bandera ? t.bandera.toUpperCase() : ''} {t.nombre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-text-muted block mb-1">Equipo Visitante *</label>
                  <select 
                    value={form.equipo_visitante_id} 
                    onChange={e => setForm(prev => ({ ...prev, equipo_visitante_id: e.target.value }))}
                    className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none"
                  >
                    <option value="">Selecciona...</option>
                    {teams.map(t => (
                      <option key={t.id} value={t.id}>{t.bandera ? t.bandera.toUpperCase() : ''} {t.nombre}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-text-muted block mb-1">Fase del Torneo *</label>
                  <select 
                    value={form.fase} 
                    onChange={e => setForm(prev => ({ ...prev, fase: e.target.value }))}
                    className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none"
                  >
                    <option value="grupos">Fase de Grupos</option>
                    <option value="dieciseisavos">Dieciseisavos de Final</option>
                    <option value="octavos">Octavos de Final</option>
                    <option value="cuartos">Cuartos de Final</option>
                    <option value="semifinal">Semifinales</option>
                    <option value="tercer_puesto">Tercer Puesto</option>
                    <option value="final">Final</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-text-muted block mb-1">Grupo (Solo Fase Grupos)</label>
                  <select 
                    value={form.grupo_id} 
                    onChange={e => setForm(prev => ({ ...prev, grupo_id: e.target.value }))}
                    className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none"
                  >
                    <option value="">Ninguno (Fase Eliminatoria)</option>
                    {groups.map(g => (
                      <option key={g.id} value={g.id}>Grupo {g.nombre}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-text-muted block mb-1">Fecha y Hora *</label>
                  <input 
                    type="datetime-local" 
                    value={form.fecha_partido}
                    onChange={e => setForm(prev => ({ ...prev, fecha_partido: e.target.value }))}
                    className="w-full bg-surface-card border border-border-default rounded-xl p-2 text-sm focus:border-cyan focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-text-muted block mb-1">Estado del Partido</label>
                  <select 
                    value={form.estado} 
                    onChange={e => setForm(prev => ({ ...prev, estado: e.target.value }))}
                    className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none"
                  >
                    <option value="programado">Programado</option>
                    <option value="en_juego">En Juego</option>
                    <option value="finalizado">Finalizado</option>
                    <option value="suspendido">Suspendido</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-1.5 pt-1.5 pb-2.5">
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="marcador-completo-check" 
                    checked={form.marcador_completo === 1}
                    onChange={e => setForm(prev => ({ ...prev, marcador_completo: e.target.checked ? 1 : 0 }))}
                    className="rounded bg-navy border-border-default text-cyan focus:ring-cyan w-4 h-4 cursor-pointer"
                  />
                  <label htmlFor="marcador-completo-check" className="text-xs font-semibold text-text-primary cursor-pointer select-none">
                    Evaluar Marcador Completo (Bono)
                  </label>
                </div>
                <span className="text-[10px] text-text-muted block pl-6">
                  Si está activado, los usuarios deben acertar los goles exactos de ambos equipos para recibir el bono (multiplicador). Si no, solo se consideran los goles de su selección.
                </span>
              </div>

              <div className="pt-4 border-t border-border-subtle flex justify-end gap-3">
                <Button type="button" variant="ghost" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button type="submit" variant="primary" loading={saveMutation.isPending}>Guardar</Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  )
}

// ==========================================
// SECCIÓN: EQUIPOS (CRUD)
// ==========================================
function EquiposSection({ groups }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [showModal, setShowModal] = useState(false)
  const [editingTeam, setEditingTeam] = useState(null)
  
  const [form, setForm] = useState({
    nombre: '',
    grupo_id: '',
    bandera: '',
    confederacion: 'CONMEBOL'
  })

  // Obtener equipos
  const { data: teamsData } = useQuery({
    queryKey: ['admin-all-teams'],
    queryFn: async () => {
      const response = await client.get('/teams')
      return response.data?.datos || []
    }
  })
  const teams = teamsData || []

  const {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    searchTerm,
    setSearchTerm,
    sortField,
    sortOrder,
    requestSort,
    paginatedData: paginatedTeams,
    totalPages,
    totalRecords,
    startIndex,
    endIndex
  } = useDataTable({
    data: teams,
    searchFields: ['nombre', 'grupo_nombre', 'confederacion', 'bandera'],
    defaultSortField: 'nombre',
    defaultSortOrder: 'asc'
  })

  const saveMutation = useMutation({
    mutationFn: async (data) => {
      if (editingTeam) {
        return (await client.put(`/admin/teams/${editingTeam.id}`, data)).data
      }
      return (await client.post('/admin/teams', data)).data
    },
    onSuccess: () => {
      toast.success(editingTeam ? 'Equipo actualizado' : 'Equipo creado')
      setShowModal(false)
      setEditingTeam(null)
      queryClient.invalidateQueries({ queryKey: ['admin-all-teams'] })
      queryClient.invalidateQueries({ queryKey: ['groups'] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.mensaje || 'Error al guardar equipo')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return (await client.delete(`/admin/teams/${id}`)).data
    },
    onSuccess: () => {
      toast.success('Equipo eliminado')
      queryClient.invalidateQueries({ queryKey: ['admin-all-teams'] })
      queryClient.invalidateQueries({ queryKey: ['groups'] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.mensaje || 'Error al eliminar equipo')
    }
  })

  const handleOpenCreate = () => {
    setEditingTeam(null)
    setForm({ nombre: '', grupo_id: '', bandera: '', confederacion: 'CONMEBOL' })
    setShowModal(true)
  }

  const handleOpenEdit = (t) => {
    setEditingTeam(t)
    setForm({
      nombre: t.nombre,
      grupo_id: t.grupo_id,
      bandera: t.bandera || '',
      confederacion: t.confederacion
    })
    setShowModal(true)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="font-display font-bold text-xl text-text-primary">Gestión de Equipos</h2>
        <Button size="sm" icon={Plus} onClick={handleOpenCreate}>Crear Equipo</Button>
      </div>

      <Card className="p-4">
        <DataTableControls
          pageSize={pageSize}
          setPageSize={setPageSize}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Buscar equipos..."
        />

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-subtle">
                <SortableHeader field="bandera" currentField={sortField} sortOrder={sortOrder} label="Bandera" onSort={requestSort} className="text-left px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase" />
                <SortableHeader field="nombre" currentField={sortField} sortOrder={sortOrder} label="Nombre" onSort={requestSort} className="text-left px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase" />
                <SortableHeader field="grupo_nombre" currentField={sortField} sortOrder={sortOrder} label="Grupo" onSort={requestSort} className="text-left px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase" />
                <SortableHeader field="confederacion" currentField={sortField} sortOrder={sortOrder} label="Confederación" onSort={requestSort} className="text-left px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase" />
                <th className="text-right px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {paginatedTeams.map((t) => (
                <tr key={t.id} className="hover:bg-surface-card-hover transition-colors">
                  <td className="px-4 py-3">
                    <Flag code={t.bandera} className="w-8 h-5 object-cover rounded shadow" />
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold">{t.nombre}</td>
                  <td className="px-4 py-3 text-sm font-mono">Grupo {t.grupo_nombre}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className="px-2 py-0.5 rounded bg-surface-card text-text-secondary font-semibold font-display">
                      {t.confederacion}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => handleOpenEdit(t)} className="p-1 hover:text-cyan transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => window.confirm('¿Eliminar este equipo?') && deleteMutation.mutate(t.id)} className="p-1 hover:text-error transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {totalRecords === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-8 text-text-muted text-sm">
                    No se encontraron equipos.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <DataTablePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          startIndex={startIndex}
          endIndex={endIndex}
          totalRecords={totalRecords}
        />
      </Card>

      {/* Modal CRUD */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-navy-dark/80 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glass max-w-md w-full rounded-2xl overflow-hidden border border-border-default">
            <div className="p-5 border-b border-border-subtle flex justify-between items-center">
              <h3 className="font-display font-bold text-lg text-text-primary">
                {editingTeam ? 'Editar Equipo' : 'Crear Equipo'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-text-muted hover:text-text-primary">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(form); }} className="p-5 space-y-4">
              <div>
                <label className="text-xs text-text-muted block mb-1">Nombre de la Selección *</label>
                <Input value={form.nombre} onChange={e => setForm(prev => ({ ...prev, nombre: e.target.value }))} placeholder="Ej: Argentina" required />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-text-muted block mb-1">Grupo Mundial *</label>
                  <select 
                    value={form.grupo_id} 
                    onChange={e => setForm(prev => ({ ...prev, grupo_id: e.target.value }))}
                    className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none"
                    required
                  >
                    <option value="">Selecciona...</option>
                    {groups.map(g => (
                      <option key={g.id} value={g.id}>Grupo {g.nombre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-text-muted block mb-1">Bandera (Código ISO de 2 letras) *</label>
                  <Input value={form.bandera} onChange={e => setForm(prev => ({ ...prev, bandera: e.target.value }))} placeholder="Ej: mx, us, ar, gb-eng" required />
                </div>
              </div>

              <div>
                <label className="text-xs text-text-muted block mb-1">Confederación *</label>
                <select 
                  value={form.confederacion} 
                  onChange={e => setForm(prev => ({ ...prev, confederacion: e.target.value }))}
                  className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none"
                  required
                >
                  <option value="CONMEBOL">CONMEBOL</option>
                  <option value="UEFA">UEFA</option>
                  <option value="CONCACAF">CONCACAF</option>
                  <option value="CAF">CAF</option>
                  <option value="AFC">AFC</option>
                  <option value="OFC">OFC</option>
                </select>
              </div>

              <div className="pt-4 border-t border-border-subtle flex justify-end gap-3">
                <Button type="button" variant="ghost" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button type="submit" variant="primary" loading={saveMutation.isPending}>Guardar</Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  )
}

// ==========================================
// SECCIÓN: JUGADORES (CRUD)
// ==========================================
function JugadoresSection({ teams }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [showModal, setShowModal] = useState(false)
  const [editingPlayer, setEditingPlayer] = useState(null)
  const [teamFilter, setTeamFilter] = useState('')

  const [form, setForm] = useState({
    nombre: '',
    equipo_id: '',
    posicion: 'Delantero',
    foto: ''
  })

  // Obtener jugadores
  const { data: playersData, isLoading } = useQuery({
    queryKey: ['admin-all-players'],
    queryFn: async () => {
      const response = await client.get('/players')
      return response.data?.datos || []
    }
  })
  const players = playersData || []

  const {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    searchTerm,
    setSearchTerm,
    sortField,
    sortOrder,
    requestSort,
    paginatedData: paginatedPlayers,
    totalPages,
    totalRecords,
    startIndex,
    endIndex
  } = useDataTable({
    data: players,
    searchFields: ['nombre', 'equipo_nombre', 'posicion'],
    defaultSortField: 'nombre',
    defaultSortOrder: 'asc',
    customFilter: (p) => teamFilter === '' || Number(p.equipo_id) === Number(teamFilter)
  })

  const saveMutation = useMutation({
    mutationFn: async (data) => {
      if (editingPlayer) {
        return (await client.put(`/admin/players/${editingPlayer.id}`, data)).data
      }
      return (await client.post('/admin/players', data)).data
    },
    onSuccess: () => {
      toast.success(editingPlayer ? 'Jugador actualizado' : 'Jugador creado')
      setShowModal(false)
      setEditingPlayer(null)
      queryClient.invalidateQueries({ queryKey: ['admin-all-players'] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.mensaje || 'Error al guardar jugador')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return (await client.delete(`/admin/players/${id}`)).data
    },
    onSuccess: () => {
      toast.success('Jugador eliminado')
      queryClient.invalidateQueries({ queryKey: ['admin-all-players'] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.mensaje || 'Error al eliminar jugador')
    }
  })

  const handleOpenCreate = () => {
    setEditingPlayer(null)
    setForm({ nombre: '', equipo_id: '', posicion: 'Delantero', foto: '' })
    setShowModal(true)
  }

  const handleOpenEdit = (p) => {
    setEditingPlayer(p)
    setForm({
      nombre: p.nombre,
      equipo_id: p.equipo_id,
      posicion: p.posicion,
      foto: p.foto || ''
    })
    setShowModal(true)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="font-display font-bold text-xl text-text-primary">Gestión de Jugadores</h2>
        <Button size="sm" icon={Plus} onClick={handleOpenCreate}>Crear Jugador</Button>
      </div>

      <Card className="p-4">
        <DataTableControls
          pageSize={pageSize}
          setPageSize={setPageSize}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Buscar jugadores..."
        >
          <div className="w-full sm:w-48 shrink-0">
            <select
              value={teamFilter}
              onChange={(e) => {
                setTeamFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="w-full bg-surface-card border border-border-default rounded-xl p-1.5 text-xs focus:border-cyan focus:outline-none text-text-secondary cursor-pointer"
            >
              <option value="">Todos los equipos</option>
              {teams.map(t => (
                <option key={t.id} value={t.id}>{t.bandera ? t.bandera.toUpperCase() : ''} {t.nombre}</option>
              ))}
            </select>
          </div>
        </DataTableControls>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-subtle">
                <SortableHeader field="nombre" currentField={sortField} sortOrder={sortOrder} label="Nombre" onSort={requestSort} className="text-left px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase" />
                <SortableHeader field="equipo_nombre" currentField={sortField} sortOrder={sortOrder} label="Equipo" onSort={requestSort} className="text-left px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase" />
                <SortableHeader field="posicion" currentField={sortField} sortOrder={sortOrder} label="Posición" onSort={requestSort} className="text-left px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase" />
                <th className="text-right px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {paginatedPlayers.map((p) => (
                <tr key={p.id} className="hover:bg-surface-card-hover transition-colors">
                  <td className="px-4 py-3 text-sm font-semibold">{p.nombre}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-1.5">
                      <Flag code={p.equipo_bandera} className="w-4 h-3 object-cover rounded shadow-sm shrink-0" />
                      <span>{p.equipo_nombre}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs font-medium text-text-secondary">{p.posicion}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => handleOpenEdit(p)} className="p-1 hover:text-cyan transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => window.confirm('¿Eliminar este jugador?') && deleteMutation.mutate(p.id)} className="p-1 hover:text-error transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {totalRecords === 0 && (
                <tr>
                  <td colSpan="4" className="text-center py-8 text-text-muted text-sm">No se encontraron jugadores.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <DataTablePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          startIndex={startIndex}
          endIndex={endIndex}
          totalRecords={totalRecords}
        />
      </Card>

      {/* Modal CRUD */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-navy-dark/80 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glass max-w-md w-full rounded-2xl border border-border-default overflow-hidden">
            <div className="p-5 border-b border-border-subtle flex justify-between items-center">
              <h3 className="font-display font-bold text-lg text-text-primary">
                {editingPlayer ? 'Editar Jugador' : 'Crear Jugador'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-text-muted hover:text-text-primary">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(form); }} className="p-5 space-y-4">
              <div>
                <label className="text-xs text-text-muted block mb-1">Nombre Completo *</label>
                <Input value={form.nombre} onChange={e => setForm(prev => ({ ...prev, nombre: e.target.value }))} placeholder="Ej: Lionel Messi" required />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-text-muted block mb-1">Selección Nacional *</label>
                  <select 
                    value={form.equipo_id} 
                    onChange={e => setForm(prev => ({ ...prev, equipo_id: e.target.value }))}
                    className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none"
                    required
                  >
                    <option value="">Selecciona...</option>
                    {teams.map(t => (
                      <option key={t.id} value={t.id}>{t.bandera ? t.bandera.toUpperCase() : ''} {t.nombre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-text-muted block mb-1">Posición *</label>
                  <select 
                    value={form.posicion} 
                    onChange={e => setForm(prev => ({ ...prev, posicion: e.target.value }))}
                    className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none"
                    required
                  >
                    <option value="Portero">Portero</option>
                    <option value="Defensa">Defensa</option>
                    <option value="Mediocampista">Mediocampista</option>
                    <option value="Delantero">Delantero</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs text-text-muted block mb-1">URL Foto (Opcional)</label>
                <Input value={form.foto} onChange={e => setForm(prev => ({ ...prev, foto: e.target.value }))} placeholder="URL de la imagen" />
              </div>

              <div className="pt-4 border-t border-border-subtle flex justify-end gap-3">
                <Button type="button" variant="ghost" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button type="submit" variant="primary" loading={saveMutation.isPending}>Guardar</Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  )
}

// ==========================================
// SECCIÓN: USUARIOS (CRUD / GESTIÓN ROLES)
// ==========================================
function UsuariosSection() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { user: loggedInUser } = useAuthStore()
  const isSuperAdmin = loggedInUser?.rol === 'superadmin'
  const canSeeFinance = loggedInUser?.rol === 'superadmin' || loggedInUser?.rol === 'admin'


  const [rolFilter, setRolFilter] = useState('all')
  const [premiumFilter, setPremiumFilter] = useState('all')
  const [estadoFilter, setEstadoFilter] = useState('all')
  const [editingUser, setEditingUser] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [selectedUserFinance, setSelectedUserFinance] = useState(null)
  const [showFinanceModal, setShowFinanceModal] = useState(false)
  const [isLoadingFinance, setIsLoadingFinance] = useState(false)

  // Acreditación de saldo manual
  const [showCreditModal, setShowCreditModal] = useState(false)
  const [selectedUserForCredit, setSelectedUserForCredit] = useState(null)
  const [creditAmount, setCreditAmount] = useState('')
  const [creditReason, setCreditReason] = useState('')

  const canManageFinance = loggedInUser?.rol === 'superadmin' || loggedInUser?.rol === 'admin' || (loggedInUser?.permisos && loggedInUser.permisos.gestionar_finanzas === 1)

  const creditMutation = useMutation({
    mutationFn: async ({ id, amount, reason }) => {
      const response = await client.post(`/admin/users/${id}/credit`, {
        monto: parseFloat(amount),
        motivo: reason
      })
      return response.data
    },
    onSuccess: () => {
      toast.success('Saldo acreditado correctamente')
      setShowCreditModal(false)
      setSelectedUserForCredit(null)
      setCreditAmount('')
      setCreditReason('')
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      if (selectedUserFinance?.usuario) {
        handleOpenFinance(selectedUserFinance.usuario)
      }
    },
    onError: (err) => {
      toast.error(err.response?.data?.mensaje || 'Error al acreditar saldo')
    }
  })

  const [form, setForm] = useState({
    nombre: '',
    apellido: '',
    email: '',
    rol: 'usuario',
    premium: 0,
    estado: 'activo',
    permitir_editar_apuestas: 0,
    permitir_apostar_iniciados: 0,
    excluir_auditoria: 0
  })

  // Obtener usuarios
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const response = await client.get('/admin/users')
      return response.data?.datos || []
    }
  })
  const usuarios = usersData || []

  const {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    searchTerm,
    setSearchTerm,
    sortField,
    sortOrder,
    requestSort,
    paginatedData: paginatedUsers,
    totalPages,
    totalRecords,
    startIndex,
    endIndex
  } = useDataTable({
    data: usuarios,
    searchFields: ['nombre', 'apellido', 'email', 'rol', 'estado'],
    defaultSortField: 'nombre',
    defaultSortOrder: 'asc',
    customFilter: (u) => {
      const matchesRol = rolFilter === 'all' || u.rol === rolFilter
      const matchesPremium = premiumFilter === 'all' || (premiumFilter === 'premium' ? u.premium == 1 : u.premium == 0)
      const matchesEstado = estadoFilter === 'all' || u.estado === estadoFilter
      return matchesRol && matchesPremium && matchesEstado
    }
  })

  const saveMutation = useMutation({
    mutationFn: async (data) => {
      return (await client.put(`/admin/users/${editingUser.id}`, data)).data
    },
    onSuccess: () => {
      toast.success('Usuario actualizado')
      setShowModal(false)
      setEditingUser(null)
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.mensaje || 'Error al actualizar usuario')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return (await client.delete(`/admin/users/${id}`)).data
    },
    onSuccess: () => {
      toast.success('Usuario eliminado de forma permanente')
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.mensaje || 'Error al eliminar usuario')
    }
  })

  const handleOpenFinance = async (usuario) => {
    setIsLoadingFinance(true)
    setShowFinanceModal(true)
    try {
      const response = await client.get(`/admin/users/${usuario.id}/finance`)
      setSelectedUserFinance(response.data?.datos)
    } catch (err) {
      toast.error('Error al obtener la información financiera del usuario')
      setShowFinanceModal(false)
    } finally {
      setIsLoadingFinance(false)
    }
  }

  const handleOpenEdit = (u) => {
    setEditingUser(u)
    setForm({
      nombre: u.nombre,
      apellido: u.apellido,
      email: u.email,
      rol: u.rol,
      premium: Number(u.premium),
      estado: u.estado,
      permitir_editar_apuestas: u.permitir_editar_apuestas !== undefined ? Number(u.permitir_editar_apuestas) : 0,
      permitir_apostar_iniciados: u.permitir_apostar_iniciados !== undefined ? Number(u.permitir_apostar_iniciados) : 0,
      excluir_auditoria: u.excluir_auditoria !== undefined ? Number(u.excluir_auditoria) : 0
    })
    setShowModal(true)
  }

  const getRolBadge = (rol) => {
    const styles = {
      admin: 'bg-error/10 text-error',
      premium: 'bg-warning/10 text-warning',
      usuario: 'bg-cyan/10 text-cyan',
      empleado: 'bg-success/10 text-success',
    }
    return styles[rol] || styles.usuario
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
        <h2 className="font-display font-bold text-2xl text-text-primary flex items-center gap-2">
          <Users className="w-6 h-6 text-cyan" />
          Gestión de Usuarios
        </h2>
      </div>

      <Card className="p-4">
        <DataTableControls
          pageSize={pageSize}
          setPageSize={setPageSize}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Buscar usuarios..."
        >
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <select
              value={rolFilter}
              onChange={(e) => { setRolFilter(e.target.value); setCurrentPage(1); }}
              className="bg-surface-card rounded-xl px-3 py-1.5 text-xs text-text-primary border border-border-default outline-none cursor-pointer"
            >
              <option value="all">Todos los Roles</option>
              {isSuperAdmin && <option value="admin">Administradores</option>}
              <option value="empleado">Empleados</option>
              <option value="usuario">Usuarios</option>
            </select>
            <select
              value={premiumFilter}
              onChange={(e) => { setPremiumFilter(e.target.value); setCurrentPage(1); }}
              className="bg-surface-card rounded-xl px-3 py-1.5 text-xs text-text-primary border border-border-default outline-none cursor-pointer"
            >
              <option value="all">Todos los Estados Premium</option>
              <option value="premium">Premium</option>
              <option value="regular">Regular</option>
            </select>
            <select
              value={estadoFilter}
              onChange={(e) => { setEstadoFilter(e.target.value); setCurrentPage(1); }}
              className="bg-surface-card rounded-xl px-3 py-1.5 text-xs text-text-primary border border-border-default outline-none cursor-pointer"
            >
              <option value="all">Todos los Estados</option>
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
              <option value="baneado">Baneado</option>
            </select>
          </div>
        </DataTableControls>

        {isLoading ? (
          <div className="text-center py-12 text-text-muted">Cargando usuarios...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-subtle">
                  <SortableHeader field="nombre" currentField={sortField} sortOrder={sortOrder} label="Usuario" onSort={requestSort} className="text-left px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase" />
                  <SortableHeader field="email" currentField={sortField} sortOrder={sortOrder} label="Email" onSort={requestSort} className="text-left px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase" />
                  {canSeeFinance && (
                    <SortableHeader field="creditos" currentField={sortField} sortOrder={sortOrder} label="Saldo" onSort={requestSort} className="text-left px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase" />
                  )}
                  <SortableHeader field="premium" currentField={sortField} sortOrder={sortOrder} label="Premium" onSort={requestSort} className="text-left px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase" />
                  <SortableHeader field="estado" currentField={sortField} sortOrder={sortOrder} label="Estado" onSort={requestSort} className="text-left px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase" />
                  <th className="text-right px-4 py-3 text-xs font-display font-semibold text-text-muted uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {paginatedUsers.map((usuario) => (
                  <tr key={usuario.id} className="hover:bg-surface-card-hover transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-navy flex items-center justify-center">
                          <span className="text-xs font-bold text-cyan">
                            {usuario.nombre ? usuario.nombre[0] : ''}{usuario.apellido ? usuario.apellido[0] : ''}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm">{usuario.nombre} {usuario.apellido}</span>
                            {isSuperAdmin && (
                              <div className="flex items-center" title={`Última actividad: ${usuario.ultima_actividad_texto || 'Nunca'}`}>
                                <span className={`w-2 h-2 rounded-full ${usuario.en_linea ? 'bg-emerald-500 animate-pulse' : 'bg-slate-500'}`} />
                              </div>
                            )}
                            {Number(usuario.excluir_auditoria) === 1 && usuario.rol !== 'admin' && isSuperAdmin && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700 flex items-center gap-0.5" title="Excluido de la auditoría de apuestas">
                                <span>👁️‍🗨️</span> Excluido
                              </span>
                            )}
                          </div>
                          {isSuperAdmin && (
                            <span className="text-[10px] text-text-muted">
                              {usuario.en_linea ? 'En línea' : `Activo: ${usuario.ultima_actividad_texto || 'Nunca'}`}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-text-secondary text-sm">{usuario.email}</td>
                    {canSeeFinance && (
                      <td className="px-4 py-3 text-sm font-semibold text-cyan font-mono">
                        {Number(usuario.creditos || 0).toFixed(2)} Bs
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm">
                      <span className={usuario.premium == 1 ? 'text-warning font-semibold' : 'text-text-muted'}>
                        {usuario.premium == 1 ? 'Premium' : 'Regular'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm capitalize">
                      <span className={`w-2 h-2 rounded-full inline-block mr-1.5 ${
                        usuario.estado === 'activo' ? 'bg-success' : usuario.estado === 'inactivo' ? 'bg-text-muted' : 'bg-error'
                      }`} />
                      {usuario.estado}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canSeeFinance && (
                          <button onClick={() => handleOpenFinance(usuario)} className="p-1 hover:text-cyan transition-colors" title="Ver finanzas">
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        {canManageFinance && (
                          <button
                            onClick={() => {
                              setSelectedUserForCredit(usuario)
                              setShowCreditModal(true)
                            }}
                            className="p-1 hover:text-emerald-400 transition-colors"
                            title="Acreditar saldo"
                          >
                            <Coins className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => handleOpenEdit(usuario)} className="p-1 hover:text-cyan transition-colors" title="Editar">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => window.confirm(`¿Estás seguro de eliminar permanentemente a ${usuario.nombre}?`) && deleteMutation.mutate(usuario.id)} className="p-1 hover:text-error transition-colors" title="Eliminar">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DataTablePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          startIndex={startIndex}
          endIndex={endIndex}
          totalRecords={totalRecords}
        />
      </Card>

      {/* Modal CRUD */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-navy-dark/80 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glass max-w-md w-full rounded-2xl overflow-hidden border border-border-default">
            <div className="p-5 border-b border-border-subtle flex justify-between items-center">
              <h3 className="font-display font-bold text-lg text-text-primary">Editar Cuenta de Usuario</h3>
              <button onClick={() => setShowModal(false)} className="text-text-muted hover:text-text-primary">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(form); }} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-text-muted block mb-1">Nombre *</label>
                  <Input value={form.nombre} onChange={e => setForm(prev => ({ ...prev, nombre: e.target.value }))} required />
                </div>
                <div>
                  <label className="text-xs text-text-muted block mb-1">Apellido *</label>
                  <Input value={form.apellido} onChange={e => setForm(prev => ({ ...prev, apellido: e.target.value }))} required />
                </div>
              </div>
              
              <div>
                <label className="text-xs text-text-muted block mb-1">Email *</label>
                <Input type="email" value={form.email} onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))} required />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-text-muted block mb-1">Rol *</label>
                  <select 
                    value={form.rol} 
                    onChange={e => setForm(prev => ({ ...prev, rol: e.target.value }))}
                    className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none"
                  >
                    <option value="usuario">Usuario Común</option>
                    <option value="admin">Administrador</option>
                    <option value="empleado">Empleado / Staff</option>
                  </select>

                </div>
                <div>
                  <label className="text-xs text-text-muted block mb-1">Estado Cuenta *</label>
                  <select 
                    value={form.estado} 
                    onChange={e => setForm(prev => ({ ...prev, estado: e.target.value }))}
                    className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none"
                  >
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                    <option value="baneado">Baneado</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input 
                  type="checkbox" 
                  id="premium-check" 
                  checked={form.premium === 1}
                  onChange={e => setForm(prev => ({ ...prev, premium: e.target.checked ? 1 : 0 }))}
                  className="rounded bg-navy border-border-default text-cyan focus:ring-cyan w-4 h-4"
                />
                <label htmlFor="premium-check" className="text-sm font-semibold cursor-pointer">Otorgar suscripción Premium ⭐</label>
              </div>

              {isSuperAdmin && (
                <>
                  <div className="flex items-center gap-2 pt-2">
                    <input 
                      type="checkbox" 
                      id="permitir-edit-check" 
                      checked={form.permitir_editar_apuestas === 1}
                      onChange={e => setForm(prev => ({ ...prev, permitir_editar_apuestas: e.target.checked ? 1 : 0 }))}
                      className="rounded bg-navy border-border-default text-cyan focus:ring-cyan w-4 h-4"
                    />
                    <label htmlFor="permitir-edit-check" className="text-sm font-semibold cursor-pointer">Autorizar edición de apuestas en curso ✏️</label>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <input 
                      type="checkbox" 
                      id="permitir-apostar-iniciados-check" 
                      checked={form.permitir_apostar_iniciados === 1}
                      onChange={e => setForm(prev => ({ ...prev, permitir_apostar_iniciados: e.target.checked ? 1 : 0 }))}
                      className="rounded bg-navy border-border-default text-cyan focus:ring-cyan w-4 h-4"
                    />
                    <label htmlFor="permitir-apostar-iniciados-check" className="text-sm font-semibold cursor-pointer">Permitir apuestas fuera de hora / en vivo 🚀</label>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <input 
                      type="checkbox" 
                      id="excluir-auditoria-check" 
                      checked={form.excluir_auditoria === 1}
                      onChange={e => setForm(prev => ({ ...prev, excluir_auditoria: e.target.checked ? 1 : 0 }))}
                      className="rounded bg-navy border-border-default text-cyan focus:ring-cyan w-4 h-4"
                    />
                    <label htmlFor="excluir-auditoria-check" className="text-sm font-semibold cursor-pointer">Excluir de Auditoría de Apuestas 👁️‍🗨️</label>
                  </div>
                </>
              )}

              <div className="pt-4 border-t border-border-subtle flex justify-end gap-3">
                <Button type="button" variant="ghost" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button type="submit" variant="primary" loading={saveMutation.isPending}>Guardar Cambios</Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Modal de Información Financiera */}
      {showFinanceModal && (
        <div className="fixed inset-0 z-55 bg-navy-dark/80 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="bg-[#0E1626] border border-border-default max-w-lg w-full rounded-2xl overflow-hidden shadow-2xl flex flex-col font-body"
          >
            <div className="p-5 border-b border-border-subtle flex justify-between items-center bg-navy-dark/40">
              <div>
                <h3 className="font-display font-bold text-lg text-text-primary">
                  Resumen Financiero de Usuario
                </h3>
                {selectedUserFinance?.usuario && (
                  <p className="text-xs text-text-muted mt-0.5">
                    {selectedUserFinance.usuario.nombre} {selectedUserFinance.usuario.apellido} — {selectedUserFinance.usuario.email}
                  </p>
                )}
              </div>
              <button 
                onClick={() => { setShowFinanceModal(false); setSelectedUserFinance(null); }} 
                className="text-text-muted hover:text-text-primary p-1 rounded-lg hover:bg-surface-card transition-all cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
              {isLoadingFinance ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-3">
                  <div className="w-8 h-8 border-4 border-cyan border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-text-muted">Cargando métricas financieras...</span>
                </div>
              ) : selectedUserFinance ? (
                <div className="space-y-6">
                  {/* Saldo Destacado */}
                  <div className="bg-surface-card/40 border border-border-subtle/50 rounded-2xl p-5 flex items-center justify-between shadow-inner">
                    <div>
                      <span className="text-xs text-text-muted font-medium uppercase tracking-wider block mb-1">Saldo Disponible</span>
                      <span className="text-2xl font-display font-extrabold text-cyan font-mono">
                        {Number(selectedUserFinance.usuario.creditos || 0).toFixed(2)} Bs
                      </span>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-cyan/10 border border-cyan/20 flex items-center justify-center text-cyan">
                      <Coins className="w-6 h-6 animate-pulse" />
                    </div>
                  </div>

                  {/* Grid de Métricas Principales */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Depósitos */}
                    <div className="bg-surface-card/20 border border-border-subtle/30 rounded-xl p-3.5 space-y-1">
                      <span className="text-[10px] text-text-muted font-bold uppercase tracking-wider block">Total Recargado</span>
                      <span className="text-base font-display font-extrabold text-emerald-400 font-mono">
                        +{Number(selectedUserFinance.depositos || 0).toFixed(2)} Bs
                      </span>
                    </div>

                    {/* Retiros */}
                    <div className="bg-surface-card/20 border border-border-subtle/30 rounded-xl p-3.5 space-y-1">
                      <span className="text-[10px] text-text-muted font-bold uppercase tracking-wider block">Total Retirado</span>
                      <span className="text-base font-display font-extrabold text-orange-400 font-mono">
                        -{Number(selectedUserFinance.retiros || 0).toFixed(2)} Bs
                      </span>
                    </div>

                    {/* Total Apostado */}
                    <div className="bg-surface-card/20 border border-border-subtle/30 rounded-xl p-3.5 space-y-1">
                      <span className="text-[10px] text-text-muted font-bold uppercase tracking-wider block">Invertido Apuestas</span>
                      <span className="text-base font-display font-extrabold text-cyan font-mono">
                        {Number(selectedUserFinance.apostado || 0).toFixed(2)} Bs
                      </span>
                    </div>

                    {/* Pendientes */}
                    <div className="bg-surface-card/20 border border-border-subtle/30 rounded-xl p-3.5 space-y-1">
                      <span className="text-[10px] text-text-muted font-bold uppercase tracking-wider block">Apuestas en Juego</span>
                      <span className="text-base font-display font-extrabold text-yellow-500 font-mono">
                        {Number(selectedUserFinance.pendiente || 0).toFixed(2)} Bs
                      </span>
                    </div>
                  </div>

                  {/* Detalle de Rendimiento de Apuestas */}
                  <div className="bg-navy-dark/30 border border-border-subtle/40 rounded-2xl p-4.5 space-y-3">
                    <h4 className="text-xs font-display font-bold uppercase tracking-wider text-text-muted border-b border-border-subtle/20 pb-2">
                      Rendimiento de Apuestas
                    </h4>
                    <div className="space-y-2.5 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-text-secondary">Premios Ganados:</span>
                        <span className="font-semibold text-emerald-400 font-mono">
                          +{Number(selectedUserFinance.ganado || 0).toFixed(2)} Bs
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-text-secondary">Monto Perdido:</span>
                        <span className="font-semibold text-rose-400 font-mono">
                          -{Number(selectedUserFinance.perdido || 0).toFixed(2)} Bs
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-text-secondary">Reembolsos/Devoluciones:</span>
                        <span className="font-semibold text-text-muted font-mono">
                          +{Number(selectedUserFinance.devuelto_apuestas || 0).toFixed(2)} Bs
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-text-secondary">Compra de Canales:</span>
                        <span className="font-semibold text-purple-400 font-mono">
                          -{Number(selectedUserFinance.compras_canales || 0).toFixed(2)} Bs
                        </span>
                      </div>
                    </div>

                    {/* Neto Ganancia/Pérdida */}
                    <div className="border-t border-border-subtle/20 pt-3 flex justify-between items-center">
                      <span className="text-sm font-semibold text-text-primary">Resultado Neto de Juego:</span>
                      <span className={`text-base font-display font-extrabold font-mono ${
                        (selectedUserFinance.ganado - selectedUserFinance.perdido) >= 0 ? 'text-emerald-400' : 'text-rose-400'
                      }`}>
                        {(selectedUserFinance.ganado - selectedUserFinance.perdido) >= 0 ? '+' : ''}
                        {(selectedUserFinance.ganado - selectedUserFinance.perdido).toFixed(2)} Bs
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-text-muted">No se pudo cargar la información.</div>
              )}
              
              <div className="pt-4 border-t border-border-subtle flex justify-end gap-3">
                {canManageFinance && selectedUserFinance?.usuario && (
                  <Button
                    type="button"
                    variant="primary"
                    className="bg-emerald-600 hover:bg-emerald-500 text-white"
                    icon={Coins}
                    onClick={() => {
                      setSelectedUserForCredit(selectedUserFinance.usuario)
                      setShowCreditModal(true)
                    }}
                  >
                    Acreditar Saldo
                  </Button>
                )}
                <Button type="button" variant="ghost" onClick={() => { setShowFinanceModal(false); setSelectedUserFinance(null); }}>
                  Cerrar
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal Acreditar Saldo */}
      {showCreditModal && selectedUserForCredit && (
        <div className="fixed inset-0 z-55 bg-navy-dark/80 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass max-w-md w-full rounded-2xl overflow-hidden border border-border-default"
          >
            <div className="p-5 border-b border-border-subtle flex justify-between items-center">
              <h3 className="font-display font-bold text-lg text-text-primary">
                Acreditar Saldo Manualmente
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowCreditModal(false)
                  setSelectedUserForCredit(null)
                  setCreditAmount('')
                  setCreditReason('')
                }}
                className="text-text-muted hover:text-text-primary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (!creditAmount || parseFloat(creditAmount) <= 0) {
                  toast.warning('Por favor ingresa un monto válido mayor a 0.')
                  return
                }
                if (!creditReason.trim()) {
                  toast.warning('Por favor ingresa el motivo de la acreditación.')
                  return
                }
                creditMutation.mutate({
                  id: selectedUserForCredit.id,
                  amount: creditAmount,
                  reason: creditReason
                })
              }}
              className="p-5 space-y-4"
            >
              <div>
                <p className="text-sm text-text-secondary mb-2">
                  Vas a acreditar saldo al usuario:{' '}
                  <span className="text-cyan font-semibold font-display">
                    {selectedUserForCredit.nombre} {selectedUserForCredit.apellido}
                  </span>{' '}
                  ({selectedUserForCredit.email}).
                </p>
                <p className="text-xs text-text-muted">
                  Saldo actual:{' '}
                  <span className="font-mono text-cyan">
                    {Number(selectedUserForCredit.creditos || 0).toFixed(2)} Bs
                  </span>
                </p>
              </div>

              <div className="space-y-1">
                <label className="text-xs text-text-muted block mb-1">Monto a Acreditar (Bs) *</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs text-text-muted block mb-1">Motivo / Justificación *</label>
                <textarea
                  className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none min-h-[80px]"
                  placeholder="Ej. Compensación por error de sistema en apuesta partido X, ajuste saldo de usuario Mario Estrada..."
                  value={creditReason}
                  onChange={(e) => setCreditReason(e.target.value)}
                  required
                />
              </div>

              <div className="pt-4 border-t border-border-subtle flex justify-end gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowCreditModal(false)
                    setSelectedUserForCredit(null)
                    setCreditAmount('')
                    setCreditReason('')
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  loading={creditMutation.isPending}
                >
                  Confirmar Acreditación
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  )
}

// ==========================================
// SECCIÓN: CONFIGURACIÓN GENERAL (REGLAS Y FECHAS)
// ==========================================
function ConfiguracionSection() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
    ? import.meta.env.VITE_API_URL.replace('/api', '')
    : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      toast.error('El logotipo no debe superar los 5MB')
      return
    }

    const formData = new FormData()
    formData.append('logo', file)

    try {
      toast.info('Subiendo logotipo...')
      await client.post('/admin/settings/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      toast.success('¡Logotipo del sistema actualizado correctamente!')
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] })
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      queryClient.invalidateQueries({ queryKey: ['public-settings-modal'] })
    } catch (err) {
      toast.error(err.response?.data?.mensaje || 'Error al subir el logotipo')
    }
  }

  const handleQrUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar los 5MB')
      return
    }

    const formData = new FormData()
    formData.append('qr_code', file)

    try {
      toast.info('Subiendo código QR...')
      await client.post('/admin/settings/qr', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      toast.success('¡Código QR de depósitos actualizado correctamente!')
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] })
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      queryClient.invalidateQueries({ queryKey: ['public-settings-modal'] })
    } catch (err) {
      toast.error(err.response?.data?.mensaje || 'Error al subir el código QR')
    }
  }
  
  // Obtener lista completa de jugadores para el dropdown
  const { data: playersData } = useQuery({
    queryKey: ['admin-all-players'],
    queryFn: async () => {
      const response = await client.get('/players')
      return response.data?.datos || []
    }
  })
  const players = playersData || []

  // Obtener configuraciones del backend
  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: async () => {
      const response = await client.get('/admin/settings')
      return response.data?.datos || []
    }
  })
  const settingsList = settingsData || []

  // Mutación para guardar configuraciones
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      return (await client.put('/admin/settings', data)).data
    },
    onSuccess: () => {
      toast.success('Configuraciones generales guardadas')
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] })
      queryClient.invalidateQueries({ queryKey: ['settings'] })
    },
    onError: (err) => {
      toast.error('Error al guardar configuraciones')
    }
  })

  const [retencionActivada, setRetencionActivada] = useState('0')

  React.useEffect(() => {
    if (settingsList.length > 0) {
      const item = settingsList.find(s => s.clave === 'retencion_sin_ganadores_activado')
      if (item) {
        setRetencionActivada(item.valor)
      }
    }
  }, [settingsList])

  const handleFormSubmit = (e) => {
    e.preventDefault()
    const fd = new FormData(e.target)
    
    // Validar rango del porcentaje de retención
    const pctVal = fd.get('porcentaje_retencion_sin_ganadores')
    if (pctVal !== null) {
      const pct = parseFloat(pctVal)
      if (isNaN(pct) || pct < 0 || pct > 100) {
        toast.warning('El porcentaje de retención debe estar entre 0% y 100%')
        return
      }
    }

    const payload = {}
    settingsList.forEach(s => {
      const val = fd.get(s.clave)
      payload[s.clave] = val !== null ? val : s.valor
    })
    saveMutation.mutate(payload)
  }

  if (isLoading) {
    return <div className="text-center py-12 text-text-muted">Cargando configuraciones...</div>
  }

  return (
    <div className="space-y-6">
      <h2 className="font-display font-bold text-xl text-text-primary">Panel de Configuración General</h2>
      
      <form onSubmit={handleFormSubmit} className="space-y-6">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Columna Izquierda: Configuración del Torneo, Apuestas y Ganadores */}
          <div className="space-y-6">
            
            {/* Card 1: Configuración del Torneo */}
            <Card className="p-6 border-l-4 border-l-cyan">
              <h3 className="font-display font-semibold text-md text-cyan border-b border-border-subtle pb-2 mb-4 flex items-center gap-2">
                <CalendarDays className="w-4 h-4" /> Configuración del Torneo y Cierre
              </h3>
              <div className="space-y-4">
                {/* Fecha de cierre */}
                {settingsList.filter(s => s.clave === 'fecha_cierre').map(s => (
                  <div key={s.clave} className="space-y-1">
                    <label className="text-sm font-semibold block">{s.descripcion}</label>
                    <input
                      type="text"
                      name={s.clave}
                      defaultValue={s.valor}
                      placeholder="YYYY-MM-DD HH:MM:SS"
                      className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none font-mono"
                    />
                    <span className="text-xs text-text-muted block">Límite para ingresar y modificar pronósticos.</span>
                  </div>
                ))}

                {/* Bloqueo dinámico por inicio de partidos */}
                {settingsList.filter(s => s.clave === 'bloqueo_dinamico_partidos').map(s => (
                  <div key={s.clave} className="space-y-1">
                    <label className="text-sm font-semibold block">{s.descripcion}</label>
                    <select
                      name={s.clave}
                      defaultValue={s.valor}
                      className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none"
                    >
                      <option value="1">Sí (Bloquear al comenzar cualquier partido)</option>
                      <option value="0">No (Permitir registrar ignorando partidos iniciados)</option>
                    </select>
                    <span className="text-xs text-text-muted block">Determina si se cierran las predicciones cuando inicia el primer partido del mundial.</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Card 2: Finanzas y Apuestas */}
            <Card className="p-6 border-l-4 border-l-cyan">
              <h3 className="font-display font-semibold text-md text-cyan border-b border-border-subtle pb-2 mb-4 flex items-center gap-2">
                <Coins className="w-4 h-4" /> Finanzas y Apuestas
              </h3>
              <div className="space-y-4">
                {/* Comisión de Apuestas */}
                {settingsList.filter(s => s.clave === 'comision_apuestas').map(s => (
                  <div key={s.clave} className="space-y-1">
                    <label className="text-sm font-semibold block">{s.descripcion}</label>
                    <input
                      type="number"
                      name={s.clave}
                      defaultValue={s.valor}
                      min="0"
                      max="30"
                      className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none font-mono"
                    />
                    <span className="text-xs text-text-muted block">Define el porcentaje de ganancia cobrado por la casa para cada pozo de apuestas.</span>
                  </div>
                ))}

                {/* Multiplicador de Goles Exactos */}
                {settingsList.filter(s => s.clave === 'multiplicador_goles_exacto').map(s => (
                  <div key={s.clave} className="space-y-1">
                    <label className="text-sm font-semibold block">{s.descripcion}</label>
                    <input
                      type="number"
                      step="0.1"
                      name={s.clave}
                      defaultValue={s.valor}
                      min="1"
                      max="10"
                      className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none font-mono"
                    />
                    <span className="text-xs text-text-muted block">Multiplicador por goles exactos acertados (ej: 3 para triple participación). Mínimo 1.</span>
                  </div>
                ))}

                {/* Dividendo Mínimo Garantizado */}
                {settingsList.filter(s => s.clave === 'dividendo_minimo_ganador').map(s => (
                  <div key={s.clave} className="space-y-1">
                    <label className="text-sm font-semibold block">{s.descripcion}</label>
                    <input
                      type="number"
                      step="0.01"
                      name={s.clave}
                      defaultValue={s.valor}
                      min="1.00"
                      max="2.00"
                      className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none font-mono"
                    />
                    <span className="text-xs text-text-muted block">Retorno mínimo garantizado por cada 1.00 Bs apostado (ej: 1.05 para retorno del 105%). Mínimo 1.00.</span>
                  </div>
                ))}

                {/* Retención cuando nadie gana */}
                {settingsList.filter(s => s.clave === 'retencion_sin_ganadores_activado').map(s => (
                  <div key={s.clave} className="space-y-1 pt-2 border-t border-border-subtle/30">
                    <label className="text-sm font-semibold block">Falta de Ganadores (Reembolso)</label>
                    <select
                      name={s.clave}
                      value={retencionActivada}
                      onChange={(e) => setRetencionActivada(e.target.value)}
                      className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none cursor-pointer"
                    >
                      <option value="0">Devolver 100% sin cobrar comisión 🔄</option>
                      <option value="1">Retener un porcentaje para la casa 🏦</option>
                    </select>
                    <span className="text-xs text-text-muted block">Define el comportamiento del pozo cuando ningún usuario acierte el ganador.</span>
                  </div>
                ))}

                {/* Porcentaje de Retención cuando nadie gana */}
                {retencionActivada === '1' && settingsList.filter(s => s.clave === 'porcentaje_retencion_sin_ganadores').map(s => (
                  <div key={s.clave} className="space-y-1">
                    <label className="text-sm font-semibold block">Porcentaje de Retención por Casa (%)</label>
                    <input
                      type="number"
                      name={s.clave}
                      defaultValue={s.valor}
                      min="0"
                      max="100"
                      className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none font-mono"
                    />
                    <span className="text-xs text-text-muted block">Porcentaje que retendrá la casa si no hay ganadores. El resto se devuelve.</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Card 3: Ganadores Reales del Torneo */}
            <Card className="p-6 border-l-4 border-l-cyan">
              <h3 className="font-display font-semibold text-md text-cyan border-b border-border-subtle pb-2 mb-4 flex items-center gap-2">
                <Trophy className="w-4 h-4" /> Ganadores Reales del Torneo
              </h3>
              <div className="space-y-4">
                {/* Goleador Real */}
                <div className="space-y-1">
                  <label className="text-sm font-semibold block">Goleador Real del Torneo</label>
                  <select
                    name="goleador_real_id"
                    defaultValue={settingsList.find(s => s.clave === 'goleador_real_id')?.valor || '0'}
                    className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none"
                  >
                    <option value="0">No Definido (Aún en juego)</option>
                    {players.map(p => (
                      <option key={p.id} value={p.id}>{p.nombre} ({p.equipo_nombre})</option>
                    ))}
                  </select>
                  <span className="text-xs text-text-muted block">Selecciona el jugador que ganó la Bota de Oro.</span>
                </div>

                {/* MVP Real */}
                <div className="space-y-1">
                  <label className="text-sm font-semibold block">Mejor Jugador Real (MVP)</label>
                  <select
                    name="mejor_jugador_real_id"
                    defaultValue={settingsList.find(s => s.clave === 'mejor_jugador_real_id')?.valor || '0'}
                    className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none"
                  >
                    <option value="0">No Definido (Aún en juego)</option>
                    {players.map(p => (
                      <option key={p.id} value={p.id}>{p.nombre} ({p.equipo_nombre})</option>
                    ))}
                  </select>
                  <span className="text-xs text-text-muted block">Selecciona el jugador galardonado como MVP.</span>
                </div>
              </div>
            </Card>
          </div>

          {/* Columna Derecha: Sistema de Puntuación */}
          <div className="space-y-6">
            {/* Card 4: Sistema de Puntuación */}
            <Card className="p-6 border-l-4 border-l-warning">
              <h3 className="font-display font-semibold text-md text-warning border-b border-border-subtle pb-2 mb-4 flex items-center gap-2">
                <Shield className="w-4 h-4" /> Sistema de Puntuación (Predicciones)
              </h3>
              <div className="space-y-6">
                {/* Subgrupo: Fase de Grupos */}
                <div className="space-y-4">
                  <h4 className="font-display font-bold text-xs text-text-muted uppercase tracking-widest border-b border-border-subtle/30 pb-1">Fase de Grupos</h4>
                  
                  {settingsList.filter(s => s.clave === 'puntos_grupo_primero').map(s => (
                    <div key={s.clave} className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-sm font-semibold block">{s.descripcion || s.clave}</label>
                        <span className="text-xs text-text-muted font-mono">{s.clave}</span>
                      </div>
                      <input
                        type="number"
                        name={s.clave}
                        defaultValue={s.valor}
                        min="0"
                        max="100"
                        className="w-20 bg-surface-card border border-border-default rounded-xl p-2 text-center text-sm font-bold text-cyan focus:border-cyan focus:outline-none"
                      />
                    </div>
                  ))}

                  {settingsList.filter(s => s.clave === 'puntos_grupo_segundo').map(s => (
                    <div key={s.clave} className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-sm font-semibold block">{s.descripcion || s.clave}</label>
                        <span className="text-xs text-text-muted font-mono">{s.clave}</span>
                      </div>
                      <input
                        type="number"
                        name={s.clave}
                        defaultValue={s.valor}
                        min="0"
                        max="100"
                        className="w-20 bg-surface-card border border-border-default rounded-xl p-2 text-center text-sm font-bold text-cyan focus:border-cyan focus:outline-none"
                      />
                    </div>
                  ))}

                  {settingsList.filter(s => s.clave === 'puntos_mejor_tercero').map(s => (
                    <div key={s.clave} className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-sm font-semibold block">{s.descripcion || s.clave}</label>
                        <span className="text-xs text-text-muted font-mono">{s.clave}</span>
                      </div>
                      <input
                        type="number"
                        name={s.clave}
                        defaultValue={s.valor}
                        min="0"
                        max="100"
                        className="w-20 bg-surface-card border border-border-default rounded-xl p-2 text-center text-sm font-bold text-cyan focus:border-cyan focus:outline-none"
                      />
                    </div>
                  ))}
                </div>

                {/* Subgrupo: Fases Eliminatorias */}
                <div className="space-y-4 pt-2">
                  <h4 className="font-display font-bold text-xs text-text-muted uppercase tracking-widest border-b border-border-subtle/30 pb-1">Rondas Eliminatorias</h4>
                  
                  {settingsList.filter(s => s.clave === 'puntos_dieciseisavos').map(s => (
                    <div key={s.clave} className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-sm font-semibold block">{s.descripcion || s.clave}</label>
                        <span className="text-xs text-text-muted font-mono">{s.clave}</span>
                      </div>
                      <input
                        type="number"
                        name={s.clave}
                        defaultValue={s.valor}
                        min="0"
                        max="100"
                        className="w-20 bg-surface-card border border-border-default rounded-xl p-2 text-center text-sm font-bold text-cyan focus:border-cyan focus:outline-none"
                      />
                    </div>
                  ))}

                  {settingsList.filter(s => s.clave === 'puntos_octavos').map(s => (
                    <div key={s.clave} className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-sm font-semibold block">{s.descripcion || s.clave}</label>
                        <span className="text-xs text-text-muted font-mono">{s.clave}</span>
                      </div>
                      <input
                        type="number"
                        name={s.clave}
                        defaultValue={s.valor}
                        min="0"
                        max="100"
                        className="w-20 bg-surface-card border border-border-default rounded-xl p-2 text-center text-sm font-bold text-cyan focus:border-cyan focus:outline-none"
                      />
                    </div>
                  ))}

                  {settingsList.filter(s => s.clave === 'puntos_cuartos').map(s => (
                    <div key={s.clave} className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-sm font-semibold block">{s.descripcion || s.clave}</label>
                        <span className="text-xs text-text-muted font-mono">{s.clave}</span>
                      </div>
                      <input
                        type="number"
                        name={s.clave}
                        defaultValue={s.valor}
                        min="0"
                        max="100"
                        className="w-20 bg-surface-card border border-border-default rounded-xl p-2 text-center text-sm font-bold text-cyan focus:border-cyan focus:outline-none"
                      />
                    </div>
                  ))}

                  {settingsList.filter(s => s.clave === 'puntos_semifinal').map(s => (
                    <div key={s.clave} className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-sm font-semibold block">{s.descripcion || s.clave}</label>
                        <span className="text-xs text-text-muted font-mono">{s.clave}</span>
                      </div>
                      <input
                        type="number"
                        name={s.clave}
                        defaultValue={s.valor}
                        min="0"
                        max="100"
                        className="w-20 bg-surface-card border border-border-default rounded-xl p-2 text-center text-sm font-bold text-cyan focus:border-cyan focus:outline-none"
                      />
                    </div>
                  ))}

                  {settingsList.filter(s => s.clave === 'puntos_final').map(s => (
                    <div key={s.clave} className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <label className="text-sm font-semibold block">{s.descripcion || s.clave}</label>
                        <span className="text-xs text-text-muted font-mono">{s.clave}</span>
                      </div>
                      <input
                        type="number"
                        name={s.clave}
                        defaultValue={s.valor}
                        min="0"
                        max="100"
                        className="w-20 bg-surface-card border border-border-default rounded-xl p-2 text-center text-sm font-bold text-cyan focus:border-cyan focus:outline-none"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            {/* Card 4b: Configuración de Notificaciones */}
            <Card className="p-6 border-l-4 border-l-cyan">
              <h3 className="font-display font-semibold text-md text-cyan border-b border-border-subtle pb-2 mb-4 flex items-center gap-2">
                <Bell className="w-4 h-4" /> Configuración de Notificaciones
              </h3>
              <div className="space-y-4">
                {/* Apuestas Ganadoras */}
                {settingsList.filter(s => s.clave === 'notificar_apuestas_ganadoras').map(s => (
                  <div key={s.clave} className="space-y-1">
                    <label className="text-sm font-semibold block">{s.descripcion}</label>
                    <select
                      name={s.clave}
                      defaultValue={s.valor}
                      className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none cursor-pointer"
                    >
                      <option value="1">Habilitado ✅</option>
                      <option value="0">Deshabilitado ❌</option>
                    </select>
                    <span className="text-xs text-text-muted block">Enviar notificación cuando una apuesta resulte ganadora.</span>
                  </div>
                ))}

                {/* Apuestas Perdidas */}
                {settingsList.filter(s => s.clave === 'notificar_apuestas_perdidas').map(s => (
                  <div key={s.clave} className="space-y-1">
                    <label className="text-sm font-semibold block">{s.descripcion}</label>
                    <select
                      name={s.clave}
                      defaultValue={s.valor}
                      className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none cursor-pointer"
                    >
                      <option value="1">Habilitado ✅</option>
                      <option value="0">Deshabilitado ❌</option>
                    </select>
                    <span className="text-xs text-text-muted block">Enviar notificación cuando una apuesta finalice sin aciertos.</span>
                  </div>
                ))}

                {/* Apuestas Devueltas (Falta de Ganadores) */}
                {settingsList.filter(s => s.clave === 'notificar_apuestas_devueltas').map(s => (
                  <div key={s.clave} className="space-y-1">
                    <label className="text-sm font-semibold block">{s.descripcion}</label>
                    <select
                      name={s.clave}
                      defaultValue={s.valor}
                      className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none cursor-pointer"
                    >
                      <option value="1">Habilitado ✅</option>
                      <option value="0">Deshabilitado ❌</option>
                    </select>
                    <span className="text-xs text-text-muted block">Enviar notificación en caso de devolución de apuesta por falta de ganadores.</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>

        {/* Fila Inferior: Identidad de Marca y Código QR */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Card 5: Identidad de la Plataforma */}
          <Card className="p-6 border-l-4 border-l-success flex flex-col justify-between">
            <div>
              <h3 className="font-display font-semibold text-md text-success border-b border-border-subtle pb-2 mb-4 flex items-center gap-2">
                <Image className="w-5 h-5" /> Identidad Visual y Marca
              </h3>
              
              {/* Nombre de la plataforma */}
              {settingsList.filter(s => s.clave === 'nombre_sistema').map(s => (
                <div key={s.clave} className="space-y-1 mb-6">
                  <label className="text-sm font-semibold block">{s.descripcion || 'Nombre de la Plataforma'}</label>
                  <input
                    type="text"
                    name={s.clave}
                    defaultValue={s.valor}
                    className="w-full bg-surface-card border border-border-default rounded-xl p-2.5 text-sm focus:border-cyan focus:outline-none font-medium"
                  />
                  <span className="text-xs text-text-muted block">Nombre que verán los usuarios en la barra de navegación, el título de la pestaña y los correos.</span>
                </div>
              ))}
            </div>

            {/* Logotipo del sistema */}
            <div className="grid sm:grid-cols-2 gap-4 items-center border-t border-border-subtle pt-4">
              <div className="flex flex-col items-center bg-navy-dark/40 border border-border-subtle p-3 rounded-xl space-y-1.5">
                <span className="text-[10px] font-semibold text-text-muted uppercase">Logotipo Activo</span>
                {settingsList.find(s => s.clave === 'logo_sistema')?.valor ? (
                  <div className="p-2 rounded-lg border border-border-subtle bg-navy flex items-center justify-center min-h-[96px] w-full">
                    <img
                      src={(() => {
                        const val = settingsList.find(s => s.clave === 'logo_sistema').valor
                        return val.startsWith('/uploads') 
                          ? `${baseImgUrl}${val}` 
                          : (val.startsWith('/') ? `${window.__APP_BASE_URL__ || '/'}${val.substring(1)}` : val)
                      })()}
                      alt="Logotipo del Sistema"
                      className="max-h-16 max-w-full object-contain rounded-md transition-transform duration-500 hover:rotate-[360deg] hover:scale-110"
                    />
                  </div>
                ) : (
                  <div className="w-full h-24 flex items-center justify-center bg-navy border border-border-subtle rounded-lg text-text-muted text-[10px] text-center p-2">
                    Sin logotipo.
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-semibold block text-text-muted">Subir nuevo Logotipo</label>
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-border-subtle hover:border-cyan/50 rounded-xl p-3 cursor-pointer bg-navy-dark/40 transition-colors group">
                  <Upload className="w-6 h-6 text-text-muted group-hover:text-cyan transition-colors mb-1" />
                  <span className="text-[11px] text-text-secondary group-hover:text-text-primary transition-colors text-center font-medium">
                    Seleccionar Logotipo
                  </span>
                  <span className="text-[9px] text-text-muted mt-0.5 font-mono">SVG, PNG, JPG (Max. 5MB)</span>
                  <input
                    type="file"
                    accept=".svg,image/png,image/jpeg,image/jpg"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </Card>

          {/* Card 6: Código QR de Depósitos */}
          <Card className="p-6 border-l-4 border-l-success flex flex-col justify-between">
            <div>
              <h3 className="font-display font-semibold text-md text-success border-b border-border-subtle pb-2 mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5" /> Código QR de Depósitos
              </h3>
              <p className="text-text-secondary text-xs mb-6">
                Sube el código QR que verán los usuarios en "Mi Billetera" para realizar transferencias simples o depósitos bancarios.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 items-center border-t border-border-subtle pt-4">
              <div className="flex flex-col items-center bg-navy-dark/40 border border-border-subtle p-3 rounded-xl space-y-1.5">
                <span className="text-[10px] font-semibold text-text-muted uppercase">Código QR Activo</span>
                {settingsList.find(s => s.clave === 'qr_deposito')?.valor ? (
                  <div className="bg-white p-2 rounded-lg border border-border-subtle shadow-inner flex items-center justify-center min-h-[96px] w-full">
                    <img
                      src={`${baseImgUrl}${settingsList.find(s => s.clave === 'qr_deposito').valor}`}
                      alt="QR Depósito Activo"
                      className="w-20 h-20 object-contain rounded-md"
                    />
                  </div>
                ) : (
                  <div className="w-full h-24 flex items-center justify-center bg-navy border border-border-subtle rounded-lg text-text-muted text-[10px] text-center p-2">
                    Sin QR configurado.
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-semibold block text-text-muted">Subir nueva Imagen de Código QR</label>
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-border-subtle hover:border-cyan/50 rounded-xl p-3 cursor-pointer bg-navy-dark/40 transition-colors group">
                  <Upload className="w-6 h-6 text-text-muted group-hover:text-cyan transition-colors mb-1" />
                  <span className="text-[11px] text-text-secondary group-hover:text-text-primary transition-colors text-center font-medium">
                    Seleccionar Imagen QR
                  </span>
                  <span className="text-[9px] text-text-muted mt-0.5 font-mono">PNG, JPG, WEBP (Max. 5MB)</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleQrUpload}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </Card>
        </div>

        {/* Botón Guardar Flotante / Fijo al final */}
        <div className="flex justify-end p-4 glass rounded-2xl border border-border-subtle sticky bottom-4 z-10 shadow-lg">
          <Button type="submit" variant="primary" icon={Save} loading={saveMutation.isPending}>
            Guardar Todas las Configuraciones
          </Button>
        </div>
      </form>
    </div>
  )
}

// ==========================================
// SECCIÓN: RECARGAS (APROBACIONES)
// ==========================================
function RecargasSection() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [selectedVoucher, setSelectedVoucher] = useState(null)
  const [rejectingId, setRejectingId] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
    ? import.meta.env.VITE_API_URL.replace('/api', '')
    : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')

  const { data: rechargesData, isLoading } = useQuery({
    queryKey: ['admin-recharges'],
    queryFn: async () => {
      const response = await client.get('/admin/recharges')
      return response.data?.datos || []
    }
  })
  const recharges = rechargesData || []

  const {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    searchTerm,
    setSearchTerm,
    sortField,
    sortOrder,
    requestSort,
    paginatedData: paginatedRecharges,
    totalPages,
    totalRecords,
    startIndex,
    endIndex
  } = useDataTable({
    data: recharges,
    searchFields: ['nombre', 'apellido', 'email', 'referencia', 'estado'],
    defaultSortField: 'creado_en',
    defaultSortOrder: 'desc',
    customFilter: (req) => statusFilter === 'all' || req.estado === statusFilter
  })

  const processMutation = useMutation({
    mutationFn: async ({ id, estado, motivo_rechazo }) => {
      const response = await client.put(`/admin/recharges/${id}`, { estado, motivo_rechazo })
      return response.data
    },
    onSuccess: (_, variables) => {
      toast.success(`Solicitud marcada como '${variables.estado}' con éxito.`)
      setRejectingId(null)
      setRejectReason('')
      queryClient.invalidateQueries({ queryKey: ['admin-recharges'] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.mensaje || 'Error al procesar la solicitud.')
    }
  })

  if (isLoading) return <Skeleton className="h-64 w-full rounded-2xl" />

  return (
    <div className="space-y-6">
      <h2 className="font-display font-bold text-xl text-text-primary">
        Aprobación de Depósitos / Recargas
      </h2>

      <Card className="p-4">
        <DataTableControls
          pageSize={pageSize}
          setPageSize={setPageSize}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Buscar por usuario, email o referencia..."
        >
          <div className="w-full sm:w-64 flex gap-2 shrink-0">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="w-full bg-surface-card border border-border-default rounded-xl p-1.5 text-xs focus:border-cyan focus:outline-none text-text-secondary cursor-pointer"
            >
              <option value="all">Todos los estados</option>
              <option value="pendiente">Pendientes ⏳</option>
              <option value="aprobado">Aprobados ✅</option>
              <option value="rechazado">Rechazados ❌</option>
            </select>
          </div>
        </DataTableControls>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left font-body">
            <thead className="bg-navy-dark/40 text-text-muted text-xs uppercase tracking-wider border-b border-border-subtle">
              <tr>
                <SortableHeader field="nombre" currentField={sortField} sortOrder={sortOrder} label="Usuario" onSort={requestSort} className="px-6 py-4" />
                <SortableHeader field="monto" currentField={sortField} sortOrder={sortOrder} label="Monto" onSort={requestSort} className="px-6 py-4" />
                <th className="px-6 py-4 text-text-muted">Comprobante</th>
                <SortableHeader field="referencia" currentField={sortField} sortOrder={sortOrder} label="Referencia" onSort={requestSort} className="px-6 py-4" />
                <SortableHeader field="creado_en" currentField={sortField} sortOrder={sortOrder} label="Fecha" onSort={requestSort} className="px-6 py-4" />
                <SortableHeader field="estado" currentField={sortField} sortOrder={sortOrder} label="Estado" onSort={requestSort} className="px-6 py-4 text-center" />
                <th className="px-6 py-4 text-right text-text-muted">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {paginatedRecharges.map((req) => (
                <tr key={req.id} className="hover:bg-navy-dark/10 transition-colors">
                  <td className="px-6 py-4">
                    <p className="font-semibold text-text-primary">{req.nombre} {req.apellido}</p>
                    <p className="text-xs text-text-muted">{req.email}</p>
                  </td>
                  <td className="px-6 py-4 font-mono font-bold text-cyan">
                    {Number(req.monto).toFixed(2)} Bs
                  </td>
                  <td className="px-6 py-4">
                    {req.comprobante_ruta ? (
                      <button
                        onClick={() => setSelectedVoucher(`${baseImgUrl}${req.comprobante_ruta}`)}
                        className="text-xs text-cyan hover:underline font-semibold"
                      >
                        Ver Voucher
                      </button>
                    ) : (
                      <span className="text-xs text-text-muted">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-xs font-mono max-w-[150px] truncate" title={req.referencia}>
                    {req.referencia}
                  </td>
                  <td className="px-6 py-4 text-text-secondary text-xs">
                    {new Date(req.creado_en).toLocaleString('es-ES')}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {req.estado === 'pendiente' && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-500/10 text-orange-400 border border-orange-500/20">
                        Pendiente
                      </span>
                    )}
                    {req.estado === 'aprobado' && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-success/10 text-success border border-success/20">
                        Aprobado
                      </span>
                    )}
                    {req.estado === 'rechazado' && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-error/10 text-error border border-error/20">
                        Rechazado
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {req.estado === 'pendiente' ? (
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="xs"
                          variant="success"
                          disabled={processMutation.isPending}
                          onClick={() => processMutation.mutate({ id: req.id, estado: 'aprobado' })}
                        >
                          Aprobar
                        </Button>
                        <Button
                          size="xs"
                          variant="danger"
                          disabled={processMutation.isPending}
                          onClick={() => setRejectingId(req.id)}
                        >
                          Rechazar
                        </Button>
                      </div>
                    ) : (
                      <div className="text-xs text-text-muted text-right space-y-0.5">
                        <span className="font-semibold">Procesada</span>
                        {req.estado === 'rechazado' && req.motivo_rechazo && (
                          <p className="text-[10px] text-error font-medium">Motivo: <span>{req.motivo_rechazo}</span></p>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {totalRecords === 0 && (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-text-muted">
                    No se registran solicitudes que coincidan con los filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <DataTablePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          startIndex={startIndex}
          endIndex={endIndex}
          totalRecords={totalRecords}
        />
      </Card>

      {/* Modal para ver el Voucher */}
      <AnimatePresence>
        {selectedVoucher && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedVoucher(null)}
              className="fixed inset-0 bg-navy-dark/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-md w-full bg-white dark:bg-navy-dark border border-border-default rounded-2xl p-6 shadow-2xl z-10 space-y-4"
            >
              <div className="flex justify-between items-center">
                <h3 className="font-display font-bold text-lg text-text-primary">Comprobante de Depósito (Voucher)</h3>
                <button
                  onClick={() => setSelectedVoucher(null)}
                  className="p-1.5 rounded-full hover:bg-navy-light transition-colors text-text-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex justify-center bg-white p-4 rounded-xl border border-border-subtle">
                <img src={selectedVoucher} alt="Voucher de Pago" className="max-h-96 object-contain rounded-lg" />
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setSelectedVoucher(null)} variant="outline">
                  Cerrar
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal para ingresar Motivo de Rechazo (Rechazar) */}
      <AnimatePresence>
        {rejectingId !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setRejectingId(null)
                setRejectReason('')
              }}
              className="fixed inset-0 bg-navy-dark/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-sm w-full bg-white dark:bg-navy-dark border border-border-default rounded-2xl p-6 shadow-2xl z-10 space-y-4"
            >
              <h3 className="font-display font-bold text-lg text-text-primary">Rechazar Depósito / Recarga</h3>
              <p className="text-sm text-text-secondary">
                Especifica la razón por la cual se rechaza este depósito. Se notificará al usuario el motivo del rechazo.
              </p>
              <div className="space-y-2">
                <label className="text-xs font-semibold block text-text-muted">Motivo de Rechazo</label>
                <input
                  type="text"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Ej: Comprobante ilegible o falso"
                  className="w-full bg-navy-dark/50 border border-border-subtle rounded-xl px-4 py-2.5 text-text-primary focus:outline-none focus:border-cyan transition-colors"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  className="flex-1"
                  variant="outline"
                  onClick={() => {
                    setRejectingId(null)
                    setRejectReason('')
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1"
                  variant="danger"
                  loading={processMutation.isPending}
                  onClick={() =>
                    processMutation.mutate({
                      id: rejectingId,
                      estado: 'rechazado',
                      motivo_rechazo: rejectReason
                    })
                  }
                >
                  Confirmar Rechazo
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

function ApuestasSection() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [activeSubTab, setActiveSubTab] = useState('pools') // 'pools' o 'tickets'
  
  // Obtener rol del usuario autenticado
  const { user: loggedInUser } = useAuthStore()
  const isSuperAdmin = loggedInUser?.rol === 'superadmin'

  // Obtener configuraciones del sistema
  const { data: settingsData = [] } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: async () => {
      const response = await client.get('/admin/settings')
      return response.data?.datos || []
    }
  })

  // Modales
  const [selectedPool, setSelectedPool] = useState(null)
  const [showPoolModal, setShowPoolModal] = useState(false)

  // Estados del Simulador de Pozo
  const [showSimulatorModal, setShowSimulatorModal] = useState(false)
  const [simulatedBono, setSimulatedBono] = useState('3')
  const [simulatedMinDiv, setSimulatedMinDiv] = useState('1.05')
  const [simulatedGolesLocal, setSimulatedGolesLocal] = useState('0')
  const [simulatedGolesVisitante, setSimulatedGolesVisitante] = useState('0')
  const [simulatedRetencionSinGanadores, setSimulatedRetencionSinGanadores] = useState(true)
  const [simulatedPorcentajeRetencion, setSimulatedPorcentajeRetencion] = useState('50')
  const [simulatedComision, setSimulatedComision] = useState('10')
  const [simulatedClasificaWinner, setSimulatedClasificaWinner] = useState('local')

  // Helper calculations for selected pool
  const configComisionGlobal = Number(settingsData.find(s => s.clave === 'comision_apuestas')?.valor || 10);
  const sIsLiquidated = selectedPool?.estado === 'liquidado';
  const sMontoTotal = Number(selectedPool?.monto_total || 0);
  const sTotalPremios = Number(selectedPool?.total_premios || 0);
  const sDisplayCommissionPct = sIsLiquidated
    ? (sMontoTotal > 0 ? ((sMontoTotal - sTotalPremios) / sMontoTotal) * 100 : Number(selectedPool?.comision || 10))
    : configComisionGlobal;
  const sDisplayCommissionVal = sIsLiquidated
    ? (sMontoTotal - sTotalPremios)
    : sMontoTotal * (sDisplayCommissionPct / 100);
  const sDisplayNetPool = sIsLiquidated ? sTotalPremios : sMontoTotal * (1 - sDisplayCommissionPct / 100);

  // Filtros y búsquedas
  const [ticketStatusFilter, setTicketStatusFilter] = useState('all')

  // Obtener pozos de apuestas
  const { data: poolsData = [], isLoading: isLoadingPools } = useQuery({
    queryKey: ['admin-pools'],
    queryFn: async () => {
      const response = await client.get('/admin/bets/pools')
      return response.data?.datos || []
    }
  })
  const pools = poolsData

  // Ordenar pozos: primero los de estado 'abierto', y luego por fecha_partido descendente
  const sortedPools = useMemo(() => {
    return [...pools].sort((a, b) => {
      if (a.estado === 'abierto' && b.estado !== 'abierto') return -1
      if (b.estado === 'abierto' && a.estado !== 'abierto') return 1
      
      const dateA = a.fecha_partido ? new Date(a.fecha_partido.replace(' ', 'T')).getTime() : 0
      const dateB = b.fecha_partido ? new Date(b.fecha_partido.replace(' ', 'T')).getTime() : 0
      return dateB - dateA
    })
  }, [pools])

  const { data: betsData = [], isLoading: isLoadingBets } = useQuery({
    queryKey: ['admin-bets-all'],
    queryFn: async () => {
      const response = await client.get('/admin/bets')
      return response.data?.datos || []
    }
  })

  const selectedPoolBets = useMemo(() => {
    if (!selectedPool) return []
    return betsData.filter(b => {
      const bPartId = Number(b.partido_id)
      const pPartId = Number(selectedPool.partido_id)
      if (bPartId !== pPartId) return false
      const bMercado = b.mercado || '1x2'
      const pMercado = selectedPool.mercado || '1x2'
      return bMercado === pMercado
    })
  }, [betsData, selectedPool])

  // Hook para Pozos
  const {
    pageSize: poolPageSize,
    setPageSize: setPoolPageSize,
    currentPage: poolCurrentPage,
    setCurrentPage: setPoolCurrentPage,
    searchTerm: poolSearchTerm,
    setSearchTerm: setPoolSearchTerm,
    sortField: poolSortField,
    sortOrder: poolSortOrder,
    requestSort: requestPoolSort,
    paginatedData: paginatedPools,
    totalPages: poolTotalPages,
    totalRecords: poolTotalRecords,
    startIndex: poolStartIndex,
    endIndex: poolEndIndex
  } = useDataTable({
    data: sortedPools,
    searchFields: ['equipo_local_nombre', 'equipo_visitante_nombre', 'partido_fase', 'estado'],
    defaultSortField: '',
    defaultSortOrder: 'desc'
  })

  // Hook para Tickets
  const {
    pageSize: ticketPageSize,
    setPageSize: setTicketPageSize,
    currentPage: ticketCurrentPage,
    setCurrentPage: setTicketCurrentPage,
    searchTerm: ticketSearchTerm,
    setSearchTerm: setTicketSearchTerm,
    sortField: ticketSortField,
    sortOrder: ticketSortOrder,
    requestSort: requestTicketSort,
    paginatedData: paginatedTickets,
    totalPages: ticketTotalPages,
    totalRecords: ticketTotalRecords,
    startIndex: ticketStartIndex,
    endIndex: ticketEndIndex
  } = useDataTable({
    data: betsData,
    searchFields: ['usuario_nombre', 'usuario_apellido', 'usuario_email', 'id', 'equipo_local_nombre', 'equipo_visitante_nombre'],
    defaultSortField: 'id',
    defaultSortOrder: 'desc',
    customFilter: (ticket) => {
      if (!isSuperAdmin && Number(ticket.usuario_excluir_auditoria) === 1) {
        return false
      }
      return ticketStatusFilter === 'all' || ticket.estado === ticketStatusFilter
    }
  })

  const simulationResults = useMemo(() => {
    if (!selectedPool || !showSimulatorModal) return null

    const poolBets = selectedPoolBets
    const totalCollected = Number(selectedPool.monto_total || 0)

    const golesL = Number(simulatedGolesLocal || 0)
    const golesV = Number(simulatedGolesVisitante || 0)
    const bono = Number(simulatedBono || 2)
    const minDiv = Number(simulatedMinDiv || 1.20)

    // Determine simulated outcome
    let simOutcome = 'empate'
    if (selectedPool.mercado === 'clasifica') {
      if (golesL > golesV) {
        simOutcome = 'local'
      } else if (golesL < golesV) {
        simOutcome = 'visitante'
      } else {
        simOutcome = simulatedClasificaWinner
      }
    } else {
      if (golesL > golesV) simOutcome = 'local'
      else if (golesL < golesV) simOutcome = 'visitante'
    }

    // Compute participations for each bet
    let totalParticipaciones = 0
    const processedBets = poolBets.map(bet => {
      const isWinner = bet.seleccion === simOutcome
      const isExact = selectedPool.mercado === 'clasifica'
        ? false
        : (isWinner && Number(bet.goles_local_predichos) === golesL && Number(bet.goles_visitante_predichos) === golesV)
      
      let participaciones = 0
      if (isWinner) {
        participaciones = Number(bet.monto) * (isExact ? bono : 1)
      }

      totalParticipaciones += participaciones

      return {
        ...bet,
        isWinner,
        isExact,
        participaciones
      }
    })

    const totalWinners = processedBets.filter(b => b.isWinner).length

    // Retrieve settings for special retention if no winners
    const retencionSinGanadores = simulatedRetencionSinGanadores
    const porcentajeRetencion = Number(simulatedPorcentajeRetencion || '50')
    const refundCoef = retencionSinGanadores ? (1 - porcentajeRetencion / 100) : 1

    // Math calculation for commission and subsidy
    const comisionSim = Number(simulatedComision || 10.00)
    let appliedCommission = comisionSim
    let netPool = totalCollected * (1 - (comisionSim / 100))
    let valuePerParticipation = totalParticipaciones > 0 ? netPool / totalParticipaciones : 0
    let subsidyVal = 0.00

    if (totalWinners > 0) {
      if (valuePerParticipation < minDiv) {
        // Opción 1: Reducción dinámica de la comisión de la casa
        const cNew = 1 - (minDiv * totalParticipaciones) / totalCollected

        if (cNew > 0) {
          appliedCommission = cNew * 100
          netPool = totalCollected * (1 - cNew)
          valuePerParticipation = netPool / totalParticipaciones
        } else {
          // Opción 2: Comisión a 0% y subsidio por déficit
          appliedCommission = 0.00
          netPool = totalCollected
          valuePerParticipation = minDiv
          
          const totalToPay = totalParticipaciones * minDiv
          subsidyVal = totalToPay - totalCollected
        }
      }
    } else {
      // Caso sin ganadores: Reembolso / Devolución
      appliedCommission = retencionSinGanadores ? porcentajeRetencion : 0.00
      netPool = totalCollected * refundCoef
      valuePerParticipation = 0.00
    }

    // Assign final simulated payouts
    const finalBets = processedBets.map(b => {
      let payout = 0
      let isRefund = false

      if (totalWinners > 0) {
        payout = b.isWinner ? b.participaciones * valuePerParticipation : 0
      } else {
        payout = Number(b.monto) * refundCoef
        isRefund = true
      }

      return {
        ...b,
        simulatedPayout: payout,
        isRefund,
        refundCoef
      }
    })

    return {
      totalCollected,
      appliedCommission,
      netPool,
      totalWinners,
      subsidyVal,
      valuePerParticipation,
      totalParticipaciones,
      refundCoef,
      finalBets
    }
  }, [selectedPool, showSimulatorModal, simulatedGolesLocal, simulatedGolesVisitante, simulatedBono, simulatedMinDiv, simulatedRetencionSinGanadores, simulatedPorcentajeRetencion, simulatedComision, selectedPoolBets, simulatedClasificaWinner])


  const settleMutation = useMutation({
    mutationFn: async (partidoId) => {
      const response = await client.post('/admin/bets/settle', { partido_id: partidoId })
      return response.data
    },
    onSuccess: () => {
      toast.success('El pozo de apuestas ha sido liquidado correctamente.')
      queryClient.invalidateQueries({ queryKey: ['admin-pools'] })
      queryClient.invalidateQueries({ queryKey: ['admin-bets-all'] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.mensaje || 'Error al liquidar apuestas.')
    }
  })



  // Funciones de Impresión
  const handlePrintTicket = (ticket) => {
    const printWindow = window.open('', '_blank', 'width=800,height=600')
    if (!printWindow) {
      toast.warning('Por favor habilita las ventanas emergentes para imprimir.')
      return
    }

    const html = `
      <html>
        <head>
          <title>Ticket de Apuesta #${ticket.id}</title>
          <style>
            body { font-family: 'Outfit', 'Plus Jakarta Sans', Arial, sans-serif; color: #1e293b; padding: 20px; line-height: 1.5; }
            .ticket-card { border: 2px solid #cbd5e1; border-radius: 12px; padding: 24px; max-width: 500px; margin: 0 auto; background: #fff; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); }
            .header { text-align: center; border-bottom: 2px dashed #e2e8f0; padding-bottom: 16px; margin-bottom: 20px; }
            .header h1 { margin: 0; font-size: 24px; color: #0f172a; }
            .header p { margin: 4px 0 0; font-size: 12px; color: #64748b; }
            .match-details { text-align: center; margin: 20px 0; background: #f8fafc; padding: 16px; border-radius: 8px; border: 1px solid #f1f5f9; }
            .match-title { font-weight: bold; font-size: 16px; margin-bottom: 4px; }
            .match-meta { font-size: 11px; color: #64748b; text-transform: uppercase; }
            .details-grid { display: grid; grid-template-cols: 1fr 1fr; gap: 12px; font-size: 13px; border-bottom: 1px solid #e2e8f0; padding-bottom: 16px; margin-bottom: 16px; }
            .details-label { color: #64748b; }
            .details-value { font-weight: 600; text-align: right; color: #0f172a; }
            .payout-section { display: flex; justify-content: space-between; align-items: center; background: #f0fdf4; border: 1px solid #bbf7d0; padding: 12px 16px; border-radius: 8px; margin-top: 16px; }
            .payout-label { font-weight: bold; color: #15803d; font-size: 14px; }
            .payout-value { font-weight: 800; font-size: 18px; color: #166534; }
            .footer { text-align: center; font-size: 10px; color: #94a3b8; margin-top: 30px; border-top: 1px solid #f1f5f9; padding-top: 12px; }
            @media print {
              body { padding: 0; background: none; }
              .ticket-card { border: 1px solid #000; box-shadow: none; max-width: 100%; margin: 0; }
            }
          </style>
        </head>
        <body>
          <div class="ticket-card">
            <div class="header">
              <h1>SUBIGOL</h1>
              <p>Comprobante Oficial de Apuesta</p>
              <p style="margin-top: 6px; font-weight: bold; color: #0f172a;">TICKET ID: #${ticket.id}</p>
            </div>
            
            <div class="details-grid">
              <div class="details-label">Usuario:</div>
              <div class="details-value">
                ${ticket.usuario_nombre} ${ticket.usuario_apellido}
                ${Number(ticket.usuario_premium) === 1 ? ' <span style="background: #fef3c7; color: #b45309; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: bold; border: 1px solid #fde68a; margin-left: 6px;">★ PREMIUM</span>' : ''}
              </div>
              
              <div class="details-label">Correo Electrónico:</div>
              <div class="details-value">${ticket.usuario_email}</div>
              
              <div class="details-label">Fecha Registro:</div>
              <div class="details-value">${new Date(ticket.creado_en).toLocaleString('es-BO')}</div>
            </div>
            
            <div class="match-details">
              <div class="match-title" style="display: flex; align-items: center; justify-content: center; gap: 8px;">
                <img src="https://flagcdn.com/w40/${ticket.equipo_local_bandera ? ticket.equipo_local_bandera.toLowerCase() : 'unknown'}.png" style="width: 20px; height: 14px; object-fit: cover; border-radius: 2px; border: 1px solid #cbd5e1;" onerror="this.style.display='none'" />
                <span>${ticket.equipo_local_nombre} vs ${ticket.equipo_visitante_nombre}</span>
                <img src="https://flagcdn.com/w40/${ticket.equipo_visitante_bandera ? ticket.equipo_visitante_bandera.toLowerCase() : 'unknown'}.png" style="width: 20px; height: 14px; object-fit: cover; border-radius: 2px; border: 1px solid #cbd5e1;" onerror="this.style.display='none'" />
              </div>
              <div class="match-meta">${ticket.partido_fase.replace('_', ' ')} — ${new Date(ticket.fecha_partido.replace(' ', 'T')).toLocaleString('es-BO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
            </div>
            
            <div class="details-grid" style="border-bottom: none; margin-bottom: 0; padding-bottom: 0;">
              <div class="details-label">Selección Realizada:</div>
              <div class="details-value" style="text-transform: capitalize;">
                ${ticket.seleccion === 'local' ? ticket.equipo_local_nombre : ticket.seleccion === 'visitante' ? ticket.equipo_visitante_nombre : 'Empate'}
              </div>
              
              <div class="details-label">Marcador Predicho:</div>
              <div class="details-value">${ticket.goles_local_predichos} - ${ticket.goles_visitante_predichos}</div>
              
              <div class="details-label">Importe Apostado:</div>
              <div class="details-value" style="color: #0284c7;">${Number(ticket.monto).toFixed(2)} Bs</div>
              
              <div class="details-label">Estado de Ticket:</div>
              <div class="details-value" style="text-transform: uppercase; font-weight: bold;">
                ${ticket.estado === 'pendiente' ? 'Pendiente ⏳' : ticket.estado === 'ganada' ? 'Ganador 🏆' : ticket.estado === 'perdida' ? 'No Acertado ❌' : ticket.estado}
              </div>
            </div>
            
            ${ticket.estado === 'ganada' ? `
              <div class="payout-section">
                <span class="payout-label">Premio Cobrado:</span>
                <span class="payout-value">${Number(ticket.premio_pagado).toFixed(2)} Bs</span>
              </div>
            ` : ''}

            ${ticket.estado === 'devuelta' ? `
              <div class="payout-section" style="background: #f1f5f9; border-color: #cbd5e1;">
                <span class="payout-label" style="color: #475569;">Monto Devuelto:</span>
                <span class="payout-value" style="color: #334155;">${Number(ticket.premio_pagado).toFixed(2)} Bs</span>
              </div>
            ` : ''}
            
            <div class="footer">
              <p>Gracias por participar en SubiGol. Este es un comprobante de auditoría de sistema.</p>
              <p>Generado el: ${new Date().toLocaleString('es-BO')}</p>
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            }
          </script>
        </body>
      </html>
    `
    printWindow.document.write(html)
    printWindow.document.close()
  }

  const handlePrintPool = (pool, poolBets) => {
    const printWindow = window.open('', '_blank', 'width=900,height=700')
    if (!printWindow) {
      toast.warning('Por favor habilita las ventanas emergentes para imprimir.')
      return
    }

    const totalParticipants = poolBets.length
    const isPoolLiquidated = pool.estado === 'liquidado'
    const pMontoTotal = Number(pool.monto_total || 0)
    const pTotalPremios = Number(pool.total_premios || 0)

    const pDisplayNetPool = isPoolLiquidated ? pTotalPremios : pMontoTotal * (1 - Number(pool.comision) / 100)
    const pDisplayCommissionPct = isPoolLiquidated
      ? (pMontoTotal > 0 ? ((pMontoTotal - pTotalPremios) / pMontoTotal) * 100 : Number(pool.comision))
      : Number(pool.comision)
    const pDisplayCommissionVal = isPoolLiquidated
      ? (pMontoTotal - pTotalPremios)
      : pMontoTotal * (Number(pool.comision) / 100)
    
    const rows = poolBets.map((bet, index) => `
      <tr class="${bet.estado === 'ganada' ? 'row-winner' : ''}">
        <td>${index + 1}</td>
        <td>
          ${bet.usuario_nombre} ${bet.usuario_apellido}
          ${Number(bet.usuario_premium) === 1 ? ' <span style="background: #fef3c7; color: #b45309; padding: 1px 4px; border-radius: 3px; font-size: 8px; font-weight: bold; border: 1px solid #fde68a; margin-left: 4px;">★ PREMIUM</span>' : ''}
          <br><small>${bet.usuario_email}</small>
        </td>
        <td style="text-transform: capitalize;">
          ${bet.seleccion === 'local' 
            ? `<img src="https://flagcdn.com/w40/${pool.equipo_local_bandera.toLowerCase()}.png" style="width: 14px; height: 10px; object-fit: cover; border-radius: 1px; vertical-align: middle; margin-right: 4px;" />` + pool.equipo_local_nombre 
            : bet.seleccion === 'visitante' 
            ? `<img src="https://flagcdn.com/w40/${pool.equipo_visitante_bandera.toLowerCase()}.png" style="width: 14px; height: 10px; object-fit: cover; border-radius: 1px; vertical-align: middle; margin-right: 4px;" />` + pool.equipo_visitante_nombre 
            : 'Empate'}
        </td>
        <td style="text-align: center;">${bet.goles_local_predichos} - ${bet.goles_visitante_predichos}</td>
        <td style="text-align: right; font-weight: bold;">${Number(bet.monto).toFixed(2)} Bs</td>
        <td style="text-transform: capitalize; text-align: center;">
          <span class="badge ${bet.estado === 'ganada' ? 'badge-success' : bet.estado === 'pendiente' ? 'badge-warning' : 'badge-danger'}">
            ${bet.estado === 'ganada' ? 'Ganador 🏆' : bet.estado === 'pendiente' ? 'Pendiente' : bet.estado === 'perdida' ? 'No Acertado' : bet.estado}
          </span>
        </td>
        <td style="text-align: right; font-weight: bold; color: ${bet.estado === 'ganada' ? (Number(bet.premio_pagado) > Number(bet.monto) ? '#166534' : Number(bet.premio_pagado) === Number(bet.monto) ? '#475569' : '#b91c1c') : '#64748b'};">
          ${Number(bet.premio_pagado).toFixed(2)} Bs
          ${bet.estado === 'ganada' && Number(bet.premio_pagado) !== Number(bet.monto)
            ? `<br><span style="font-size: 9px; font-weight: normal; color: ${Number(bet.premio_pagado) > Number(bet.monto) ? '#166534' : '#b91c1c'}">(${Number(bet.premio_pagado) > Number(bet.monto) ? '+' : ''}${(Number(bet.premio_pagado) - Number(bet.monto)).toFixed(2)} Bs)</span>`
            : ''
          }
        </td>
      </tr>
    `).join('')

    const html = `
      <html>
        <head>
          <title>Auditoría de Pozo - ${pool.equipo_local_nombre} vs ${pool.equipo_visitante_nombre}</title>
          <style>
            body { font-family: 'Outfit', 'Plus Jakarta Sans', Arial, sans-serif; color: #1e293b; padding: 30px; line-height: 1.4; }
            .report-container { max-width: 850px; margin: 0 auto; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px double #cbd5e1; padding-bottom: 16px; margin-bottom: 24px; }
            .header-title h1 { margin: 0; font-size: 26px; color: #0f172a; }
            .header-title p { margin: 4px 0 0; font-size: 13px; color: #64748b; }
            .report-meta { text-align: right; font-size: 12px; color: #64748b; }
            
            .kpi-grid { display: grid; grid-template-cols: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
            .kpi-card { border: 1px solid #e2e8f0; background: #f8fafc; padding: 12px 16px; border-radius: 8px; text-align: center; }
            .kpi-val { font-size: 16px; font-weight: bold; color: #0f172a; margin-top: 4px; }
            .kpi-lbl { font-size: 10px; color: #64748b; text-transform: uppercase; font-weight: bold; }
            
            table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 16px; }
            th { background: #f1f5f9; color: #475569; font-weight: bold; text-align: left; padding: 10px 12px; border-bottom: 2px solid #cbd5e1; text-transform: uppercase; font-size: 10px; }
            td { padding: 10px 12px; border-bottom: 1px solid #e2e8f0; color: #334155; }
            tr:hover { background: #f8fafc; }
            .row-winner { background: #f0fdf4; }
            .row-winner:hover { background: #dcfce7; }
            
            .badge { font-weight: bold; font-size: 10px; padding: 2px 6px; border-radius: 4px; display: inline-block; }
            .badge-success { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
            .badge-warning { background: #ffedd5; color: #c2410c; border: 1px solid #fed7aa; }
            .badge-danger { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
            
            small { font-size: 10px; color: #64748b; }
            .footer { text-align: center; font-size: 10px; color: #94a3b8; margin-top: 40px; border-top: 1px solid #e2e8f0; padding-top: 16px; }
            @media print {
              body { padding: 0; }
              .kpi-card { border: 1px solid #000; background: none; }
              th { background: #e2e8f0; border-bottom: 2px solid #000; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .row-winner { background: #f0fdf4 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="report-container">
            <div class="header">
              <div class="header-title">
                <h1>SUBIGOL - REPORTE DE AUDITORÍA</h1>
                <p>Liquidación de Pozo y Distribución de Premios por Partido</p>
              </div>
              <div class="report-meta">
                <p><strong>Partido ID:</strong> #${pool.partido_id}</p>
                <p><strong>Fecha Partido:</strong> ${new Date(pool.fecha_partido.replace(' ', 'T')).toLocaleString('es-BO')}</p>
                <p><strong>Estado Pozo:</strong> ${pool.estado.toUpperCase()} 🏆</p>
              </div>
            </div>
            
            <div style="background: #f8fafc; border: 1px solid #e2e8f0; padding: 16px; border-radius: 8px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center;">
              <div style="font-size: 16px; font-weight: bold; color: #0f172a; display: flex; align-items: center; gap: 6px;">
                <img src="https://flagcdn.com/w40/${pool.equipo_local_bandera.toLowerCase()}.png" style="width: 20px; height: 14px; object-fit: cover; border-radius: 2px; border: 1px solid #cbd5e1;" />
                ${pool.equipo_local_nombre} (${pool.goles_local !== null ? pool.goles_local : '-'}) vs (${pool.goles_visitante !== null ? pool.goles_visitante : '-'}) ${pool.equipo_visitante_nombre}
                <img src="https://flagcdn.com/w40/${pool.equipo_visitante_bandera.toLowerCase()}.png" style="width: 20px; height: 14px; object-fit: cover; border-radius: 2px; border: 1px solid #cbd5e1;" />
              </div>
              <div style="font-size: 12px; color: #475569;">
                Fase: <span style="text-transform: capitalize; font-weight: bold;">${FASE_NOMBRES[pool.partido_fase] || pool.partido_fase?.replace('_', ' ') || ''}</span>
              </div>
            </div>
            
            <div class="kpi-grid">
              <div class="kpi-card">
                <div class="kpi-lbl">Pozo Recaudado</div>
                <div class="kpi-val">${Number(pool.monto_total || 0).toFixed(2)} Bs</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-lbl">${isPoolLiquidated && Number(pool.total_ganadores || 0) === 0 ? 'Retención Casa' : 'Comisión Casa'} (${pDisplayCommissionPct.toFixed(0)}%)</div>
                <div class="kpi-val">${pDisplayCommissionVal.toFixed(2)} Bs</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-lbl">${isPoolLiquidated && Number(pool.total_ganadores || 0) === 0 ? 'Reembolsado a Usuarios' : 'Pozo Neto Distribuido'}</div>
                <div class="kpi-val" style="color: #0284c7;">${pDisplayNetPool.toFixed(2)} Bs</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-lbl">Participantes / Ganadores</div>
                <div class="kpi-val">${totalParticipants} / ${pool.total_ganadores || 0}</div>
              </div>
            </div>
            
            <h2 style="font-size: 14px; font-weight: bold; color: #0f172a; margin-top: 24px; border-bottom: 1px solid #cbd5e1; padding-bottom: 6px;">
              Desglose Detallado de Apuestas y Participantes
            </h2>
            
            <table>
              <thead>
                <tr>
                  <th style="width: 40px;">#</th>
                  <th>Usuario</th>
                  <th>Selección</th>
                  <th style="text-align: center; width: 60px;">Marcador Pred.</th>
                  <th style="text-align: right; width: 100px;">Monto Apostado</th>
                  <th style="text-align: center; width: 90px;">Estado</th>
                  <th style="text-align: right; width: 110px;">Premio Pagado</th>
                </tr>
              </thead>
              <tbody>
                ${rows}
              </tbody>
            </table>
            
            <div class="footer">
              <p>Gracias por participar en SubiGol. Este es un reporte de auditoría del pozo generado por el sistema.</p>
              <p>Generado el: ${new Date().toLocaleString('es-BO')}</p>
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            }
          </script>
        </body>
      </html>
    `
    printWindow.document.write(html)
    printWindow.document.close()
  }

  if (isLoadingPools || isLoadingBets) {
    return <Skeleton className="h-96 w-full rounded-2xl" />
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <h2 className="font-display font-bold text-xl text-text-primary">
        Auditoría y Gestión de Apuestas
      </h2>

      {/* Sub-tabs de control */}
      <div className="flex gap-2 border-b border-border-subtle pb-3 mb-6">
        <button
          onClick={() => setActiveSubTab('pools')}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
            activeSubTab === 'pools'
              ? 'bg-cyan/10 text-cyan border border-cyan/20'
              : 'text-text-muted hover:text-text-primary hover:bg-navy-light/30'
          }`}
        >
          Pozos por Partido
        </button>
        <button
          onClick={() => setActiveSubTab('tickets')}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
            activeSubTab === 'tickets'
              ? 'bg-cyan/10 text-cyan border border-cyan/20'
              : 'text-text-muted hover:text-text-primary hover:bg-navy-light/30'
          }`}
        >
          Tickets de Usuario
        </button>
      </div>

      {activeSubTab === 'pools' && (
        <div className="space-y-4">
          <Card className="p-4">
            <DataTableControls
              pageSize={poolPageSize}
              setPageSize={setPoolPageSize}
              searchTerm={poolSearchTerm}
              setSearchTerm={setPoolSearchTerm}
              placeholder="Buscar por equipo o fase..."
            />

            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left font-body">
                <thead className="bg-navy-dark/40 text-text-muted text-xs uppercase tracking-wider border-b border-border-subtle">
                  <tr>
                    <SortableHeader field="equipo_local_nombre" currentField={poolSortField} sortOrder={poolSortOrder} label="Partido" onSort={requestPoolSort} className="px-6 py-4" />
                    <SortableHeader field="partido_fase" currentField={poolSortField} sortOrder={poolSortOrder} label="Fase" onSort={requestPoolSort} className="px-6 py-4" />
                    <SortableHeader field="monto_total" currentField={poolSortField} sortOrder={poolSortOrder} label="Pozo Total" onSort={requestPoolSort} className="px-6 py-4 text-right" />
                    <SortableHeader field="comision" currentField={poolSortField} sortOrder={poolSortOrder} label="Comisión" onSort={requestPoolSort} className="px-6 py-4 text-center" />
                    <th className="px-6 py-4 text-right text-text-muted">Pozo Neto</th>
                    <SortableHeader field="total_participantes" currentField={poolSortField} sortOrder={poolSortOrder} label="Part. / Ganadores" onSort={requestPoolSort} className="px-6 py-4 text-center" />
                    <SortableHeader field="estado" currentField={poolSortField} sortOrder={poolSortOrder} label="Estado" onSort={requestPoolSort} className="px-6 py-4 text-center" />
                    <th className="px-6 py-4 text-right text-text-muted">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {paginatedPools.map((pool) => {
                    const isLiquidated = pool.estado === 'liquidado';
                    const isMatchFinished = pool.partido_estado === 'finalizado';
                    const montoTotal = Number(pool.monto_total || 0);
                    const totalPremios = Number(pool.total_premios || 0);
                    
                    const displayNetPool = isLiquidated ? totalPremios : montoTotal * (1 - Number(pool.comision) / 100);
                    const displayCommissionPct = isLiquidated 
                      ? (montoTotal > 0 ? ((montoTotal - totalPremios) / montoTotal) * 100 : Number(pool.comision))
                      : Number(pool.comision);
                    
                    return (
                      <tr key={pool.id} className="hover:bg-navy-dark/10 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="font-semibold text-text-primary flex items-center gap-1.5 align-middle">
                              <Flag code={pool.equipo_local_bandera} className="w-5 h-3.5 object-cover rounded shadow-sm inline-block mr-0.5 align-middle" />
                              <span>{pool.equipo_local_nombre}</span>
                              <span className="text-text-muted font-normal text-xs px-0.5">vs</span>
                              <Flag code={pool.equipo_visitante_bandera} className="w-5 h-3.5 object-cover rounded shadow-sm inline-block mr-0.5 align-middle" />
                              <span>{pool.equipo_visitante_nombre}</span>
                            </span>
                            <span className="text-xs text-text-muted mt-0.5">
                              {new Date(pool.fecha_partido.replace(' ', 'T')).toLocaleString('es-BO', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                              {pool.mercado && (
                                <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold font-sans ${
                                  pool.mercado === 'clasifica'
                                    ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
                                    : 'bg-cyan/10 text-cyan border border-cyan/20'
                                }`}>
                                  {pool.mercado === 'clasifica' ? '🏆 Clasificación' : '⏱️ 90 min'}
                                </span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-text-secondary text-xs capitalize">
                          {FASE_NOMBRES[pool.partido_fase] || pool.partido_fase?.replace('_', ' ') || ''}
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-text-primary">
                          {montoTotal.toFixed(2)} Bs
                        </td>
                        <td className="px-6 py-4 text-center text-text-secondary font-mono text-xs">
                          {displayCommissionPct.toFixed(0)}%
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-cyan">
                          {displayNetPool.toFixed(2)} Bs
                        </td>
                        <td className="px-6 py-4 text-center text-text-secondary text-xs">
                          {pool.total_participantes || 0} / {pool.total_ganadores || 0}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            pool.estado === 'liquidado'
                              ? 'bg-success/10 text-success border border-success/20'
                              : pool.estado === 'cerrado'
                              ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                              : 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                          }`}>
                            {pool.estado}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={() => {
                                setSelectedPool(pool)
                                setShowPoolModal(true)
                              }}
                            >
                              Distribución
                            </Button>
                            <Button
                              size="xs"
                              variant="outline"
                              className="p-1 px-2 flex items-center justify-center hover:text-cyan border hover:border-cyan/50"
                              title="Simular Distribución"
                              onClick={() => {
                                setSelectedPool(pool)
                                const configBono = settingsData.find(s => s.clave === 'multiplicador_goles_exacto')?.valor || '3'
                                const configMinDiv = settingsData.find(s => s.clave === 'dividendo_minimo_ganador')?.valor || '1.05'
                                const configRetencionActivo = settingsData.find(s => s.clave === 'retencion_sin_ganadores_activado')?.valor === '1'
                                const configPorcentajeRetencion = settingsData.find(s => s.clave === 'porcentaje_retencion_sin_ganadores')?.valor || '50'
                                const configComision = (settingsData.find(s => s.clave === 'comision_apuestas')?.valor) || (pool.comision !== null && pool.comision !== undefined ? String(pool.comision) : '10')
                                
                                const initGolesLocal = pool.goles_local !== null && pool.goles_local !== undefined ? String(pool.goles_local) : '0'
                                const initGolesVisitante = pool.goles_visitante !== null && pool.goles_visitante !== undefined ? String(pool.goles_visitante) : '0'
                                const initClasificaWinner = pool.equipo_ganador_id && pool.equipo_visitante_id && Number(pool.equipo_ganador_id) === Number(pool.equipo_visitante_id) ? 'visitante' : 'local'

                                setSimulatedBono(configBono)
                                setSimulatedMinDiv(configMinDiv)
                                setSimulatedRetencionSinGanadores(configRetencionActivo)
                                setSimulatedPorcentajeRetencion(configPorcentajeRetencion)
                                setSimulatedComision(configComision)
                                setSimulatedGolesLocal(initGolesLocal)
                                setSimulatedGolesVisitante(initGolesVisitante)
                                setSimulatedClasificaWinner(initClasificaWinner)
                                setShowSimulatorModal(true)
                              }}
                            >
                              <Calculator className="w-4 h-4" />
                            </Button>
                            {!isLiquidated && isMatchFinished && (
                              <Button
                                size="xs"
                                variant="success"
                                loading={settleMutation.isPending}
                                onClick={() => {
                                  if (window.confirm(`¿Estás seguro de liquidar el pozo de apuestas de ${pool.equipo_local_nombre} vs ${pool.equipo_visitante_nombre}?`)) {
                                    settleMutation.mutate(pool.partido_id)
                                  }
                                }}
                              >
                                Liquidar
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                  {poolTotalRecords === 0 && (
                    <tr>
                      <td colSpan="8" className="px-6 py-8 text-center text-text-muted">
                        No hay pozos de apuestas creados o coincidentes.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <DataTablePagination
              currentPage={poolCurrentPage}
              setCurrentPage={setPoolCurrentPage}
              totalPages={poolTotalPages}
              startIndex={poolStartIndex}
              endIndex={poolEndIndex}
              totalRecords={poolTotalRecords}
            />
          </Card>
        </div>
      )}

      {activeSubTab === 'tickets' && (
        <div className="space-y-4">
          <Card className="p-4">
            <DataTableControls
              pageSize={ticketPageSize}
              setPageSize={setTicketPageSize}
              searchTerm={ticketSearchTerm}
              setSearchTerm={setTicketSearchTerm}
              placeholder="Buscar por usuario, email o equipo..."
            >
              <div className="w-full sm:w-64 flex gap-2 shrink-0">
                <select
                  value={ticketStatusFilter}
                  onChange={(e) => { setTicketStatusFilter(e.target.value); setTicketCurrentPage(1); }}
                  className="w-full bg-surface-card border border-border-default rounded-xl p-1.5 text-xs focus:border-cyan focus:outline-none text-text-secondary cursor-pointer"
                >
                  <option value="all">Todos los estados</option>
                  <option value="pendiente">Pendientes ⏳</option>
                  <option value="ganada">Ganadores 🏆</option>
                  <option value="perdida">No Acertados ❌</option>
                  <option value="devuelta">Devueltos ↩️</option>
                </select>
              </div>
            </DataTableControls>

            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left font-body">
                <thead className="bg-navy-dark/40 text-text-muted text-xs uppercase tracking-wider border-b border-border-subtle">
                  <tr>
                    <SortableHeader field="id" currentField={ticketSortField} sortOrder={ticketSortOrder} label="Ticket" onSort={requestTicketSort} className="px-6 py-4" />
                    <SortableHeader field="usuario_nombre" currentField={ticketSortField} sortOrder={ticketSortOrder} label="Usuario" onSort={requestTicketSort} className="px-6 py-4" />
                    <SortableHeader field="equipo_local_nombre" currentField={ticketSortField} sortOrder={ticketSortOrder} label="Partido" onSort={requestTicketSort} className="px-6 py-4" />
                    <SortableHeader field="seleccion" currentField={ticketSortField} sortOrder={ticketSortOrder} label="Predicción" onSort={requestTicketSort} className="px-6 py-4" />
                    <SortableHeader field="monto" currentField={ticketSortField} sortOrder={ticketSortOrder} label="Monto" onSort={requestTicketSort} className="px-6 py-4 text-right" />
                    <SortableHeader field="premio_pagado" currentField={ticketSortField} sortOrder={ticketSortOrder} label="Premio" onSort={requestTicketSort} className="px-6 py-4 text-right" />
                    <SortableHeader field="estado" currentField={ticketSortField} sortOrder={ticketSortOrder} label="Estado" onSort={requestTicketSort} className="px-6 py-4 text-center" />
                    <th className="px-6 py-4 text-right text-text-muted">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {paginatedTickets.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-navy-dark/10 transition-colors">
                      <td className="px-6 py-4 font-mono text-xs text-text-secondary">
                        #{ticket.id}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-text-primary flex items-center gap-1.5 flex-wrap">
                          <span>{ticket.usuario_nombre} {ticket.usuario_apellido}</span>
                          {Number(ticket.usuario_premium) === 1 && (
                            <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 inline-flex items-center gap-0.5">
                              ★ Premium
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-text-muted">{ticket.usuario_email}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-text-primary text-xs font-semibold flex items-center gap-1.5 align-middle">
                            <Flag code={ticket.equipo_local_bandera} className="w-5 h-3.5 object-cover rounded shadow-sm inline-block mr-0.5 align-middle" />
                            <span>{ticket.equipo_local_nombre}</span>
                            <span className="text-text-muted font-normal text-xs px-0.5">vs</span>
                            <Flag code={ticket.equipo_visitante_bandera} className="w-5 h-3.5 object-cover rounded shadow-sm inline-block mr-0.5 align-middle" />
                            <span>{ticket.equipo_visitante_nombre}</span>
                          </span>
                          <span className="text-[10px] text-text-muted">
                            {new Date(ticket.fecha_partido.replace(' ', 'T')).toLocaleString('es-BO', {
                              day: 'numeric',
                              month: 'short',
                            })}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs">
                        <p className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${
                          ticket.mercado === 'clasifica' ? 'text-purple-400' : 'text-cyan'
                        }`}>
                          {ticket.mercado === 'clasifica' ? '🏆 Clasificación' : '⏱️ 90 min'}
                        </p>
                        <p className="capitalize text-text-primary font-medium">
                          Sel: {ticket.seleccion === 'local' ? ticket.equipo_local_nombre : ticket.seleccion === 'visitante' ? ticket.equipo_visitante_nombre : 'Empate'}
                        </p>
                        {ticket.mercado !== 'clasifica' && (
                          <p className="text-text-muted text-[10px] font-mono">
                            Pred: {ticket.goles_local_predichos} - {ticket.goles_visitante_predichos}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right font-mono font-bold text-text-primary text-xs">
                        {Number(ticket.monto).toFixed(2)} Bs
                      </td>
                      <td className={`px-6 py-4 text-right font-mono font-bold text-xs ${
                        Number(ticket.premio_pagado || 0) > Number(ticket.monto)
                          ? 'text-emerald-400'
                          : Number(ticket.premio_pagado || 0) === Number(ticket.monto)
                          ? 'text-text-secondary'
                          : 'text-rose-400'
                      }`}>
                        {Number(ticket.premio_pagado || 0) > 0 ? (
                          <>
                            <div>{Number(ticket.premio_pagado).toFixed(2)} Bs</div>
                            {Number(ticket.premio_pagado) !== Number(ticket.monto) && (
                              <div className="text-[10px] opacity-80 font-normal">
                                ({(Number(ticket.premio_pagado) - Number(ticket.monto)) >= 0 ? '+' : ''}
                                {(Number(ticket.premio_pagado) - Number(ticket.monto)).toFixed(2)} Bs)
                              </div>
                            )}
                          </>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          ticket.estado === 'ganada'
                            ? 'bg-success/10 text-success border border-success/20'
                            : ticket.estado === 'pendiente'
                            ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                            : ticket.estado === 'devuelta'
                            ? 'bg-gray-500/10 text-gray-400 border border-gray-500/20'
                            : 'bg-red-500/10 text-red-400 border border-red-500/20'
                        }`}>
                          {ticket.estado}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => handlePrintTicket(ticket)}
                          className="flex items-center gap-1 ml-auto"
                        >
                          <Receipt className="w-3.5 h-3.5" />
                          Imprimir
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {ticketTotalRecords === 0 && (
                    <tr>
                      <td colSpan="8" className="px-6 py-8 text-center text-text-muted">
                        No se encontraron tickets con los filtros aplicados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <DataTablePagination
              currentPage={ticketCurrentPage}
              setCurrentPage={setTicketCurrentPage}
              totalPages={ticketTotalPages}
              startIndex={ticketStartIndex}
              endIndex={ticketEndIndex}
              totalRecords={ticketTotalRecords}
            />
          </Card>
        </div>
      )}

      {/* Modal de Distribución detallado */}
      <AnimatePresence>
        {showPoolModal && selectedPool && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setShowPoolModal(false)
                setSelectedPool(null)
              }}
              className="fixed inset-0 bg-navy-dark/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-4xl w-full bg-white dark:bg-navy-dark border border-border-default rounded-2xl p-6 shadow-2xl z-10 space-y-4 max-h-[90vh] flex flex-col"
            >
              <div className="flex justify-between items-center">
                <h3 className="font-display font-bold text-lg text-text-primary">
                  Distribución del Pozo de Apuestas
                </h3>
                <button
                  onClick={() => {
                    setShowPoolModal(false)
                    setSelectedPool(null)
                  }}
                  className="p-1.5 rounded-full hover:bg-navy-light transition-colors text-text-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="overflow-y-auto space-y-4 flex-1 pr-1">
                {/* Cabecera del Partido en el modal */}
                <div className="bg-slate-50 dark:bg-navy-dark/40 border border-border-subtle p-4 rounded-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h4 className="font-bold text-text-primary text-base flex items-center gap-1.5 align-middle">
                      <Flag code={selectedPool.equipo_local_bandera} className="w-5 h-3.5 object-cover rounded shadow-sm mr-1 align-middle" />
                      <span>{selectedPool.equipo_local_nombre}</span> 
                      <span className="text-cyan">
                        ({selectedPool.goles_local !== null ? selectedPool.goles_local : '-'})
                      </span>
                      <span className="text-text-muted font-normal text-xs px-0.5">vs</span>
                      <span className="text-cyan">
                        ({selectedPool.goles_visitante !== null ? selectedPool.goles_visitante : '-'})
                      </span>
                      <Flag code={selectedPool.equipo_visitante_bandera} className="w-5 h-3.5 object-cover rounded shadow-sm mr-1 align-middle" />
                      <span>{selectedPool.equipo_visitante_nombre}</span>
                    </h4>
                    <p className="text-xs text-text-muted mt-1 capitalize">
                      Fase: {FASE_NOMBRES[selectedPool.partido_fase] || selectedPool.partido_fase?.replace('_', ' ')} — {new Date(selectedPool.fecha_partido.replace(' ', 'T')).toLocaleString('es-BO')}
                    </p>
                  </div>
                  <div className="text-left md:text-right">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      selectedPool.estado === 'liquidado'
                        ? 'bg-success/10 text-success border border-success/20'
                        : 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                    }`}>
                      Pozo {selectedPool.estado}
                    </span>
                  </div>
                </div>

                {sIsLiquidated && Number(selectedPool.monto_subsidio) > 0 && (
                  <div className="bg-orange-500/10 border border-orange-500/20 text-orange-400 p-3.5 rounded-xl text-xs flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 text-orange-400" />
                    <div>
                      <span className="font-semibold">Pozo Subsidiado:</span> La plataforma inyectó <span className="font-bold font-mono">{Number(selectedPool.monto_subsidio).toFixed(2)} Bs</span> para cumplir con el dividendo mínimo garantizado (la comisión efectiva fue de 0%).
                    </div>
                  </div>
                )}

                {/* KPI stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 dark:bg-navy-dark/20 border border-border-subtle/50 p-3 rounded-lg text-center">
                    <p className="text-[10px] text-text-muted uppercase font-bold">Total Recaudado</p>
                    <p className="font-mono font-bold text-base text-text-primary mt-1">
                      {sMontoTotal.toFixed(2)} Bs
                    </p>
                  </div>
                  <div className="bg-slate-50 dark:bg-navy-dark/20 border border-border-subtle/50 p-3 rounded-lg text-center">
                    <p className="text-[10px] text-text-muted uppercase font-bold">
                      {sIsLiquidated && Number(selectedPool.total_ganadores || 0) === 0 ? 'Retención Casa' : 'Comisión'} ({sDisplayCommissionPct.toFixed(0)}%)
                    </p>
                    <p className="font-mono font-bold text-base text-text-muted mt-1">
                      {sDisplayCommissionVal.toFixed(2)} Bs
                    </p>
                  </div>
                  <div className="bg-slate-50 dark:bg-navy-dark/20 border border-border-subtle/50 p-3 rounded-lg text-center">
                    <p className="text-[10px] text-text-muted uppercase font-bold">
                      {sIsLiquidated && Number(selectedPool.total_ganadores || 0) === 0 ? 'Reembolsado a Usuarios' : 'Pozo Neto'}
                    </p>
                    <p className="font-mono font-bold text-base text-cyan mt-1">
                      {sDisplayNetPool.toFixed(2)} Bs
                    </p>
                  </div>
                  <div className="bg-slate-50 dark:bg-navy-dark/20 border border-border-subtle/50 p-3 rounded-lg text-center">
                    <p className="text-[10px] text-text-muted uppercase font-bold">Participantes/Ganadores</p>
                    <p className="font-mono font-bold text-base text-text-primary mt-1">
                      {selectedPool.total_participantes || 0} / {selectedPool.total_ganadores || 0}
                    </p>
                  </div>
                </div>

                {/* Tabla de apuestas de este partido */}
                <div>
                  <h5 className="font-bold text-text-primary text-sm mb-3">Apuestas realizadas por usuarios:</h5>
                  <div className="border border-border-subtle rounded-xl overflow-x-auto max-h-[300px] overflow-y-auto">
                    <table className="w-full text-xs text-left font-body min-w-[700px]">
                      <thead className="bg-slate-100 dark:bg-navy-dark text-text-muted uppercase tracking-wider sticky top-0">
                        <tr>
                          <th className="px-4 py-2.5">Usuario</th>
                          <th className="px-4 py-2.5">Predicción</th>
                          <th className="px-4 py-2.5 text-right">Monto</th>
                          <th className="px-4 py-2.5 text-center">Estado</th>
                          <th className="px-4 py-2.5 text-right">Premio Pagado</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border-subtle/40 bg-slate-50/50 dark:bg-navy-dark/10">
                        {selectedPoolBets.map((bet) => (
                          <tr key={bet.id} className={bet.estado === 'ganada' ? 'bg-success/5' : ''}>
                            <td className="px-4 py-2">
                              <div className="font-semibold text-text-primary flex items-center gap-1.5 flex-wrap">
                                <span>{bet.usuario_nombre} {bet.usuario_apellido}</span>
                                {Number(bet.usuario_premium) === 1 && (
                                  <span className="px-1 py-0.5 text-[8px] font-bold uppercase tracking-wider rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 inline-flex items-center gap-0.5">
                                    ★ Premium
                                  </span>
                                )}
                              </div>
                              <p className="text-[10px] text-text-muted">{bet.usuario_email}</p>
                            </td>
                            <td className="px-4 py-2 capitalize text-text-secondary">
                              <div className="flex flex-col gap-0.5 text-xs">
                                <span className="flex items-center gap-1 align-middle">
                                  <span>Sel:</span>
                                  {bet.seleccion === 'local' ? (
                                    <>
                                      <Flag code={selectedPool.equipo_local_bandera} className="w-4 h-3 object-cover rounded shadow-sm mr-0.5 align-middle" />
                                      <span className="font-medium">{selectedPool.equipo_local_nombre}</span>
                                    </>
                                  ) : bet.seleccion === 'visitante' ? (
                                    <>
                                      <Flag code={selectedPool.equipo_visitante_bandera} className="w-4 h-3 object-cover rounded shadow-sm mr-0.5 align-middle" />
                                      <span className="font-medium">{selectedPool.equipo_visitante_nombre}</span>
                                    </>
                                  ) : (
                                    <span className="font-medium">Empate</span>
                                  )}
                                </span>
                                {selectedPool.mercado !== 'clasifica' && (
                                  <span className="text-[10px] text-text-muted font-mono">Pred: {bet.goles_local_predichos} - {bet.goles_visitante_predichos}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-right font-mono font-bold text-text-primary">
                              {Number(bet.monto).toFixed(2)} Bs
                            </td>
                            <td className="px-4 py-2 text-center">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                                bet.estado === 'ganada'
                                  ? 'bg-success/10 text-success border border-success/20'
                                  : bet.estado === 'pendiente'
                                  ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                                  : bet.estado === 'devuelta'
                                  ? 'bg-gray-500/10 text-gray-400 border border-gray-500/20'
                                  : 'bg-red-500/10 text-red-400 border border-red-500/20'
                              }`}>
                                {bet.estado}
                              </span>
                            </td>
                            <td className={`px-4 py-2 text-right font-mono font-bold ${
                              Number(bet.premio_pagado || 0) > Number(bet.monto)
                                ? 'text-emerald-400'
                                : Number(bet.premio_pagado || 0) === Number(bet.monto)
                                ? 'text-text-secondary'
                                : 'text-rose-400'
                            }`}>
                              {Number(bet.premio_pagado || 0) > 0 ? (
                                <>
                                  <div>{Number(bet.premio_pagado).toFixed(2)} Bs</div>
                                  {Number(bet.premio_pagado) !== Number(bet.monto) && (
                                    <div className="text-[9px] opacity-80 font-normal">
                                      ({(Number(bet.premio_pagado) - Number(bet.monto)) >= 0 ? '+' : ''}
                                      {(Number(bet.premio_pagado) - Number(bet.monto)).toFixed(2)} Bs)
                                    </div>
                                  )}
                                </>
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                        {selectedPoolBets.length === 0 && (
                          <tr>
                            <td colSpan="5" className="px-4 py-6 text-center text-text-muted">
                              No hay apuestas registradas para este partido.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center border-t border-border-subtle pt-4 mt-2 shrink-0">
                <Button
                  onClick={() => handlePrintPool(selectedPool, selectedPoolBets)}
                  variant="outline"
                  className="flex items-center gap-1"
                  disabled={selectedPoolBets.length === 0}
                >
                  <Receipt className="w-4 h-4" />
                  Imprimir Reporte
                </Button>
                <Button
                  onClick={() => {
                    setShowPoolModal(false)
                    setSelectedPool(null)
                  }}
                  variant="primary"
                >
                  Cerrar
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal del Simulador de Pozo */}
      <AnimatePresence>
        {showSimulatorModal && selectedPool && simulationResults && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setShowSimulatorModal(false)
                setSelectedPool(null)
              }}
              className="fixed inset-0 bg-navy-dark/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-5xl w-full bg-white dark:bg-navy-dark border border-border-default rounded-2xl p-6 shadow-2xl z-10 space-y-4 max-h-[90vh] flex flex-col"
            >
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-display font-bold text-lg text-text-primary flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-cyan" />
                    Simulador de Liquidación y Reparto
                  </h3>
                  <p className="text-xs text-text-muted">
                    Simula la distribución del pozo antes de liquidar el partido de forma real.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowSimulatorModal(false)
                    setSelectedPool(null)
                  }}
                  className="p-1.5 rounded-full hover:bg-navy-light transition-colors text-text-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="overflow-y-auto space-y-4 flex-1 pr-1">
                {/* Cabecera del Partido a Simular */}
                <div className="bg-slate-50 dark:bg-navy-dark/40 border border-border-subtle p-4 rounded-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h4 className="font-bold text-text-primary text-base flex items-center gap-1.5 align-middle">
                      <Flag code={selectedPool.equipo_local_bandera} className="w-5 h-3.5 object-cover rounded shadow-sm mr-1 align-middle" />
                      <span>{selectedPool.equipo_local_nombre}</span> 
                      <span className="text-text-muted px-1">vs</span>
                      <span>{selectedPool.equipo_visitante_nombre}</span>
                      <Flag code={selectedPool.equipo_visitante_bandera} className="w-5 h-3.5 object-cover rounded shadow-sm mr-1 align-middle" />
                    </h4>
                    <p className="text-xs text-text-muted mt-1 capitalize">
                      Pozo Actual: <span className="font-semibold font-mono text-text-primary">{Number(selectedPool.monto_total || 0).toFixed(2)} Bs</span>
                    </p>
                  </div>
                  <div className="text-left md:text-right">
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-cyan/10 text-cyan border border-cyan/20">
                      Modo Simulación
                    </span>
                  </div>
                </div>

                {/* Grid de Configuración del Simulador y Resultados */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                  
                  {/* Panel de Entradas (Inputs) */}
                  <div className="lg:col-span-5 bg-slate-50 dark:bg-navy-dark/20 border border-border-subtle p-4 rounded-xl space-y-3.5">
                    <h5 className="font-bold text-text-primary text-xs uppercase tracking-wider text-cyan border-b border-border-subtle/40 pb-1.5">
                      Parámetros de la Simulación
                    </h5>
                    
                    {/* Marcador Simulado */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-text-secondary block">Resultado del Partido (Goles Simulado)</label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-text-muted block mb-0.5">{selectedPool.equipo_local_nombre}</label>
                          <input
                            type="number"
                            min="0"
                            value={simulatedGolesLocal}
                            onChange={(e) => setSimulatedGolesLocal(e.target.value)}
                            className="w-full bg-white dark:bg-navy-dark border border-border-default rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-cyan text-text-primary"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-text-muted block mb-0.5">{selectedPool.equipo_visitante_nombre}</label>
                          <input
                            type="number"
                            min="0"
                            value={simulatedGolesVisitante}
                            onChange={(e) => setSimulatedGolesVisitante(e.target.value)}
                            className="w-full bg-white dark:bg-navy-dark border border-border-default rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-cyan text-text-primary"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Ganador Clasificación Simulado (Solo en mercado clasifica) */}
                    {selectedPool.mercado === 'clasifica' && (
                      <div className="space-y-1.5 pt-1.5 border-t border-border-subtle/30">
                        <label className="text-xs font-semibold text-text-secondary block">
                          Equipo que Clasifica (Simulado)
                        </label>
                        <select
                          value={simulatedClasificaWinner}
                          onChange={(e) => setSimulatedClasificaWinner(e.target.value)}
                          className="w-full bg-white dark:bg-navy-dark border border-border-default rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-cyan text-text-primary cursor-pointer"
                        >
                          <option value="local">Local: {selectedPool.equipo_local_nombre}</option>
                          <option value="visitante">Visitante: {selectedPool.equipo_visitante_nombre}</option>
                        </select>
                      </div>
                    )}

                    {/* Multiplicador y Dividendo Minimo */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs font-semibold text-text-secondary block mb-1">Mult. Goles Exactos</label>
                        <input
                          type="number"
                          step="0.5"
                          min="1"
                          max="10"
                          value={simulatedBono}
                          onChange={(e) => setSimulatedBono(e.target.value)}
                          className="w-full bg-white dark:bg-navy-dark border border-border-default rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-cyan text-text-primary"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-text-secondary block mb-1">Dividendo Mínimo</label>
                        <input
                          type="number"
                          step="0.05"
                          min="1.00"
                          max="2.00"
                          value={simulatedMinDiv}
                          onChange={(e) => setSimulatedMinDiv(e.target.value)}
                          className="w-full bg-white dark:bg-navy-dark border border-border-default rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-cyan text-text-primary"
                        />
                      </div>
                    </div>

                    {/* Comisión de la Casa */}
                    <div className="space-y-1.5 pt-1.5 border-t border-border-subtle/30">
                      <label className="text-xs font-semibold text-text-secondary block">
                        Porcentaje de comisión cobrado por la casa en cada pozo de apuestas (0-30%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="30"
                        value={simulatedComision}
                        onChange={(e) => setSimulatedComision(e.target.value)}
                        className="w-full bg-white dark:bg-navy-dark border border-border-default rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-cyan text-text-primary font-mono"
                      />
                    </div>

                    {/* Retención Especial */}
                    <div className="space-y-1.5 pt-1.5 border-t border-border-subtle/30">
                      <label className="text-xs font-semibold text-text-secondary block">Falta de Ganadores (Reembolso)</label>
                      <select
                        value={simulatedRetencionSinGanadores ? '1' : '0'}
                        onChange={(e) => setSimulatedRetencionSinGanadores(e.target.value === '1')}
                        className="w-full bg-white dark:bg-navy-dark border border-border-default rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-cyan text-text-primary cursor-pointer"
                      >
                        <option value="0">Devolver 100% sin cobrar comisión 🔄</option>
                        <option value="1">Retener un porcentaje para la casa 🏦</option>
                      </select>
                    </div>

                    {simulatedRetencionSinGanadores && (
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-text-secondary block">Porcentaje de Retención (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={simulatedPorcentajeRetencion}
                          onChange={(e) => setSimulatedPorcentajeRetencion(e.target.value)}
                          className="w-full bg-white dark:bg-navy-dark border border-border-default rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-cyan text-text-primary"
                        />
                      </div>
                    )}
                  </div>

                  {/* Resumen Financiero Simulado */}
                  <div className="lg:col-span-7 bg-slate-50 dark:bg-navy-dark/20 border border-border-subtle p-4 rounded-xl space-y-3.5">
                    <h5 className="font-bold text-text-primary text-xs uppercase tracking-wider text-cyan border-b border-border-subtle/40 pb-1.5">
                      Resumen Financiero Simulado
                    </h5>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div className="bg-white dark:bg-navy-dark p-2.5 rounded-lg border border-border-subtle/50">
                        <span className="text-[10px] text-text-muted block">Recaudación Total</span>
                        <span className="font-mono text-sm font-bold text-text-primary">
                          {simulationResults.totalCollected.toFixed(2)} Bs
                        </span>
                      </div>
                      <div className="bg-white dark:bg-navy-dark p-2.5 rounded-lg border border-border-subtle/50">
                        <span className="text-[10px] text-text-muted block">Comisión Casa %</span>
                        <span className={`font-mono text-sm font-bold ${simulationResults.appliedCommission < 10 ? 'text-amber-400 font-semibold' : 'text-text-primary'}`}>
                          {simulationResults.appliedCommission.toFixed(2)}%
                        </span>
                      </div>
                      <div className="bg-white dark:bg-navy-dark p-2.5 rounded-lg border border-border-subtle/50">
                        <span className="text-[10px] text-text-muted block">Monto Comisión</span>
                        <span className="font-mono text-sm font-bold text-text-primary">
                          {(simulationResults.totalCollected - simulationResults.netPool).toFixed(2)} Bs
                        </span>
                      </div>
                      <div className="bg-white dark:bg-navy-dark p-2.5 rounded-lg border border-border-subtle/50">
                        <span className="text-[10px] text-text-muted block">Pozo Neto a Repartir</span>
                        <span className="font-mono text-sm font-bold text-emerald-400">
                          {simulationResults.netPool.toFixed(2)} Bs
                        </span>
                      </div>
                      <div className="bg-white dark:bg-navy-dark p-2.5 rounded-lg border border-border-subtle/50">
                        <span className="text-[10px] text-text-muted block">Ganadores</span>
                        <span className="font-mono text-sm font-bold text-cyan">
                          {simulationResults.totalWinners} de {simulationResults.finalBets.length}
                        </span>
                      </div>
                      <div className="bg-white dark:bg-navy-dark p-2.5 rounded-lg border border-border-subtle/50">
                        <span className="text-[10px] text-text-muted block">Div. por Participación</span>
                        <span className="font-mono text-sm font-bold text-text-primary">
                          {simulationResults.valuePerParticipation > 0 ? simulationResults.valuePerParticipation.toFixed(4) : '0.0000'}
                        </span>
                      </div>
                    </div>

                    {simulationResults.subsidyVal > 0 && (
                      <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-lg text-xs space-y-1">
                        <div className="font-bold flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                          ¡DÉFICIT DETECTADO Y SUBSIDIADO POR LA PLATAFORMA!
                        </div>
                        <p className="opacity-90">
                          La comisión de la casa bajó a 0.00%. Aún así, para garantizar el Dividendo Mínimo de <b>{simulatedMinDiv}x</b>, la plataforma debe subsidiar <b>{simulationResults.subsidyVal.toFixed(2)} Bs</b>.
                        </p>
                      </div>
                    )}
                    {simulationResults.totalWinners > 0 && simulationResults.subsidyVal === 0 && simulationResults.appliedCommission < 10 && (
                      <div className="bg-amber-500/10 border border-amber-500/20 text-amber-400 p-3 rounded-lg text-xs space-y-1">
                        <div className="font-bold flex items-center gap-1">
                          <Info className="w-3.5 h-3.5 shrink-0" />
                          Reducción de Comisión Aplicada
                        </div>
                        <p className="opacity-90">
                          La comisión de la casa se redujo de 10% a <b>{simulationResults.appliedCommission.toFixed(2)}%</b> para poder pagar el dividendo mínimo garantizado de {simulatedMinDiv}x sin necesidad de subsidio.
                        </p>
                      </div>
                    )}
                    {simulationResults.totalWinners === 0 && (
                      <div className={simulatedRetencionSinGanadores ? "bg-amber-500/10 border border-amber-500/20 text-amber-400 p-3 rounded-lg text-xs space-y-1" : "bg-cyan/10 border border-cyan/20 text-cyan p-3 rounded-lg text-xs space-y-1"}>
                        <div className="font-bold flex items-center gap-1">
                          <Info className="w-3.5 h-3.5 shrink-0" />
                          {simulatedRetencionSinGanadores ? "FALTA DE GANADORES: RETENCIÓN DE POZO" : "FALTA DE GANADORES: REEMBOLSO COMPLETO"}
                        </div>
                        <p className="opacity-90">
                          {simulatedRetencionSinGanadores 
                            ? `Ningún usuario acertó el ganador. Se retendrá el ${simulatedPorcentajeRetencion}% (${(simulationResults.totalCollected * (Number(simulatedPorcentajeRetencion) / 100)).toFixed(2)} Bs) para la plataforma/acumulado. Se reembolsará el ${100 - Number(simulatedPorcentajeRetencion)}% (${simulationResults.netPool.toFixed(2)} Bs) a los usuarios.` 
                            : `Ningún usuario acertó el ganador. Se reembolsará el 100% (${simulationResults.totalCollected.toFixed(2)} Bs) de las apuestas a todos los usuarios.`
                          }
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tabla de Resultados de Apuestas Simulados */}
                <div>
                  <h5 className="font-bold text-text-primary text-xs uppercase tracking-wider text-text-secondary mb-2 flex items-center gap-1">
                    <span>Detalle de Apuestas con Reparto Simulado</span>
                    <span className="text-[10px] text-text-muted font-normal lowercase">(los premios se calculan en base a las participaciones del usuario)</span>
                  </h5>
                  <div className="border border-border-subtle rounded-xl overflow-x-auto max-h-[250px] overflow-y-auto">
                    <table className="w-full text-xs text-left font-body min-w-[850px]">
                      <thead className="bg-slate-100 dark:bg-navy-dark text-text-muted uppercase tracking-wider sticky top-0">
                        <tr>
                          <th className="px-4 py-2">Usuario</th>
                          <th className="px-4 py-2">Predicción</th>
                          <th className="px-4 py-2 text-right">Monto</th>
                          <th className="px-4 py-2 text-center">Sim. Resultado</th>
                          <th className="px-4 py-2 text-right">Participaciones</th>
                          <th className="px-4 py-2 text-right text-cyan">Premio Simulado</th>
                          <th className="px-4 py-2 text-right text-emerald-400">Rendimiento (ROI)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border-subtle/40 bg-slate-50/50 dark:bg-navy-dark/10">
                        {simulationResults.finalBets.map((bet) => {
                          const roi = Number(bet.monto) > 0 ? bet.simulatedPayout / Number(bet.monto) : 0
                          const rowBg = bet.isRefund
                            ? 'bg-amber-500/5'
                            : bet.isWinner
                            ? 'bg-success/5 animate-pulse-slow'
                            : ''
                          return (
                            <tr key={bet.id} className={rowBg}>
                              <td className="px-4 py-2 font-medium text-text-primary">
                                {bet.usuario_nombre} {bet.usuario_apellido}
                              </td>
                              <td className="px-4 py-2 text-text-secondary">
                                <span className="capitalize">{bet.seleccion}</span> (Pred: {bet.goles_local_predichos}-{bet.goles_visitante_predichos})
                              </td>
                              <td className="px-4 py-2 text-right font-mono">{Number(bet.monto).toFixed(2)} Bs</td>
                              <td className="px-4 py-2 text-center">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                                  bet.isRefund
                                    ? (bet.refundCoef === 0
                                      ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                                      : 'bg-amber-500/10 text-amber-400 border border-amber-500/20')
                                    : bet.isWinner
                                    ? 'bg-success/10 text-success border border-success/20'
                                    : 'bg-red-500/10 text-red-400 border border-red-500/20'
                                }`}>
                                  {bet.isRefund
                                    ? (bet.refundCoef === 0
                                      ? 'SIN REEMBOLSO'
                                      : bet.refundCoef < 1
                                      ? `DEV. PARCIAL (${Math.round(bet.refundCoef * 100)}%)`
                                      : 'REEMBOLSO 100%')
                                    : bet.isWinner
                                    ? 'GANADOR'
                                    : 'PERDEDOR'}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-right font-mono">
                                {bet.participaciones > 0 ? (
                                  <>
                                    <span>{bet.participaciones.toFixed(2)}</span>
                                    {bet.isExact && (
                                      <span className="ml-1 text-[9px] font-bold text-cyan" title="Marcador exacto acertado (Bono aplicado)">
                                        (x{simulatedBono})
                                      </span>
                                    )}
                                  </>
                                ) : '0.00'}
                              </td>
                              <td className={`px-4 py-2 text-right font-mono font-bold text-cyan`}>
                                {bet.isRefund || bet.simulatedPayout > 0 ? `${bet.simulatedPayout.toFixed(2)} Bs` : '-'}
                              </td>
                              <td className={`px-4 py-2 text-right font-mono font-bold ${
                                roi >= 1.05 ? 'text-emerald-400' : roi >= 1.00 ? 'text-amber-400' : bet.isRefund ? 'text-red-400' : roi > 0 ? 'text-red-400' : 'text-text-muted'
                              }`}>
                                {bet.isRefund || roi > 0 ? `${roi.toFixed(2)}x` : '-'}
                              </td>
                            </tr>
                          )
                        })}
                        {simulationResults.finalBets.length === 0 && (
                          <tr>
                            <td colSpan="7" className="px-4 py-6 text-center text-text-muted">
                              No hay apuestas registradas para este partido.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center border-t border-border-subtle pt-4 mt-2 shrink-0">
                <p className="text-[10px] text-text-muted">
                  *Esta simulación es informativa y no afecta a las transacciones de saldo de los usuarios.
                </p>
                <Button
                  onClick={() => {
                    setShowSimulatorModal(false)
                    setSelectedPool(null)
                  }}
                  variant="primary"
                >
                  Cerrar Simulador
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  )
}

// ==========================================
// SECCIÓN: RETIROS (APROBACIONES Y RECHAZOS)
// ==========================================
function RetirosSection() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [selectedQr, setSelectedQr] = useState(null)
  const [rejectingId, setRejectingId] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [approvingId, setApprovingId] = useState(null)
  const [paymentRef, setPaymentRef] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const { data: withdrawalsData, isLoading } = useQuery({
    queryKey: ['admin-withdrawals'],
    queryFn: async () => {
      const response = await client.get('/admin/withdrawals')
      return response.data?.datos || []
    }
  })
  const withdrawals = withdrawalsData || []

  const {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    searchTerm,
    setSearchTerm,
    sortField,
    sortOrder,
    requestSort,
    paginatedData: paginatedWithdrawals,
    totalPages,
    totalRecords,
    startIndex,
    endIndex
  } = useDataTable({
    data: withdrawals,
    searchFields: ['nombre', 'apellido', 'email', 'referencia_pago', 'estado'],
    defaultSortField: 'creado_en',
    defaultSortOrder: 'desc',
    customFilter: (req) => statusFilter === 'all' || req.estado === statusFilter
  })

  const processMutation = useMutation({
    mutationFn: async ({ id, estado, referencia_pago, motivo_rechazo }) => {
      const response = await client.put(`/admin/withdrawals/${id}`, {
        estado,
        referencia_pago,
        motivo_rechazo
      })
      return response.data
    },
    onSuccess: (_, variables) => {
      toast.success(`Solicitud de retiro marcada como '${variables.estado}' con éxito.`)
      setRejectingId(null)
      setRejectReason('')
      setApprovingId(null)
      setPaymentRef('')
      queryClient.invalidateQueries({ queryKey: ['admin-withdrawals'] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.mensaje || 'Error al procesar la solicitud de retiro.')
    }
  })

  if (isLoading) return <Skeleton className="h-64 w-full rounded-2xl" />

  const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
    ? import.meta.env.VITE_API_URL.replace('/api', '')
    : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')

  return (
    <div className="space-y-6 animate-fade-in">
      <h2 className="font-display font-bold text-xl text-text-primary">
        Aprobación de Retiros de Clientes
      </h2>

      <Card className="p-4">
        <DataTableControls
          pageSize={pageSize}
          setPageSize={setPageSize}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Buscar por usuario, email o referencia..."
        >
          <div className="w-full sm:w-64 flex gap-2 shrink-0">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="w-full bg-surface-card border border-border-default rounded-xl p-1.5 text-xs focus:border-cyan focus:outline-none text-text-secondary cursor-pointer"
            >
              <option value="all">Todos los estados</option>
              <option value="pendiente">Pendientes ⏳</option>
              <option value="aprobado">Aprobados ✅</option>
              <option value="rechazado">Rechazados ❌</option>
            </select>
          </div>
        </DataTableControls>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left font-body">
            <thead className="bg-navy-dark/40 text-text-muted text-xs uppercase tracking-wider border-b border-border-subtle">
              <tr>
                <SortableHeader field="nombre" currentField={sortField} sortOrder={sortOrder} label="Usuario" onSort={requestSort} className="px-6 py-4" />
                <SortableHeader field="monto" currentField={sortField} sortOrder={sortOrder} label="Monto" onSort={requestSort} className="px-6 py-4" />
                <th className="px-6 py-4 text-text-muted">QR Cuenta</th>
                <SortableHeader field="creado_en" currentField={sortField} sortOrder={sortOrder} label="Fecha" onSort={requestSort} className="px-6 py-4" />
                <SortableHeader field="estado" currentField={sortField} sortOrder={sortOrder} label="Estado" onSort={requestSort} className="px-6 py-4 text-center" />
                <th className="px-6 py-4 text-right text-text-muted">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {paginatedWithdrawals.map((req) => (
                <tr key={req.id} className="hover:bg-navy-dark/10 transition-colors">
                  <td className="px-6 py-4">
                    <p className="font-semibold text-text-primary">{req.nombre} {req.apellido}</p>
                    <p className="text-xs text-text-muted">{req.email}</p>
                  </td>
                  <td className="px-6 py-4 font-mono font-bold text-cyan">
                    {Number(req.monto).toFixed(2)} Bs
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => setSelectedQr(`${baseImgUrl}${req.qr_ruta}`)}
                      className="text-xs text-cyan hover:underline font-semibold flex items-center gap-1"
                    >
                      Ver QR de cuenta
                    </button>
                  </td>
                  <td className="px-6 py-4 text-text-secondary text-xs">
                    {new Date(req.creado_en).toLocaleString('es-ES')}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {req.estado === 'pendiente' && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-500/10 text-orange-400 border border-orange-500/20">
                        Pendiente
                      </span>
                    )}
                    {req.estado === 'aprobado' && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-success/10 text-success border border-success/20">
                        Aprobado
                      </span>
                    )}
                    {req.estado === 'rechazado' && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-error/10 text-error border border-error/20">
                        Rechazado
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {req.estado === 'pendiente' ? (
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="xs"
                          variant="success"
                          disabled={processMutation.isPending}
                          onClick={() => setApprovingId(req.id)}
                        >
                          Aprobar
                        </Button>
                        <Button
                          size="xs"
                          variant="danger"
                          disabled={processMutation.isPending}
                          onClick={() => setRejectingId(req.id)}
                        >
                          Rechazar
                        </Button>
                      </div>
                    ) : (
                      <div className="text-xs text-text-muted text-right space-y-0.5">
                        <span className="font-semibold">Procesada</span>
                        {req.estado === 'aprobado' && req.referencia_pago && (
                          <p className="text-[10px]">Ref: <span className="font-mono text-text-secondary">{req.referencia_pago}</span></p>
                        )}
                        {req.estado === 'rechazado' && req.motivo_rechazo && (
                          <p className="text-[10px] text-error font-medium">Motivo: <span>{req.motivo_rechazo}</span></p>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {totalRecords === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-text-muted">
                    No se registran solicitudes que coincidan con los filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <DataTablePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          startIndex={startIndex}
          endIndex={endIndex}
          totalRecords={totalRecords}
        />
      </Card>

      {/* Modal para ver el QR */}
      <AnimatePresence>
        {selectedQr && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedQr(null)}
              className="fixed inset-0 bg-navy-dark/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-md w-full bg-white dark:bg-navy-dark border border-border-default rounded-2xl p-6 shadow-2xl z-10 space-y-4"
            >
              <div className="flex justify-between items-center">
                <h3 className="font-display font-bold text-lg text-text-primary">Código QR de la Cuenta</h3>
                <button
                  onClick={() => setSelectedQr(null)}
                  className="p-1.5 rounded-full hover:bg-navy-light transition-colors text-text-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex justify-center bg-white p-4 rounded-xl border border-border-subtle">
                <img src={selectedQr} alt="QR Cuenta Bancaria" className="max-h-96 object-contain rounded-lg" />
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setSelectedQr(null)} variant="outline">
                  Cerrar
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal para ingresar Referencia de Pago (Aprobar) */}
      <AnimatePresence>
        {approvingId !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setApprovingId(null)}
              className="fixed inset-0 bg-navy-dark/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-sm w-full bg-white dark:bg-navy-dark border border-border-default rounded-2xl p-6 shadow-2xl z-10 space-y-4"
            >
              <h3 className="font-display font-bold text-lg text-text-primary">Aprobar Retiro</h3>
              <p className="text-sm text-text-secondary">
                Por favor, realiza la transferencia al QR del cliente e ingresa el código de referencia bancaria para guardar el comprobante (opcional).
              </p>
              <div className="space-y-2">
                <label className="text-xs font-semibold block text-text-muted">Referencia de Pago</label>
                <input
                  type="text"
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                  placeholder="Ej: TR-8930412"
                  className="w-full bg-navy-dark/50 border border-border-subtle rounded-xl px-4 py-2.5 text-text-primary focus:outline-none focus:border-cyan transition-colors"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  className="flex-1"
                  variant="outline"
                  onClick={() => {
                    setApprovingId(null)
                    setPaymentRef('')
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1"
                  variant="success"
                  loading={processMutation.isPending}
                  onClick={() =>
                    processMutation.mutate({
                      id: approvingId,
                      estado: 'aprobado',
                      referencia_pago: paymentRef
                    })
                  }
                >
                  Confirmar Aprobación
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal para ingresar Motivo de Rechazo (Rechazar) */}
      <AnimatePresence>
        {rejectingId !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setRejectingId(null)}
              className="fixed inset-0 bg-navy-dark/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-sm w-full bg-white dark:bg-navy-dark border border-border-default rounded-2xl p-6 shadow-2xl z-10 space-y-4"
            >
              <h3 className="font-display font-bold text-lg text-text-primary">Rechazar Retiro</h3>
              <p className="text-sm text-text-secondary">
                Especifica la razón por la cual se rechaza el retiro. Esto reembolsará automáticamente los créditos al balance del cliente y se le notificará el motivo.
              </p>
              <div className="space-y-2">
                <label className="text-xs font-semibold block text-text-muted">Motivo de Rechazo</label>
                <input
                  type="text"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Ej: QR incorrecto o expirado"
                  className="w-full bg-navy-dark/50 border border-border-subtle rounded-xl px-4 py-2.5 text-text-primary focus:outline-none focus:border-cyan transition-colors"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  className="flex-1"
                  variant="outline"
                  onClick={() => {
                    setRejectingId(null)
                    setRejectReason('')
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1"
                  variant="danger"
                  loading={processMutation.isPending}
                  onClick={() =>
                    processMutation.mutate({
                      id: rejectingId,
                      estado: 'rechazado',
                      motivo_rechazo: rejectReason
                    })
                  }
                >
                  Confirmar Rechazo
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ==========================================
// SECCIÓN: PUBLICIDAD (CRUD)
// ==========================================
function PublicidadSection() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [showModal, setShowModal] = useState(false)
  const [editingAd, setEditingAd] = useState(null)
  
  const [form, setForm] = useState({
    titulo: '',
    enlace_url: '',
    whatsapp: '',
    orden: '0',
    estado: 'activo',
    mostrar_landing: true,
    mostrar_internas: true,
    mostrar_footer: true,
    duracion_video: ''
  })
  const [imagenFile, setImagenFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)

  const isCurrentAdVideo = 
    (imagenFile && (imagenFile.type.startsWith('video/') || ['mp4', 'webm'].includes(imagenFile.name.split('.').pop().toLowerCase()))) ||
    (!imagenFile && editingAd && (editingAd.imagen_ruta?.toLowerCase().endsWith('.mp4') || editingAd.imagen_ruta?.toLowerCase().endsWith('.webm')));

  // Consultar anuncios
  const { data: adsData, isLoading } = useQuery({
    queryKey: ['admin-ads'],
    queryFn: async () => {
      const response = await client.get('/admin/ads')
      return response.data?.datos || []
    }
  })
  const ads = adsData || []

  const {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    searchTerm,
    setSearchTerm,
    sortField,
    sortOrder,
    requestSort,
    paginatedData: paginatedAds,
    totalPages,
    totalRecords,
    startIndex,
    endIndex
  } = useDataTable({
    data: ads,
    searchFields: ['titulo', 'enlace_url', 'whatsapp', 'estado'],
    defaultSortField: 'orden',
    defaultSortOrder: 'asc'
  })

  const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
    ? import.meta.env.VITE_API_URL.replace('/api', '')
    : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')

  // Mutación para Guardar (Crear/Editar)
  const saveMutation = useMutation({
    mutationFn: async (formData) => {
      if (editingAd) {
        return await client.post(`/admin/ads/${editingAd.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      } else {
        return await client.post('/admin/ads', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      }
    },
    onSuccess: (res) => {
      toast.success(editingAd ? 'Banner publicitario actualizado con éxito' : 'Banner publicitario creado con éxito')
      queryClient.invalidateQueries({ queryKey: ['admin-ads'] })
      queryClient.invalidateQueries({ queryKey: ['ads'] })
      setShowModal(false)
      resetForm()
    },
    onError: (err) => {
      toast.error(err.response?.data?.mensaje || 'Error al guardar el banner publicitario')
    }
  })

  // Mutación para Eliminar
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return await client.delete(`/admin/ads/${id}`)
    },
    onSuccess: () => {
      toast.success('Banner publicitario eliminado correctamente')
      queryClient.invalidateQueries({ queryKey: ['admin-ads'] })
      queryClient.invalidateQueries({ queryKey: ['ads'] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.mensaje || 'Error al eliminar el banner')
    }
  })

  const resetForm = () => {
    setForm({
      titulo: '',
      enlace_url: '',
      whatsapp: '',
      orden: '0',
      estado: 'activo',
      mostrar_landing: true,
      mostrar_internas: true,
      mostrar_footer: true,
      duracion_video: ''
    })
    setImagenFile(null)
    setImagePreview(null)
    setEditingAd(null)
  }

  const handleOpenCreate = () => {
    resetForm()
    setShowModal(true)
  }

  const handleOpenEdit = (ad) => {
    setEditingAd(ad)
    setForm({
      titulo: ad.titulo,
      enlace_url: ad.enlace_url || '',
      whatsapp: ad.whatsapp || '',
      orden: String(ad.orden || 0),
      estado: ad.estado || 'activo',
      mostrar_landing: ad.mostrar_landing === 1 || ad.mostrar_landing === '1' || ad.mostrar_landing === true,
      mostrar_internas: ad.mostrar_internas === 1 || ad.mostrar_internas === '1' || ad.mostrar_internas === true,
      mostrar_footer: ad.mostrar_footer === 1 || ad.mostrar_footer === '1' || ad.mostrar_footer === true,
      duracion_video: String(ad.duracion_video || '')
    })
    setImagenFile(null)
    setImagePreview(`${baseImgUrl}${ad.imagen_ruta}`)
    setShowModal(true)
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase()
      const isVideo = ext === 'mp4' || ext === 'webm' || file.type.startsWith('video/')
      const isAllowed = ['jpg', 'jpeg', 'png', 'webp', 'svg', 'mp4', 'webm'].includes(ext) || file.type.startsWith('image/') || file.type.startsWith('video/')

      if (!isAllowed) {
        toast.error('Formato de archivo no permitido. Use JPG, PNG, WEBP, SVG, MP4 o WEBM.')
        e.target.value = ''
        return
      }

      const maxSize = isVideo ? 20 * 1024 * 1024 : 5 * 1024 * 1024
      const maxSizeText = isVideo ? '20MB' : '5MB'

      if (file.size > maxSize) {
        toast.error(`El archivo supera el tamaño máximo permitido de ${maxSizeText}`)
        e.target.value = ''
        return
      }

      setImagenFile(file)

      if (isVideo) {
        const video = document.createElement('video')
        video.preload = 'metadata'
        video.onloadedmetadata = () => {
          window.URL.revokeObjectURL(video.src)
          const duration = Math.round(video.duration)
          setForm(prev => ({ ...prev, duracion_video: String(duration) }))
          toast.success(`Duración del video detectada: ${duration} segundos`)
        }
        video.src = URL.createObjectURL(file)
      } else {
        setForm(prev => ({ ...prev, duracion_video: '' }))
      }

      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!form.titulo.trim()) {
      toast.error('El campo Título es obligatorio')
      return
    }

    if (!editingAd && !imagenFile) {
      toast.error('Debe seleccionar una imagen para el banner')
      return
    }

    const formData = new FormData()
    formData.append('titulo', form.titulo.trim())
    formData.append('enlace_url', form.enlace_url.trim())
    formData.append('whatsapp', form.whatsapp.trim())
    formData.append('orden', form.orden)
    formData.append('estado', form.estado)
    formData.append('mostrar_landing', form.mostrar_landing ? '1' : '0')
    formData.append('mostrar_internas', form.mostrar_internas ? '1' : '0')
    formData.append('mostrar_footer', form.mostrar_footer ? '1' : '0')
    formData.append('duracion_video', form.duracion_video)
    if (imagenFile) {
      formData.append('imagen', imagenFile)
    }

    saveMutation.mutate(formData)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="font-display font-bold text-xl text-text-primary flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-cyan" /> Gestión de Publicidades y Auspiciadores
          </h2>
          <p className="text-text-secondary text-sm">
            Crea y administra banners publicitarios de anunciantes para el footer y la landing page.
          </p>
        </div>
        <Button onClick={handleOpenCreate} className="flex items-center gap-2 shrink-0">
          <Plus className="w-4 h-4" /> Crear Anuncio
        </Button>
      </div>

      <Card className="p-4">
        <DataTableControls
          pageSize={pageSize}
          setPageSize={setPageSize}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Buscar banners..."
        />

        {isLoading ? (
          <div className="p-8 space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : totalRecords === 0 ? (
          <div className="p-8 text-center text-text-muted">
            <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No se registran banners publicitarios.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-navy-dark/40 border-b border-border-subtle text-text-muted">
                  <th className="px-6 py-4 font-semibold text-text-muted uppercase text-xs">Banner</th>
                  <SortableHeader field="titulo" currentField={sortField} sortOrder={sortOrder} label="Título / Auspiciador" onSort={requestSort} className="px-6 py-4 font-semibold text-text-muted uppercase text-xs" />
                  <SortableHeader field="enlace_url" currentField={sortField} sortOrder={sortOrder} label="Enlace / WhatsApp" onSort={requestSort} className="px-6 py-4 font-semibold text-text-muted uppercase text-xs" />
                  <SortableHeader field="orden" currentField={sortField} sortOrder={sortOrder} label="Orden" onSort={requestSort} className="px-6 py-4 font-semibold text-center text-text-muted uppercase text-xs" />
                  <SortableHeader field="estado" currentField={sortField} sortOrder={sortOrder} label="Estado" onSort={requestSort} className="px-6 py-4 font-semibold text-text-muted uppercase text-xs" />
                  <th className="px-6 py-4 font-semibold text-text-muted uppercase text-xs">Ubicaciones</th>
                  <SortableHeader field="clics" currentField={sortField} sortOrder={sortOrder} label="Clicks" onSort={requestSort} className="px-6 py-4 font-semibold text-center text-text-muted uppercase text-xs" />
                  <th className="px-6 py-4 font-semibold text-right text-text-muted uppercase text-xs">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle bg-surface-card/10">
                {paginatedAds.map((ad) => (
                  <tr key={ad.id} className="hover:bg-surface-card/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="w-24 h-12 rounded-lg bg-navy-dark border border-border-subtle overflow-hidden flex items-center justify-center p-1">
                        {ad.imagen_ruta?.toLowerCase().endsWith('.mp4') || ad.imagen_ruta?.toLowerCase().endsWith('.webm') ? (
                          <video 
                            src={`${baseImgUrl}${ad.imagen_ruta}`} 
                            className="max-h-full max-w-full object-contain rounded" 
                            muted 
                            playsInline 
                          />
                        ) : (
                          <img 
                            src={`${baseImgUrl}${ad.imagen_ruta}`} 
                            alt={ad.titulo} 
                            className="max-h-full max-w-full object-contain"
                            onError={(e) => { e.target.style.display = 'none' }}
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 font-semibold text-text-primary">
                      {ad.titulo}
                    </td>
                    <td className="px-6 py-4 text-xs text-text-secondary max-w-[180px] space-y-1">
                      {ad.enlace_url && (
                        <a 
                          href={ad.enlace_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 hover:text-cyan truncate"
                        >
                          URL: {ad.enlace_url} <ExternalLink className="w-3 h-3 shrink-0" />
                        </a>
                      )}
                      {ad.whatsapp && (
                        <p className="text-text-muted truncate">
                          WA: <strong className="text-text-primary">{ad.whatsapp}</strong>
                        </p>
                      )}
                      {!ad.enlace_url && !ad.whatsapp && (
                        <span className="text-text-muted font-normal italic">Ninguno</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center font-mono font-bold">
                      {ad.orden}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                        ad.estado === 'activo'
                          ? 'bg-success/10 text-success border-success/20'
                          : 'bg-text-muted/10 text-text-muted border-border-default'
                      }`}>
                        {ad.estado === 'activo' ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1.5">
                        {ad.mostrar_landing === 1 || ad.mostrar_landing === '1' || ad.mostrar_landing === true ? (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-cyan/10 text-cyan border border-cyan/20">Principal</span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-navy-dark text-text-muted/40 border border-border-subtle/30 line-through">Principal</span>
                        )}
                        {ad.mostrar_internas === 1 || ad.mostrar_internas === '1' || ad.mostrar_internas === true ? (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-success/10 text-success border border-success/20">Internas</span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-navy-dark text-text-muted/40 border border-border-subtle/30 line-through">Internas</span>
                        )}
                        {ad.mostrar_footer === 1 || ad.mostrar_footer === '1' || ad.mostrar_footer === true ? (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-warning/10 text-warning border border-warning/20">Footer</span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-navy-dark text-text-muted/40 border border-border-subtle/30 line-through">Footer</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center font-mono font-bold text-cyan">
                      {ad.clics}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-3 text-text-secondary">
                        <button 
                          onClick={() => handleOpenEdit(ad)} 
                          className="p-1.5 hover:text-cyan hover:bg-navy-light rounded-lg transition-colors border border-transparent hover:border-border-subtle" 
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => window.confirm(`¿Seguro que deseas eliminar el anuncio de "${ad.titulo}"?`) && deleteMutation.mutate(ad.id)} 
                          className="p-1.5 hover:text-error hover:bg-navy-light rounded-lg transition-colors border border-transparent hover:border-border-subtle" 
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DataTablePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          startIndex={startIndex}
          endIndex={endIndex}
          totalRecords={totalRecords}
        />
      </Card>

      {/* Modal Crear/Editar */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 bg-navy-dark/80 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} 
              animate={{ opacity: 1, scale: 1 }} 
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white dark:bg-navy-dark max-w-lg w-full rounded-2xl overflow-hidden shadow-2xl border border-border-default"
            >
              <div className="p-5 border-b border-border-subtle flex justify-between items-center bg-slate-50 dark:bg-navy-dark/40">
                <div className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-cyan" />
                  <h3 className="font-display font-bold text-lg text-text-primary">
                    {editingAd ? 'Editar Anuncio' : 'Crear Anuncio'}
                  </h3>
                </div>
                <button 
                  onClick={() => setShowModal(false)} 
                  className="p-1.5 rounded-full hover:bg-navy-light transition-colors text-text-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-text-secondary block">
                    Nombre o Título del Auspiciador *
                  </label>
                  <input 
                    type="text"
                    required
                    value={form.titulo}
                    onChange={e => setForm(prev => ({ ...prev, titulo: e.target.value }))}
                    placeholder="Ej. Apex Sports"
                    className="w-full bg-navy-dark/30 border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-text-secondary block">
                    Enlace de Destino (URL Opcional)
                  </label>
                  <input 
                    type="url"
                    value={form.enlace_url}
                    onChange={e => setForm(prev => ({ ...prev, enlace_url: e.target.value }))}
                    placeholder="Ej. https://www.fifa.com"
                    className="w-full bg-navy-dark/30 border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-text-secondary block">
                    Número de WhatsApp (Opcional)
                  </label>
                  <input 
                    type="text"
                    value={form.whatsapp}
                    onChange={e => setForm(prev => ({ ...prev, whatsapp: e.target.value }))}
                    placeholder="Ej. +59170000000 o 70000000"
                    className="w-full bg-navy-dark/30 border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-text-secondary block">
                      Prioridad / Orden
                    </label>
                    <input 
                      type="number"
                      min="0"
                      required
                      value={form.orden}
                      onChange={e => setForm(prev => ({ ...prev, orden: e.target.value }))}
                      className="w-full bg-navy-dark/30 border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-text-secondary block">
                      Estado del Banner
                    </label>
                    <select
                      value={form.estado}
                      onChange={e => setForm(prev => ({ ...prev, estado: e.target.value }))}
                      className="w-full bg-white dark:bg-navy-dark border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors"
                    >
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                    </select>
                  </div>
                </div>

                <AnimatePresence>
                  {isCurrentAdVideo && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-1 overflow-hidden"
                    >
                      <label className="text-xs font-semibold text-text-secondary block">
                        Duración de Reproducción del Video (segundos)
                      </label>
                      <input 
                        type="number"
                        min="1"
                        value={form.duracion_video}
                        onChange={e => setForm(prev => ({ ...prev, duracion_video: e.target.value }))}
                        placeholder="Ej. 15 (Dejar vacío o 0 para reproducir completo)"
                        className="w-full bg-navy-dark/30 border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors"
                      />
                      <span className="text-[10px] text-text-muted block mt-0.5">
                        * Opcional. Si se deja vacío o 0, el carrusel esperará automáticamente a que el video termine de reproducirse por completo.
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="space-y-2">
                  <label className="text-xs font-semibold text-text-secondary block">
                    Ubicaciones de visualización
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <label className="flex items-center gap-2 p-2.5 rounded-xl border border-border-subtle bg-navy-dark/10 cursor-pointer hover:bg-navy-light/10 transition-colors select-none text-xs text-text-primary">
                      <input 
                        type="checkbox"
                        checked={form.mostrar_landing}
                        onChange={e => setForm(prev => ({ ...prev, mostrar_landing: e.target.checked }))}
                        className="w-4 h-4 rounded border-border-subtle text-cyan focus:ring-0 focus:ring-offset-0 bg-transparent cursor-pointer"
                      />
                      Pág. Principal
                    </label>
                    <label className="flex items-center gap-2 p-2.5 rounded-xl border border-border-subtle bg-navy-dark/10 cursor-pointer hover:bg-navy-light/10 transition-colors select-none text-xs text-text-primary">
                      <input 
                        type="checkbox"
                        checked={form.mostrar_internas}
                        onChange={e => setForm(prev => ({ ...prev, mostrar_internas: e.target.checked }))}
                        className="w-4 h-4 rounded border-border-subtle text-cyan focus:ring-0 focus:ring-offset-0 bg-transparent cursor-pointer"
                      />
                      Pág. Internas
                    </label>
                    <label className="flex items-center gap-2 p-2.5 rounded-xl border border-border-subtle bg-navy-dark/10 cursor-pointer hover:bg-navy-light/10 transition-colors select-none text-xs text-text-primary">
                      <input 
                        type="checkbox"
                        checked={form.mostrar_footer}
                        onChange={e => setForm(prev => ({ ...prev, mostrar_footer: e.target.checked }))}
                        className="w-4 h-4 rounded border-border-subtle text-cyan focus:ring-0 focus:ring-offset-0 bg-transparent cursor-pointer"
                      />
                      Footer (Sesión)
                    </label>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold text-text-secondary block">
                    Imagen o Video de Banner * {editingAd && '(Dejar en blanco para conservar actual)'}
                  </label>
                  
                  {/* Preview box */}
                  {imagePreview && (
                    <div className="w-full h-32 rounded-xl bg-navy-dark border border-border-subtle flex items-center justify-center p-2 mb-2 overflow-hidden">
                      {imagePreview.startsWith('data:video/') || imagePreview.toLowerCase().endsWith('.mp4') || imagePreview.toLowerCase().endsWith('.webm') ? (
                        <video 
                          src={imagePreview} 
                          className="max-h-full max-w-full object-contain rounded" 
                          controls 
                          muted 
                          autoPlay 
                          loop 
                          playsInline 
                        />
                      ) : (
                        <img src={imagePreview} alt="Preview Banner" className="max-h-full max-w-full object-contain rounded" />
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-border-subtle border-dashed rounded-xl cursor-pointer bg-navy-dark/20 hover:bg-navy-light/20 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="w-6 h-6 text-text-muted mb-1" />
                        <p className="text-xs text-text-muted">
                          <span className="font-semibold">Haz clic para subir</span> o arrastra
                        </p>
                        <p className="text-[10px] text-text-muted mt-0.5">PNG, JPG, WEBP, SVG o Video MP4/WEBM (Imagen máx. 5MB, Video máx. 20MB)</p>
                      </div>
                      <input 
                        type="file" 
                        accept="image/*,video/mp4,video/webm"
                        className="hidden" 
                        onChange={handleImageChange}
                        required={!editingAd}
                      />
                    </label>
                  </div>
                </div>

                <div className="pt-4 border-t border-border-subtle flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" variant="primary" loading={saveMutation.isPending}>
                    Guardar
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ==========================================
// SECCIÓN: GESTIÓN DE TRANSMISIONES EN VIVO
// ==========================================
function TransmisionesSection() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [showModal, setShowModal] = useState(false)
  const [editingChannel, setEditingChannel] = useState(null)
  const [form, setForm] = useState({
    nombre: '',
    stream_url: '',
    tipo: 'iframe',
    costo: '0',
    activo: 1,
    logo_url: '',
    permitir_bloqueo_ads: 0,
    permitir_enlace_externo: 0
  })

  // Obtener canales de transmisión para admin
  const { data: channels = [], isLoading } = useQuery({
    queryKey: ['admin-channels'],
    queryFn: async () => {
      const response = await client.get('/admin/channels')
      return response.data?.datos || []
    }
  })

  // Evitar fallos de desestructuración nula o indefinida en recarga de página (F5)
  const channelsList = Array.isArray(channels) ? channels : []

  const {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    searchTerm,
    setSearchTerm,
    sortField,
    sortOrder,
    requestSort,
    paginatedData: paginatedChannels,
    totalPages,
    totalRecords,
    startIndex,
    endIndex
  } = useDataTable({
    data: channelsList,
    searchFields: ['nombre', 'tipo', 'stream_url'],
    defaultSortField: 'nombre',
    defaultSortOrder: 'asc'
  })

  // Mutación para Guardar (Crear / Editar)
  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      if (editingChannel) {
        return client.put(`/admin/channels/${editingChannel.id}`, payload)
      } else {
        return client.post('/admin/channels', payload)
      }
    },
    onSuccess: (data) => {
      toast.success(data?.mensaje || 'Canal de transmisión guardado con éxito.')
      queryClient.invalidateQueries({ queryKey: ['admin-channels'] })
      setShowModal(false)
      resetForm()
    },
    onError: (error) => {
      const msg = error.response?.data?.mensaje || 'Error al guardar el canal.'
      toast.error(msg)
    }
  })

  // Mutación para Eliminar
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await client.delete(`/admin/channels/${id}`)
      return response.data
    },
    onSuccess: (data) => {
      toast.success(data?.mensaje || 'Canal eliminado correctamente.')
      queryClient.invalidateQueries({ queryKey: ['admin-channels'] })
    },
    onError: (error) => {
      toast.error(error.response?.data?.mensaje || 'Error al eliminar el canal.')
    }
  })

  const resetForm = () => {
    setForm({
      nombre: '',
      stream_url: '',
      tipo: 'iframe',
      costo: '0',
      activo: 1,
      logo_url: '',
      permitir_bloqueo_ads: 0,
      permitir_enlace_externo: 0
    })
    setEditingChannel(null)
  }

  const handleOpenCreate = () => {
    resetForm()
    setShowModal(true)
  }

  const handleOpenEdit = (channel) => {
    setEditingChannel(channel)
    setForm({
      nombre: channel.nombre,
      stream_url: channel.stream_url,
      tipo: channel.tipo,
      costo: String(channel.costo),
      activo: parseInt(channel.activo) === 1 ? 1 : 0,
      logo_url: channel.logo_url || '',
      permitir_bloqueo_ads: channel.permitir_bloqueo_ads !== undefined ? (parseInt(channel.permitir_bloqueo_ads) === 1 ? 1 : 0) : 0,
      permitir_enlace_externo: channel.permitir_enlace_externo !== undefined ? (parseInt(channel.permitir_enlace_externo) === 1 ? 1 : 0) : 0
    })
    setShowModal(true)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!form.nombre.trim()) {
      toast.error('El Nombre es obligatorio')
      return
    }
    if (!form.stream_url.trim()) {
      toast.error('La URL de transmisión es obligatoria')
      return
    }

    const payload = {
      nombre: form.nombre.trim(),
      stream_url: form.stream_url.trim(),
      tipo: form.tipo,
      costo: parseFloat(form.costo) || 0.00,
      activo: parseInt(form.activo),
      logo_url: form.logo_url.trim() || null,
      permitir_bloqueo_ads: parseInt(form.permitir_bloqueo_ads),
      permitir_enlace_externo: parseInt(form.permitir_enlace_externo)
    }

    saveMutation.mutate(payload)
  }

  const logoPresets = [
    { name: 'TyC Sports', url: 'https://upload.wikimedia.org/wikipedia/commons/2/2a/Logo_TyC_Sports_2016.svg' },
    { name: 'ESPN', url: 'https://upload.wikimedia.org/wikipedia/commons/2/2f/ESPN_wordmark.svg' },
    { name: 'DSports', url: 'https://upload.wikimedia.org/wikipedia/commons/e/e0/DSports_logo.svg' },
    { name: 'TV Pública', url: 'https://upload.wikimedia.org/wikipedia/commons/1/15/Televisi%C3%B3n_P%C3%BAblica_Argentina_2021.svg' },
    { name: 'Telefe', url: 'https://upload.wikimedia.org/wikipedia/commons/c/cd/Telefe_logo.svg' }
  ]

  const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
    ? import.meta.env.VITE_API_URL.replace('/api', '')
    : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="font-display font-bold text-xl text-text-primary flex items-center gap-2">
            <Tv className="w-6 h-6 text-cyan" /> Canales de Transmisión en Vivo
          </h2>
          <p className="text-text-secondary text-sm">
            Configura las señales de televisión y streaming para el reproductor en vivo de la aplicación.
          </p>
        </div>
        <Button onClick={handleOpenCreate} className="flex items-center gap-2 shrink-0">
          <Plus className="w-4 h-4" /> Agregar Canal
        </Button>
      </div>

      <Card className="p-4">
        <DataTableControls
          pageSize={pageSize}
          setPageSize={setPageSize}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Buscar canales..."
        />

        {isLoading ? (
          <div className="p-8 space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : totalRecords === 0 ? (
          <div className="p-8 text-center text-text-muted">
            <Tv className="w-12 h-12 mx-auto mb-3 opacity-30 text-cyan" />
            <p>No se registran canales de transmisión.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-navy-dark/40 border-b border-border-subtle text-text-muted">
                  <th className="px-6 py-4 font-semibold text-text-muted uppercase text-xs">Logotipo</th>
                  <SortableHeader field="nombre" currentField={sortField} sortOrder={sortOrder} label="Nombre de la Señal" onSort={requestSort} className="px-6 py-4 font-semibold text-text-muted uppercase text-xs" />
                  <SortableHeader field="tipo" currentField={sortField} sortOrder={sortOrder} label="Tipo" onSort={requestSort} className="px-6 py-4 font-semibold text-text-muted uppercase text-xs" />
                  <SortableHeader field="stream_url" currentField={sortField} sortOrder={sortOrder} label="URL de Transmisión" onSort={requestSort} className="px-6 py-4 font-semibold text-text-muted uppercase text-xs" />
                  <SortableHeader field="costo" currentField={sortField} sortOrder={sortOrder} label="Costo" onSort={requestSort} className="px-6 py-4 font-semibold text-center text-text-muted uppercase text-xs" />
                  <SortableHeader field="activo" currentField={sortField} sortOrder={sortOrder} label="Estado" onSort={requestSort} className="px-6 py-4 font-semibold text-text-muted uppercase text-xs" />
                  <th className="px-6 py-4 font-semibold text-right text-text-muted uppercase text-xs">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle bg-surface-card/10">
                {paginatedChannels.map((channel) => (
                  <tr key={channel.id} className="hover:bg-surface-card/30 transition-colors">
                    <td className="px-6 py-4">
                      {channel.logo_url ? (
                        <div className="w-10 h-10 rounded-lg bg-navy-dark border border-border-subtle p-1 flex items-center justify-center shrink-0">
                          <img 
                            src={channel.logo_url.startsWith('/uploads') ? `${baseImgUrl}${channel.logo_url}` : channel.logo_url} 
                            alt={channel.nombre} 
                            className="max-h-full max-w-full object-contain rounded"
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-tr from-cyan/15 to-indigo-500/15 text-cyan border border-cyan/10 flex items-center justify-center shrink-0 font-display font-bold text-xs">
                          {channel.nombre.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 font-semibold text-text-primary">
                      {channel.nombre}
                    </td>
                    <td className="px-6 py-4 capitalize">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                        channel.tipo === 'hls'
                          ? 'bg-cyan/10 text-cyan border-cyan/20'
                          : 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20'
                      }`}>
                        {channel.tipo === 'hls' ? 'HLS (m3u8)' : 'Iframe Embed'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs text-text-secondary truncate max-w-[250px]" title={channel.stream_url}>
                      {channel.stream_url}
                    </td>
                    <td className="px-6 py-4 text-center font-mono font-bold text-cyan">
                      {parseFloat(channel.costo) > 0 ? `${parseFloat(channel.costo).toFixed(2)} Bs` : 'Gratis'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                        parseInt(channel.activo) === 1
                          ? 'bg-success/10 text-success border-success/20'
                          : 'bg-text-muted/10 text-text-muted border-border-default'
                      }`}>
                        {parseInt(channel.activo) === 1 ? 'Activo' : 'Deshabilitado'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-3 text-text-secondary">
                        <button 
                          onClick={() => handleOpenEdit(channel)} 
                          className="p-1.5 hover:text-cyan hover:bg-navy-light rounded-lg transition-colors border border-transparent hover:border-border-subtle" 
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => window.confirm(`¿Seguro que deseas eliminar el canal "${channel.nombre}"?`) && deleteMutation.mutate(channel.id)} 
                          className="p-1.5 hover:text-error hover:bg-navy-light rounded-lg transition-colors border border-transparent hover:border-border-subtle" 
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DataTablePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          startIndex={startIndex}
          endIndex={endIndex}
          totalRecords={totalRecords}
        />
      </Card>

      {/* Modal Agregar / Editar */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 bg-navy-dark/80 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} 
              animate={{ opacity: 1, scale: 1 }} 
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white dark:bg-navy-dark max-w-lg w-full rounded-2xl overflow-hidden shadow-2xl border border-border-default"
            >
              <div className="p-5 border-b border-border-subtle flex justify-between items-center bg-slate-50 dark:bg-navy-dark/40">
                <div className="flex items-center gap-2">
                  <Tv className="w-5 h-5 text-cyan" />
                  <h3 className="font-display font-bold text-lg text-text-primary">
                    {editingChannel ? 'Editar Canal de Transmisión' : 'Agregar Canal de Transmisión'}
                  </h3>
                </div>
                <button 
                  onClick={() => setShowModal(false)} 
                  className="p-1.5 rounded-full hover:bg-navy-light transition-colors text-text-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-5 space-y-4 max-h-[80vh] overflow-y-auto font-body">
                
                {/* Nombre de la señal */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-text-secondary block">
                    Nombre del Canal o Señal *
                  </label>
                  <input 
                    type="text"
                    required
                    value={form.nombre}
                    onChange={e => setForm(prev => ({ ...prev, nombre: e.target.value }))}
                    placeholder="Ej. TyC Sports En Vivo"
                    className="w-full bg-slate-50 dark:bg-navy-dark/30 border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors"
                  />
                </div>

                {/* Tipo de señal y Costo */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-text-secondary block">
                      Tipo de Señal
                    </label>
                    <select
                      value={form.tipo}
                      onChange={e => setForm(prev => ({ ...prev, tipo: e.target.value }))}
                      className="w-full bg-white dark:bg-navy-dark border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors cursor-pointer"
                    >
                      <option value="iframe">Iframe (Páginas externas)</option>
                      <option value="hls">HLS (.m3u8 señal directa)</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-text-secondary block">
                      Costo (Créditos Bs, 0 para gratis)
                    </label>
                    <input 
                      type="number"
                      min="0"
                      step="0.01"
                      required
                      value={form.costo}
                      onChange={e => setForm(prev => ({ ...prev, costo: e.target.value }))}
                      className="w-full bg-slate-50 dark:bg-navy-dark/30 border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors"
                    />
                  </div>
                </div>

                {/* URL de transmisión */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-text-secondary block flex justify-between">
                    <span>URL de Transmisión *</span>
                    <span className="text-[10px] text-text-muted">
                      {form.tipo === 'hls' ? 'Debe terminar en .m3u8' : 'URL completa del iframe'}
                    </span>
                  </label>
                  <textarea 
                    required
                    value={form.stream_url}
                    onChange={e => setForm(prev => ({ ...prev, stream_url: e.target.value }))}
                    placeholder={form.tipo === 'hls' ? "Ej. https://servidor.com/live/canal.m3u8" : "Ej. https://la18hd.com/vivo/canales.php?stream=tycsports"}
                    className="w-full bg-slate-50 dark:bg-navy-dark/30 border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors min-h-[60px]"
                  />
                </div>

                {/* Logotipo del canal (URL) */}
                <div className="space-y-2">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-text-secondary block">
                      URL del Logotipo (Opcional)
                    </label>
                    <input 
                      type="url"
                      value={form.logo_url}
                      onChange={e => setForm(prev => ({ ...prev, logo_url: e.target.value }))}
                      placeholder="Ej. https://imagen.com/logo.png"
                      className="w-full bg-slate-50 dark:bg-navy-dark/30 border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors"
                    />
                  </div>
                  
                  {/* Presets */}
                  <div className="space-y-1">
                    <span className="text-[10px] font-semibold text-text-muted">Presets Rápidos de Logotipos:</span>
                    <div className="flex flex-wrap gap-2 pt-0.5">
                      {logoPresets.map(preset => (
                        <button
                          key={preset.name}
                          type="button"
                          onClick={() => setForm(prev => ({ ...prev, logo_url: preset.url }))}
                          className="px-2 py-1 bg-navy-dark border border-border-subtle rounded-lg text-[10px] hover:border-cyan text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
                        >
                          {preset.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Opciones adicionales para Iframe Embed */}
                {form.tipo === 'iframe' && (
                  <div className="space-y-3 bg-navy-dark/10 dark:bg-navy-dark/30 p-3 rounded-xl border border-border-subtle">
                    <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wider block mb-1">
                      Opciones del Reproductor Iframe
                    </span>
                    
                    <div className="flex items-start gap-2">
                      <input 
                        type="checkbox"
                        id="permitir-bloqueo-ads"
                        checked={form.permitir_bloqueo_ads === 1}
                        onChange={e => setForm(prev => ({ ...prev, permitir_bloqueo_ads: e.target.checked ? 1 : 0 }))}
                        className="rounded bg-navy border-border-default text-cyan focus:ring-cyan w-4 h-4 cursor-pointer mt-0.5"
                      />
                      <label htmlFor="permitir-bloqueo-ads" className="text-xs font-semibold text-text-secondary cursor-pointer leading-tight">
                        Habilitar botón "Antianuncios"
                        <span className="block text-[10px] text-text-muted font-normal mt-0.5">
                          Permite activar la protección sandbox contra redirecciones y anuncios emergentes.
                        </span>
                      </label>
                    </div>

                    <div className="flex items-start gap-2">
                      <input 
                        type="checkbox"
                        id="permitir-enlace-externo"
                        checked={form.permitir_enlace_externo === 1}
                        onChange={e => setForm(prev => ({ ...prev, permitir_enlace_externo: e.target.checked ? 1 : 0 }))}
                        className="rounded bg-navy border-border-default text-cyan focus:ring-cyan w-4 h-4 cursor-pointer mt-0.5"
                      />
                      <label htmlFor="permitir-enlace-externo" className="text-xs font-semibold text-text-secondary cursor-pointer leading-tight">
                        Habilitar botón "Abrir Señal"
                        <span className="block text-[10px] text-text-muted font-normal mt-0.5">
                          Muestra el botón para abrir la señal externa en una nueva pestaña (solución para errores CORS).
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Estado del canal */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-text-secondary block">
                    Estado de Señal
                  </label>
                  <select
                    value={form.activo}
                    onChange={e => setForm(prev => ({ ...prev, activo: parseInt(e.target.value) }))}
                    className="w-full bg-white dark:bg-navy-dark border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors cursor-pointer"
                  >
                    <option value={1}>Activo (Visible para usuarios)</option>
                    <option value={0}>Deshabilitado (Oculto)</option>
                  </select>
                </div>

                {/* Footer Modal Acciones */}
                <div className="pt-4 border-t border-border-subtle flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" variant="primary" loading={saveMutation.isPending}>
                    Guardar
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ==========================================
// ERROR BOUNDARY PARA CAPTURAR ERRORES EN LAS SECCIONES
// ==========================================
class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error", error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 bg-error/10 border border-error/30 rounded-2xl text-error space-y-4 max-w-2xl mx-auto my-12">
          <h2 className="font-display font-bold text-xl flex items-center gap-2 text-error">
            <AlertTriangle className="w-6 h-6 shrink-0" />
            Algo salió mal en esta sección
          </h2>
          <p className="text-sm font-body font-semibold">
            {this.state.error?.toString()}
          </p>
          <pre className="p-4 bg-navy-dark/80 rounded-xl text-xs font-mono overflow-auto max-h-60 text-text-primary border border-border-subtle whitespace-pre-wrap break-all">
            {this.state.error?.stack}
          </pre>
          <Button onClick={() => window.location.reload()} variant="primary">
            Recargar Página
          </Button>
        </div>
      )
    }

    return this.props.children
  }
}

// ==========================================
// SECCIÓN: FINANZAS Y CAJA (CASH FLOW)
// ==========================================
function FinanzasSection() {
  const queryClient = useQueryClient()
  const toast = useToast()

  const [showGastoModal, setShowGastoModal] = useState(false)
  const [showCatModal, setShowCatModal] = useState(false)
  const [pagina, setPagina] = useState(1)
  const limite = 15

  // Formulario para Registrar Gasto
  const [gastoForm, setGastoForm] = useState({
    descripcion: '',
    monto: '',
    categoria_id: '',
    empleado_id: '',
    fecha: new Date().toISOString().split('T')[0]
  })

  // Formulario para Crear Categoría
  const [catForm, setCatForm] = useState({
    nombre: '',
    visible_empleado: 0
  })

  // 1. Métricas financieras consolidadas
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin-finance-stats'],
    queryFn: async () => {
      const res = await client.get('/admin/finances/stats')
      return res.data?.datos
    }
  })

  // 2. Categorías
  const { data: categories = [], isLoading: catsLoading } = useQuery({
    queryKey: ['admin-finance-categories'],
    queryFn: async () => {
      const res = await client.get('/finances/categories')
      return res.data?.datos || []
    }
  })

  // 3. Gastos (Paginados)
  const { data: expensesData, isLoading: expensesLoading } = useQuery({
    queryKey: ['admin-finance-expenses', pagina],
    queryFn: async () => {
      const res = await client.get(`/finances/expenses?pagina=${pagina}&limite=${limite}`)
      return res.data?.datos
    }
  })

  // 4. Personal para dropdown de sueldos/staff
  const { data: staffList = [] } = useQuery({
    queryKey: ['admin-staff'],
    queryFn: async () => {
      const res = await client.get('/admin/staff')
      return res.data?.datos || []
    }
  })

  const expensesList = expensesData?.gastos || []
  const paginacion = expensesData?.paginacion || { total: 0, paginas: 1, pagina_actual: 1 }

  // Mutación crear categoría
  const createCategoryMutation = useMutation({
    mutationFn: async (data) => {
      const res = await client.post('/admin/finances/categories', data)
      return res.data
    },
    onSuccess: () => {
      toast.success('Categoría de gastos creada correctamente')
      setCatForm({ nombre: '', visible_empleado: 0 })
      queryClient.invalidateQueries({ queryKey: ['admin-finance-categories'] })
      queryClient.invalidateQueries({ queryKey: ['admin-finance-stats'] })
    },
    onError: (error) => {
      toast.error(error.response?.data?.mensaje || 'Error al crear la categoría')
    }
  })

  // Mutación registrar gasto
  const createExpenseMutation = useMutation({
    mutationFn: async (data) => {
      const res = await client.post('/finances/expenses', data)
      return res.data
    },
    onSuccess: () => {
      toast.success('Gasto contable registrado correctamente')
      setGastoForm({
        descripcion: '',
        monto: '',
        categoria_id: '',
        empleado_id: '',
        fecha: new Date().toISOString().split('T')[0]
      })
      setShowGastoModal(false)
      queryClient.invalidateQueries({ queryKey: ['admin-finance-expenses'] })
      queryClient.invalidateQueries({ queryKey: ['admin-finance-stats'] })
    },
    onError: (error) => {
      toast.error(error.response?.data?.mensaje || 'Error al registrar el gasto')
    }
  })

  const handleCreateCategory = (e) => {
    e.preventDefault()
    if (!catForm.nombre.trim()) return
    createCategoryMutation.mutate(catForm)
  }

  const handleCreateExpense = (e) => {
    e.preventDefault()
    if (!gastoForm.descripcion.trim() || !gastoForm.monto || !gastoForm.categoria_id) {
      toast.error('Por favor, completa todos los campos requeridos')
      return
    }
    createExpenseMutation.mutate({
      ...gastoForm,
      monto: parseFloat(gastoForm.monto)
    })
  }

  const COLORS = ['#00C2FF', '#22C55E', '#F59E0B', '#EF4444', '#A855F7', '#EC4899']

  if (statsLoading || !stats) {
    return <div className="text-center py-12 text-text-muted">Cargando métricas financieras...</div>
  }

  return (
    <div className="space-y-6">
      
      {/* Encabezado y Acciones */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="font-display font-bold text-xl text-text-primary">
            Módulo Financiero y Caja (Cash Flow)
          </h2>
          <p className="text-text-secondary text-sm">
            Auditoría de ingresos por comisiones, streaming, retiros, depósitos y egresos operativos.
          </p>
        </div>
        <div className="flex gap-3 w-full sm:w-auto">
          <Button onClick={() => setShowCatModal(true)} variant="outline" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            <span>Categorías</span>
          </Button>
          <Button onClick={() => setShowGastoModal(true)} variant="primary" className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            <span>Registrar Gasto</span>
          </Button>
        </div>
      </div>

      {/* Tarjetas KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        
        {/* Caja Física */}
        <Card className="p-5 border-l-4 border-l-cyan bg-surface-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-text-muted text-xs font-semibold uppercase tracking-wider">Caja Física (Neto)</span>
            <Landmark className="w-5 h-5 text-cyan" />
          </div>
          <p className="font-display font-bold text-2xl text-text-primary mb-3">
            {parseFloat(stats.caja_fisica.saldo_neto).toFixed(2)} Bs
          </p>
          <div className="text-xs text-text-secondary space-y-1.5 font-body">
            <p className="flex justify-between border-b border-border-subtle pb-1">
              <span>Depósitos Aprobados (+)</span>
              <span className="font-semibold text-success">{parseFloat(stats.caja_fisica.depositos).toFixed(2)} Bs</span>
            </p>
            <p className="flex justify-between border-b border-border-subtle pb-1">
              <span>Retiros Aprobados (-)</span>
              <span className="font-semibold text-error">{parseFloat(stats.caja_fisica.retiros).toFixed(2)} Bs</span>
            </p>
            <p className="flex justify-between">
              <span>Gastos Operativos (-)</span>
              <span className="font-semibold text-warning">{parseFloat(stats.caja_fisica.gastos).toFixed(2)} Bs</span>
            </p>
          </div>
        </Card>

        {/* Recaudación Casa */}
        <Card className="p-5 border-l-4 border-l-success bg-surface-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-text-muted text-xs font-semibold uppercase tracking-wider">Recaudación Real de la Casa</span>
            <TrendingUp className="w-5 h-5 text-success" />
          </div>
          <p className="font-display font-bold text-2xl text-text-primary mb-3">
            {parseFloat(stats.responsabilidades.total_ganancias).toFixed(2)} Bs
          </p>
          <div className="text-xs text-text-secondary space-y-1.5 font-body">
            <p className="flex justify-between border-b border-border-subtle pb-1">
              <span>Comisiones de Apuestas</span>
              <span className="font-semibold text-success">{parseFloat(stats.responsabilidades.ganancias_comisiones).toFixed(2)} Bs</span>
            </p>
            <p className="flex justify-between border-b border-border-subtle pb-1">
              <span>Retenciones sin Ganadores</span>
              <span className="font-semibold text-warning">{parseFloat(stats.responsabilidades.retenciones_sin_ganadores || 0).toFixed(2)} Bs</span>
            </p>
            <p className="flex justify-between">
              <span>Transmisiones en Vivo (Video)</span>
              <span className="font-semibold text-cyan">{parseFloat(stats.responsabilidades.ganancias_transmisiones).toFixed(2)} Bs</span>
            </p>
            <p className="text-[10px] text-text-muted italic pt-1 text-center">
              * Ingresos netos reales (apuestas liquidadas menos premios pagados).
            </p>
          </div>
        </Card>

        {/* Pasivo Circulante */}
        <Card className="p-5 border-l-4 border-l-warning bg-surface-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-text-muted text-xs font-semibold uppercase tracking-wider">Créditos de Usuarios</span>
            <Coins className="w-5 h-5 text-warning" />
          </div>
          <p className="font-display font-bold text-2xl text-text-primary mb-3">
            {parseFloat(stats.responsabilidades.pasivo_circulante).toFixed(2)} Bs
          </p>
          <div className="text-xs text-text-secondary space-y-1.5 font-body">
            <p className="flex justify-between">
              <span>Pasivo Virtual Total</span>
              <span className="font-semibold text-warning">{parseFloat(stats.responsabilidades.pasivo_circulante).toFixed(2)} Bs</span>
            </p>
            <p className="text-[10px] text-text-muted italic pt-2.5">
              Representa los saldos que los usuarios poseen actualmente y que la casa debe respaldar financieramente.
            </p>
          </div>
        </Card>

      </div>

      {/* Gráficos Recharts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Gráfico 1: Flujo de Caja */}
        <Card className="p-6">
          <h3 className="font-display font-bold text-sm text-text-primary mb-4 flex items-center gap-2 uppercase tracking-wider">
            <TrendingUp className="w-4 h-4 text-cyan" />
            Flujo de Caja diario (30 días)
          </h3>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.tendencia} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorIng" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22C55E" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#22C55E" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorEgr" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
                <XAxis dataKey="fecha" stroke="var(--color-text-muted)" fontSize={11} tickLine={false} />
                <YAxis stroke="var(--color-text-muted)" fontSize={11} tickLine={false} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--color-navy-dark)', borderColor: 'var(--color-border-default)', borderRadius: '12px', color: '#FFF' }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '11px', pt: '10px' }} />
                <Area type="monotone" dataKey="ingresos" name="Ingresos (Bs)" stroke="#22C55E" fillOpacity={1} fill="url(#colorIng)" strokeWidth={2} />
                <Area type="monotone" dataKey="egresos" name="Egresos (Bs)" stroke="#EF4444" fillOpacity={1} fill="url(#colorEgr)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Gráfico 2: Egresos por Categoría */}
        <Card className="p-6">
          <h3 className="font-display font-bold text-sm text-text-primary mb-4 flex items-center gap-2 uppercase tracking-wider">
            <Receipt className="w-4 h-4 text-warning" />
            Egresos por Categoría
          </h3>
          <div className="h-[280px]">
            {stats.gastos_por_categoria.length === 0 ? (
              <div className="flex items-center justify-center h-full text-text-muted text-sm italic">
                Sin egresos operativos registrados aún.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.gastos_por_categoria} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
                  <XAxis dataKey="categoria_nombre" stroke="var(--color-text-muted)" fontSize={10} tickLine={false} />
                  <YAxis stroke="var(--color-text-muted)" fontSize={11} tickLine={false} />
                  <Tooltip formatter={(value) => [`${parseFloat(value).toFixed(2)} Bs`, 'Monto']} contentStyle={{ backgroundColor: 'var(--color-navy-dark)', borderColor: 'var(--color-border-default)', borderRadius: '12px', color: '#FFF' }} />
                  <Bar dataKey="total" name="Total Bs" radius={[4, 4, 0, 0]}>
                    {stats.gastos_por_categoria.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

      </div>

      {/* Historial de Egresos */}
      <Card className="p-6">
        <h3 className="font-display font-bold text-lg text-text-primary mb-4">
          Bitácora Completa de Gastos
        </h3>

        {expensesLoading ? (
          <div className="text-center py-12 text-text-muted">Cargando egresos...</div>
        ) : expensesList.length === 0 ? (
          <div className="text-center py-12 text-text-muted italic">No se han registrado egresos operativos.</div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-subtle text-text-muted text-xs font-semibold uppercase tracking-wider">
                    <th className="text-left pb-3 font-display">Concepto</th>
                    <th className="text-left pb-3 font-display">Categoría</th>
                    <th className="text-left pb-3 font-display">Beneficiario / Personal</th>
                    <th className="text-left pb-3 font-display">Registrado Por</th>
                    <th className="text-left pb-3 font-display">Fecha</th>
                    <th className="text-right pb-3 font-display">Monto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle text-text-secondary">
                  {expensesList.map((g) => (
                    <tr key={g.id} className="hover:bg-surface-card-hover/40 transition-colors">
                      <td className="py-3.5 pr-4 font-semibold text-text-primary">{g.descripcion}</td>
                      <td className="py-3.5">
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan/10 text-cyan border border-cyan/20">
                          {g.categoria_nombre}
                        </span>
                      </td>
                      <td className="py-3.5 capitalize">
                        {g.empleado_nombre ? (
                          <span className="font-medium text-text-primary">{g.empleado_nombre}</span>
                        ) : (
                          <span className="text-text-muted italic">Ninguno</span>
                        )}
                      </td>
                      <td className="py-3.5 capitalize text-xs font-medium text-text-muted">
                        {g.creador_nombre}
                      </td>
                      <td className="py-3.5 text-xs font-mono">
                        {new Date(g.fecha).toLocaleDateString('es-BO', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="py-3.5 text-right font-display font-bold text-text-primary">
                        {parseFloat(g.monto).toFixed(2)} Bs
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {paginacion.paginas > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-border-subtle">
                <span className="text-xs text-text-muted">
                  Página {paginacion.pagina_actual} de {paginacion.paginas}
                </span>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    disabled={pagina === 1} 
                    onClick={() => setPagina(prev => Math.max(1, prev - 1))}
                  >
                    Anterior
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    disabled={pagina === paginacion.paginas} 
                    onClick={() => setPagina(prev => Math.min(paginacion.paginas, prev + 1))}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}

          </div>
        )}
      </Card>

      {/* MODAL REGISTRAR GASTO */}
      <AnimatePresence>
        {showGastoModal && (
          <div className="fixed inset-0 z-50 bg-navy-dark/80 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} 
              animate={{ opacity: 1, scale: 1 }} 
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white dark:bg-navy-dark max-w-lg w-full rounded-2xl overflow-hidden shadow-2xl border border-border-default"
            >
              <div className="p-5 border-b border-border-subtle flex justify-between items-center bg-slate-50 dark:bg-navy-dark/40">
                <div className="flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-cyan" />
                  <h3 className="font-display font-bold text-lg text-text-primary">
                    Registrar Gasto Administrativo
                  </h3>
                </div>
                <button 
                  onClick={() => setShowGastoModal(false)} 
                  className="p-1.5 rounded-full hover:bg-navy-light transition-colors text-text-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCreateExpense} className="p-5 space-y-4 max-h-[85vh] overflow-y-auto font-body">
                
                {/* Concepto */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-text-secondary block">
                    Descripción / Concepto del Gasto *
                  </label>
                  <input 
                    type="text"
                    required
                    value={gastoForm.descripcion}
                    onChange={e => setGastoForm(prev => ({ ...prev, descripcion: e.target.value }))}
                    placeholder="Ej. Compra de cables ethernet para transmisiones"
                    className="w-full bg-slate-50 dark:bg-navy-dark/30 border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors"
                  />
                </div>

                {/* Monto y Fecha */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-text-secondary block">
                      Monto en Bs *
                    </label>
                    <input 
                      type="number"
                      step="0.01"
                      min="0.01"
                      required
                      value={gastoForm.monto}
                      onChange={e => setGastoForm(prev => ({ ...prev, monto: e.target.value }))}
                      placeholder="0.00"
                      className="w-full bg-slate-50 dark:bg-navy-dark/30 border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-text-secondary block">
                      Fecha *
                    </label>
                    <input 
                      type="date"
                      required
                      value={gastoForm.fecha}
                      onChange={e => setGastoForm(prev => ({ ...prev, fecha: e.target.value }))}
                      className="w-full bg-slate-50 dark:bg-navy-dark/30 border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors cursor-pointer"
                    />
                  </div>
                </div>

                {/* Categoría */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-text-secondary block">
                    Categoría de Gasto *
                  </label>
                  <select
                    required
                    value={gastoForm.categoria_id}
                    onChange={e => setGastoForm(prev => ({ ...prev, categoria_id: e.target.value }))}
                    className="w-full bg-white dark:bg-navy-dark border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors cursor-pointer"
                  >
                    <option value="">Seleccione una categoría</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nombre} {!c.visible_empleado && '(Solo Admin)'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Beneficiario / Staff */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-text-secondary block">
                    Beneficiario (Staff / Opcional)
                  </label>
                  <select
                    value={gastoForm.empleado_id}
                    onChange={e => setGastoForm(prev => ({ ...prev, empleado_id: e.target.value }))}
                    className="w-full bg-white dark:bg-navy-dark border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors cursor-pointer"
                  >
                    <option value="">Ninguno / Egreso General de la Plataforma</option>
                    {staffList.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.nombre} {staff.apellido} ({staff.rol})
                      </option>
                    ))}
                  </select>
                  <p className="text-[10px] text-text-muted">
                    Asocia el pago a un empleado o administrador si estás registrando sueldos u honorarios.
                  </p>
                </div>

                {/* Footer Modal */}
                <div className="pt-4 border-t border-border-subtle flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setShowGastoModal(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" variant="primary" loading={createExpenseMutation.isPending}>
                    Registrar Gasto
                  </Button>
                </div>

              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL GESTIONAR CATEGORÍAS */}
      <AnimatePresence>
        {showCatModal && (
          <div className="fixed inset-0 z-50 bg-navy-dark/80 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} 
              animate={{ opacity: 1, scale: 1 }} 
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white dark:bg-navy-dark max-w-2xl w-full rounded-2xl overflow-hidden shadow-2xl border border-border-default"
            >
              <div className="p-5 border-b border-border-subtle flex justify-between items-center bg-slate-50 dark:bg-navy-dark/40">
                <div className="flex items-center gap-2">
                  <Layers className="w-5 h-5 text-cyan" />
                  <h3 className="font-display font-bold text-lg text-text-primary">
                    Gestionar Categorías de Gastos
                  </h3>
                </div>
                <button 
                  onClick={() => setShowCatModal(false)} 
                  className="p-1.5 rounded-full hover:bg-navy-light transition-colors text-text-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6 font-body">
                
                {/* Lista de Categorías */}
                <div className="space-y-3">
                  <h4 className="font-display font-semibold text-sm text-text-primary border-b border-border-subtle pb-1">
                    Categorías Registradas
                  </h4>
                  <div className="max-h-[280px] overflow-y-auto space-y-2 pr-1">
                    {categories.map((c) => (
                      <div 
                        key={c.id} 
                        className="p-2.5 bg-slate-50 dark:bg-navy-dark/30 rounded-xl border border-border-subtle flex flex-col gap-1.5"
                      >
                        <p className="text-sm font-semibold text-text-primary">{c.nombre}</p>
                        <span className={`self-start text-[10px] px-2 py-0.5 rounded-full font-bold border ${
                          c.visible_empleado == 1 
                            ? 'bg-success/10 text-success border-success/20' 
                            : 'bg-text-muted/15 text-text-muted border-border-subtle'
                        }`}>
                          {c.visible_empleado == 1 ? 'Visible para Empleados ✅' : 'Solo Administradores 🛡️'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Formulario Nueva Categoría */}
                <form onSubmit={handleCreateCategory} className="space-y-4 self-start">
                  <h4 className="font-display font-semibold text-sm text-text-primary border-b border-border-subtle pb-1">
                    Nueva Categoría
                  </h4>
                  
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-text-secondary block">
                      Nombre de Categoría *
                    </label>
                    <input 
                      type="text"
                      required
                      value={catForm.nombre}
                      onChange={e => setCatForm(prev => ({ ...prev, nombre: e.target.value }))}
                      placeholder="Ej. Hosting Servidor"
                      className="w-full bg-slate-50 dark:bg-navy-dark/30 border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-cyan transition-colors"
                    />
                  </div>

                  <div className="flex items-start gap-2 pt-2">
                    <input 
                      type="checkbox"
                      id="visible-emp"
                      checked={catForm.visible_empleado === 1}
                      onChange={e => setCatForm(prev => ({ ...prev, visible_empleado: e.target.checked ? 1 : 0 }))}
                      className="rounded bg-navy border-border-default text-cyan focus:ring-cyan w-4 h-4 cursor-pointer mt-0.5"
                    />
                    <label htmlFor="visible-emp" className="text-xs font-semibold text-text-secondary cursor-pointer leading-tight">
                      Habilitar visibilidad para empleados
                      <span className="block text-[10px] text-text-muted font-normal mt-0.5">
                        Si se activa, el personal podrá registrar compras de caja chica bajo esta categoría.
                      </span>
                    </label>
                  </div>

                  <Button 
                    type="submit" 
                    variant="primary" 
                    className="w-full"
                    loading={createCategoryMutation.isPending}
                  >
                    Crear Categoría
                  </Button>
                </form>

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  )
}

// ==========================================
// SECCIÓN: HISTORIAL Y AUDITORÍA DE APUESTAS (Solo SuperAdmin)
// ==========================================
function HistorialApuestasSection() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [selectedBet, setSelectedBet] = useState(null)
  const [showAuditModal, setShowAuditModal] = useState(false)
  const [statusFilter, setStatusFilter] = useState('all')
  const [auditTypeFilter, setAuditTypeFilter] = useState('modified') // 'all' | 'modified' | 'late'

  // Obtener rol del usuario autenticado
  const { user: loggedInUser } = useAuthStore()
  const isSuperAdmin = loggedInUser?.rol === 'superadmin'

  const { data: bets = [], isLoading } = useQuery({
    queryKey: ['admin-bets-auditoria', auditTypeFilter],
    queryFn: async () => {
      let url = '/admin/bets'
      if (auditTypeFilter === 'modified') {
        url += '?modified=1'
      } else if (auditTypeFilter === 'late') {
        url += '?late=1'
      }
      const response = await client.get(url)
      return response.data?.datos || []
    }
  })

  // Obtener auditoría para la apuesta seleccionada
  const { data: auditLogs = [], isLoading: isLoadingAudit } = useQuery({
    queryKey: ['admin-bet-audit', selectedBet?.id],
    queryFn: async () => {
      if (!selectedBet?.id) return []
      const response = await client.get(`/admin/bets/${selectedBet.id}/history`)
      return response.data?.datos || []
    },
    enabled: !!selectedBet?.id
  })

  const {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    searchTerm,
    setSearchTerm,
    sortField,
    sortOrder,
    requestSort,
    paginatedData: paginatedBets,
    totalPages,
    totalRecords,
    startIndex,
    endIndex
  } = useDataTable({
    data: bets,
    searchFields: ['usuario_nombre', 'usuario_apellido', 'usuario_email', 'equipo_local_nombre', 'equipo_visitante_nombre', 'seleccion'],
    defaultSortField: 'creado_en',
    defaultSortOrder: 'desc',
    customFilter: (bet) => {
      // Ocultar del historial/auditoría general de apuestas a usuarios excluidos si no es SuperAdmin
      if (!isSuperAdmin && Number(bet.usuario_excluir_auditoria) === 1) {
        return false
      }
      return statusFilter === 'all' || bet.estado === statusFilter
    }
  })

  const handleOpenAudit = (bet) => {
    setSelectedBet(bet)
    setShowAuditModal(true)
  }

  const handleCloseAudit = () => {
    setSelectedBet(null)
    setShowAuditModal(false)
  }

  const getSeleccionLabel = (sel, loc, vis) => {
    if (sel === 'local') return `Local (${loc})`
    if (sel === 'visitante') return `Visitante (${vis})`
    return 'Empate'
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-bold text-xl text-text-primary flex items-center gap-2">
          <History className="w-6 h-6 text-cyan" /> Historial de Apuestas y Auditoría
        </h2>
        <p className="text-text-secondary text-sm">
          Monitorea todas las apuestas activas y finalizadas. Revisa la bitácora de auditoría para detectar modificaciones autorizadas.
        </p>
      </div>

      {/* Tabs de Auditoría */}
      <div className="flex border-b border-border-subtle bg-surface-card/25 p-1 rounded-xl w-fit">
        <button
          onClick={() => setAuditTypeFilter('modified')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            auditTypeFilter === 'modified'
              ? 'bg-cyan/15 text-cyan border border-cyan/35 shadow-sm'
              : 'text-text-muted hover:text-text-secondary hover:bg-surface-card/45 border border-transparent'
          }`}
        >
          <History className="w-4 h-4" />
          Apuestas Modificadas
        </button>
        <button
          onClick={() => setAuditTypeFilter('late')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            auditTypeFilter === 'late'
              ? 'bg-cyan/15 text-cyan border border-cyan/35 shadow-sm'
              : 'text-text-muted hover:text-text-secondary hover:bg-surface-card/45 border border-transparent'
          }`}
        >
          <Clock className="w-4 h-4" />
          Apuestas Fuera de Hora / En Vivo
        </button>
      </div>

      <Card className="p-4">
        <DataTableControls
          pageSize={pageSize}
          setPageSize={setPageSize}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Buscar por usuario, correo o equipos..."
        >
          <div className="w-full sm:w-64 flex gap-2 shrink-0">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="w-full bg-surface-card border border-border-default rounded-xl p-1.5 text-xs focus:border-cyan focus:outline-none text-text-secondary cursor-pointer"
            >
              <option value="all">Todos los estados</option>
              <option value="pendiente">Pendientes ⏳</option>
              <option value="ganada">Ganadas 🏆</option>
              <option value="perdida">Perdidas ❌</option>
              <option value="devuelta">Devueltas 🔄</option>
            </select>
          </div>
        </DataTableControls>

        {isLoading ? (
          <div className="p-8 space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : totalRecords === 0 ? (
          <div className="p-8 text-center text-text-muted">
            <History className="w-12 h-12 mx-auto mb-3 opacity-30 text-cyan animate-pulse" />
            <p>No se registran apuestas en el sistema.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-navy-dark/40 border-b border-border-subtle text-text-muted">
                  <SortableHeader field="usuario_nombre" currentField={sortField} sortOrder={sortOrder} label="Usuario" onSort={requestSort} className="px-6 py-4 font-semibold font-display uppercase tracking-wider text-xs" />
                  <SortableHeader field="equipo_local_nombre" currentField={sortField} sortOrder={sortOrder} label="Partido" onSort={requestSort} className="px-6 py-4 font-semibold font-display uppercase tracking-wider text-xs" />
                  <SortableHeader field="monto" currentField={sortField} sortOrder={sortOrder} label="Monto" onSort={requestSort} className="px-6 py-4 font-semibold font-display uppercase tracking-wider text-xs" />
                  <SortableHeader field="seleccion" currentField={sortField} sortOrder={sortOrder} label="Selección / Goles" onSort={requestSort} className="px-6 py-4 font-semibold font-display uppercase tracking-wider text-xs" />
                  <SortableHeader field="estado" currentField={sortField} sortOrder={sortOrder} label="Estado" onSort={requestSort} className="px-6 py-4 font-semibold font-display uppercase tracking-wider text-xs" />
                  <th className="px-6 py-4 font-semibold font-display uppercase tracking-wider text-xs text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle bg-surface-card/10">
                {paginatedBets.map((bet) => (
                  <tr key={bet.id} className="hover:bg-surface-card/30 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-semibold text-text-primary">{bet.usuario_nombre} {bet.usuario_apellido}</p>
                      <p className="text-xs text-text-muted font-mono">{bet.usuario_email}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Flag code={bet.equipo_local_bandera} className="w-5 h-3.5 object-cover rounded shadow-sm" />
                        <span className="font-semibold">{bet.equipo_local_nombre}</span>
                        <span className="text-text-muted text-xs">vs</span>
                        <span className="font-semibold">{bet.equipo_visitante_nombre}</span>
                        <Flag code={bet.equipo_visitante_bandera} className="w-5 h-3.5 object-cover rounded shadow-sm" />
                      </div>
                      <p className="text-[10px] text-text-muted mt-1 uppercase font-display font-medium tracking-wide">
                        {(FASE_NOMBRES[bet.partido_fase] || bet.partido_fase.replace('_', ' '))} — {new Date(bet.fecha_partido.replace(' ', 'T')).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                      {(bet.es_fuera_de_hora === 1 || new Date(bet.creado_en.replace(' ', 'T')) >= new Date(bet.fecha_partido.replace(' ', 'T'))) && (
                        <div className="mt-1.5 flex items-center gap-1 text-[11px] font-bold text-error bg-error/10 border border-error/20 px-2 py-0.5 rounded-lg w-fit">
                          <AlertTriangle className="w-3 h-3 shrink-0" />
                          <span>
                            {bet.minutos_atraso && Number(bet.minutos_atraso) > 0 
                              ? `Fuera de hora (+${bet.minutos_atraso} min)`
                              : 'En juego / En vivo'}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 font-mono font-bold text-cyan">
                      {Number(bet.monto).toFixed(2)} Bs
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-text-primary text-xs capitalize">
                        {getSeleccionLabel(bet.seleccion, bet.equipo_local_nombre, bet.equipo_visitante_nombre)}
                      </p>
                      <p className="text-[10px] text-text-muted mt-0.5 font-mono">
                        Marcador Predicho: <span className="font-bold text-cyan">{bet.goles_local_predichos} - {bet.goles_visitante_predichos}</span>
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                        bet.estado === 'pendiente'
                          ? 'bg-orange-500/10 text-orange-400 border-orange-500/20'
                          : bet.estado === 'ganada'
                          ? 'bg-success/10 text-success border-success/20'
                          : bet.estado === 'perdida'
                          ? 'bg-error/10 text-error border-error/20'
                          : 'bg-text-muted/10 text-text-muted border-border-default'
                      }`}>
                        {bet.estado}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        size="xs"
                        variant="outline"
                        icon={History}
                        onClick={() => handleOpenAudit(bet)}
                      >
                        Auditoría
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DataTablePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          startIndex={startIndex}
          endIndex={endIndex}
          totalRecords={totalRecords}
        />
      </Card>

      {/* Modal de Auditoría */}
      <AnimatePresence>
        {showAuditModal && selectedBet && (
          <div className="fixed inset-0 z-50 bg-navy-dark/80 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white dark:bg-navy-dark max-w-2xl w-full rounded-2xl overflow-hidden shadow-2xl border border-border-default font-body"
            >
              <div className="p-5 border-b border-border-subtle flex justify-between items-center bg-slate-50 dark:bg-navy-dark/40">
                <div className="flex items-center gap-2">
                  <History className="w-5 h-5 text-cyan" />
                  <h3 className="font-display font-bold text-lg text-text-primary">
                    Bitácora de Auditoría de la Apuesta #{selectedBet.id}
                  </h3>
                </div>
                <button
                  onClick={handleCloseAudit}
                  className="p-1.5 rounded-full hover:bg-navy-light transition-colors text-text-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="p-4 bg-navy-dark/40 rounded-xl border border-border-subtle flex flex-col gap-2">
                  <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">Detalles de Apuesta</p>
                  <div className="grid grid-cols-2 gap-4 text-sm text-text-secondary">
                    <div>
                      <p>Usuario: <span className="font-bold text-text-primary">{selectedBet.usuario_nombre} {selectedBet.usuario_apellido}</span></p>
                      <p>Monto: <span className="font-bold text-cyan font-mono">{Number(selectedBet.monto).toFixed(2)} Bs</span></p>
                    </div>
                    <div>
                      <p>Partido: <span className="font-bold text-text-primary">{selectedBet.equipo_local_nombre} vs {selectedBet.equipo_visitante_nombre}</span></p>
                      <p>Creada: <span className="font-mono text-xs">{new Date(selectedBet.creado_en).toLocaleString('es-BO')}</span></p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-display font-semibold text-sm text-text-primary border-b border-border-subtle pb-1">
                    Historial de Cambios
                  </h4>
                  {isLoadingAudit ? (
                    <div className="text-center py-6 text-text-muted text-xs">Cargando auditoría...</div>
                  ) : auditLogs.length === 0 ? (
                    <div className="text-center py-6 text-text-muted text-xs italic bg-navy-dark/20 rounded-xl p-4 border border-dashed border-border-subtle">
                      Esta apuesta conserva sus valores originales de registro. No ha tenido modificaciones.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {auditLogs.map((log) => (
                        <div key={log.id} className="p-4 bg-slate-50 dark:bg-navy-dark/20 border border-border-subtle rounded-xl flex flex-col gap-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-semibold text-text-primary">
                              Por: <span className="text-cyan">{log.modificado_por_nombre}</span> ({log.modificado_por_email})
                            </span>
                            <span className="text-[10px] text-text-muted font-mono">
                              {new Date(log.creado_en).toLocaleString('es-BO')}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-2 border-t border-border-subtle/30 text-xs">
                            <div className="space-y-1">
                              <p className="text-[10px] uppercase font-bold text-text-muted">Selección de Ganador</p>
                              <div className="flex items-center gap-1.5 font-medium">
                                <span className="text-error line-through capitalize">{log.seleccion_anterior}</span>
                                <span className="text-text-muted font-mono">→</span>
                                <span className="text-success font-bold capitalize">{log.seleccion_nueva}</span>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <p className="text-[10px] uppercase font-bold text-text-muted">Goles Predichos</p>
                              <div className="flex items-center gap-1.5 font-mono font-bold">
                                <span className="text-error line-through">{log.goles_predichos_anterior}</span>
                                <span className="text-text-muted font-normal">→</span>
                                <span className="text-success">{log.goles_predichos_nuevo}</span>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <p className="text-[10px] uppercase font-bold text-text-muted">Monto Apostado</p>
                              {log.monto_anterior !== null && log.monto_anterior !== undefined ? (
                                <div className="flex items-center gap-1.5 font-mono font-bold">
                                  <span className="text-error line-through">{Number(log.monto_anterior).toFixed(2)} Bs</span>
                                  <span className="text-text-muted font-normal">→</span>
                                  <span className="text-success">{Number(log.monto_nuevo).toFixed(2)} Bs</span>
                                </div>
                              ) : (
                                <span className="text-text-muted italic">Sin cambios</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-5 border-t border-border-subtle flex justify-end bg-slate-50 dark:bg-navy-dark/40">
                <Button onClick={handleCloseAudit} variant="primary">
                  Cerrar
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ==========================================
// SECCIÓN: ROLES Y PERMISOS (RBAC)
// ==========================================
function PermisosSection() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [selectedRole, setSelectedRole] = useState(null)
  const [localPermissions, setLocalPermissions] = useState({})

  // Obtener permisos desde la API
  const { data: rolesPermissions, isLoading, refetch } = useQuery({
    queryKey: ['admin-permissions'],
    queryFn: async () => {
      const response = await client.get('/admin/permissions')
      return response.data?.datos || []
    }
  })

  // Mutation para guardar permisos
  const saveMutation = useMutation({
    mutationFn: async ({ rol, permisos }) => {
      const response = await client.put('/admin/permissions', { rol, permisos })
      return response.data
    },
    onSuccess: (data) => {
      toast.show('¡Permisos actualizados con éxito!', 'success')
      queryClient.invalidateQueries({ queryKey: ['admin-permissions'] })
      refetch()
    },
    onError: (error) => {
      const msg = error.response?.data?.mensaje || 'Error al guardar los permisos.'
      toast.show(msg, 'error')
    }
  })

  // Establecer el primer rol de la lista cuando se cargan los datos
  React.useEffect(() => {
    if (rolesPermissions && rolesPermissions.length > 0 && !selectedRole) {
      const firstRole = rolesPermissions[0]
      setSelectedRole(firstRole.rol)
      setLocalPermissions(firstRole.permisos)
    }
  }, [rolesPermissions, selectedRole])

  // Cambiar rol seleccionado y actualizar estado local de permisos
  const handleSelectRole = (rolName) => {
    const roleData = rolesPermissions.find(r => r.rol === rolName)
    if (roleData) {
      setSelectedRole(rolName)
      setLocalPermissions(roleData.permisos)
    }
  }

  // Manejar el cambio de un switch/toggle
  const handleTogglePermission = (permissionKey) => {
    setLocalPermissions(prev => ({
      ...prev,
      [permissionKey]: prev[permissionKey] === 1 ? 0 : 1
    }))
  }

  const handleSave = () => {
    if (!selectedRole) return
    saveMutation.mutate({
      rol: selectedRole,
      permisos: localPermissions
    })
  }

  if (isLoading || !selectedRole) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 text-cyan animate-spin" />
      </div>
    )
  }

  const currentRoleData = rolesPermissions.find(r => r.rol === selectedRole) || { editable: false }
  const isEditable = currentRoleData.editable

  // Nombres amigables y descripciones para los 12 permisos
  const permissionsMeta = {
    gestionar_resultados: {
      titulo: 'Gestionar Resultados',
      descripcion: 'Registrar marcadores finales de los partidos y recalcular los puntos de los usuarios.'
    },
    gestionar_partidos: {
      titulo: 'Gestionar Partidos',
      descripcion: 'Crear, editar, reprogramar y eliminar partidos del Mundial.'
    },
    gestionar_equipos: {
      titulo: 'Gestionar Equipos',
      descripcion: 'Administrar los países participantes, grupos de la Copa y banderas.'
    },
    gestionar_jugadores: {
      titulo: 'Gestionar Jugadores',
      descripcion: 'Crear, editar y eliminar jugadores de las selecciones nacionales.'
    },
    gestionar_usuarios: {
      titulo: 'Gestionar Usuarios',
      descripcion: 'Modificar roles, sancionar/banear cuentas y activar el estado Premium.'
    },
    aprobar_recargas: {
      titulo: 'Aprobar Recargas',
      descripcion: 'Aprobar o rechazar depósitos bancarios de saldo con su respectivo voucher.'
    },
    aprobar_retiros: {
      titulo: 'Aprobar Retiros',
      descripcion: 'Procesar, aprobar o rechazar solicitudes de retiro de créditos de los usuarios.'
    },
    gestionar_finanzas: {
      titulo: 'Gestionar Finanzas',
      descripcion: 'Visualizar métricas financieras de la plataforma, crear categorías y egresos/gastos.'
    },
    gestionar_apuestas: {
      titulo: 'Gestionar Apuestas',
      descripcion: 'Administrar y liquidar pozos de apuestas acumuladas en partidos.'
    },
    gestionar_canales: {
      titulo: 'Gestionar Canales',
      descripcion: 'Configurar canales de transmisión en vivo (streaming), precios de acceso y urls.'
    },
    gestionar_publicidad: {
      titulo: 'Gestionar Publicidad',
      descripcion: 'Administrar banners publicitarios, enlaces de WhatsApp y pautas en la plataforma.'
    },
    gestionar_configuracion: {
      titulo: 'Configuración General',
      descripcion: 'Ajustar parámetros globales del sistema, PWA, logo y cuenta QR para depósitos.'
    }
  }

  // Traducir los nombres de roles para el usuario
  const roleTranslations = {
    superadmin: 'Super Administrador',
    admin: 'Administrador',
    empleado: 'Empleado / Staff',
    usuario: 'Usuario Final'
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-display font-bold text-2xl text-text-primary flex items-center gap-2">
            <Shield className="w-6 h-6 text-cyan" />
            Roles y Permisos del Sistema
          </h2>
          <p className="text-text-muted text-sm mt-1">
            Administra de forma dinámica los privilegios de cada rol en la plataforma.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-6">
        {/* Selector lateral de roles */}
        <div className="md:col-span-1 space-y-2">
          <p className="text-xs uppercase tracking-wider font-semibold text-text-muted px-2 font-display">
            Roles de Usuario
          </p>
          <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-x-visible pb-2 md:pb-0">
            {rolesPermissions.map((roleObj) => {
              const active = selectedRole === roleObj.rol
              return (
                <button
                  key={roleObj.rol}
                  onClick={() => handleSelectRole(roleObj.rol)}
                  className={`
                    w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium font-body transition-all shrink-0
                    ${active
                      ? 'bg-cyan/15 text-cyan border border-cyan/25'
                      : 'glass border border-transparent text-text-secondary hover:text-text-primary hover:bg-surface-card-hover'
                    }
                  `}
                >
                  <div className="font-display font-semibold">
                    {roleTranslations[roleObj.rol] || roleObj.rol}
                  </div>
                  <span className="text-[10px] opacity-75 font-normal block md:inline mt-0.5">
                    {roleObj.rol === 'superadmin' || roleObj.rol === 'admin' ? 'Acceso Staff' : roleObj.rol === 'empleado' ? 'Soporte Caja' : 'Cliente'}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Matriz de Permisos */}
        <div className="md:col-span-3">
          <Card className="p-6 relative overflow-hidden">
            {/* Cabecera del Rol seleccionado */}
            <div className="flex items-center justify-between border-b border-border-subtle pb-4 mb-6">
              <div>
                <h3 className="font-display font-bold text-lg text-text-primary">
                  Configuración para {roleTranslations[selectedRole] || selectedRole}
                </h3>
                <p className="text-xs text-text-muted mt-0.5">
                  {isEditable
                    ? 'Puedes activar o desactivar los permisos a continuación.'
                    : 'Este rol es de solo lectura. No se puede modificar por seguridad.'
                  }
                </p>
              </div>
              <div>
                {isEditable ? (
                  <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 px-2.5 py-1 rounded-full text-xs font-semibold">
                    Editable
                  </span>
                ) : (
                  <span className="bg-amber-500/10 text-amber-400 border border-amber-500/25 px-2.5 py-1 rounded-full text-xs font-semibold">
                    Solo lectura
                  </span>
                )}
              </div>
            </div>

            {/* Lista de Permisos */}
            <div className="space-y-4">
              {Object.keys(localPermissions).map((permissionKey) => {
                const meta = permissionsMeta[permissionKey] || {
                  titulo: permissionKey,
                  descripcion: 'Permiso del sistema.'
                }
                const isChecked = localPermissions[permissionKey] === 1

                return (
                  <div
                    key={permissionKey}
                    className={`
                      flex items-start justify-between p-4 rounded-xl transition-all border
                      ${isChecked
                        ? 'bg-cyan/5 border-cyan/10'
                        : 'glass border-transparent'
                      }
                    `}
                  >
                    <div className="space-y-1 pr-4">
                      <p className="font-display font-semibold text-sm text-text-primary">
                        {meta.titulo}
                      </p>
                      <p className="text-xs text-text-muted leading-relaxed">
                        {meta.descripcion}
                      </p>
                    </div>

                    <div className="flex items-center pt-0.5">
                      <button
                        role="switch"
                        aria-checked={isChecked}
                        disabled={!isEditable}
                        onClick={() => handleTogglePermission(permissionKey)}
                        className={`
                          relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none
                          ${!isEditable ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                          ${isChecked ? 'bg-cyan' : 'bg-surface-card border border-border-subtle'}
                        `}
                      >
                        <span
                          className={`
                            inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                            ${isChecked ? 'translate-x-6' : 'translate-x-1'}
                          `}
                        />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Acción de guardar */}
            {isEditable && (
              <div className="border-t border-border-subtle pt-6 mt-6 flex justify-end">
                <Button
                  onClick={handleSave}
                  disabled={saveMutation.isPending}
                  variant="primary"
                  className="flex items-center gap-2"
                >
                  {saveMutation.isPending ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Guardar Permisos
                    </>
                  )}
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}

// ==========================================
// SECCIÓN: HISTORIAL DE AJUSTES MANUALES DE SALDO
// ==========================================
function AjustesSaldoSection() {
  const { data: adjustments = [], isLoading } = useQuery({
    queryKey: ['admin-adjustments'],
    queryFn: async () => {
      const response = await client.get('/admin/adjustments')
      return response.data?.datos || []
    }
  })
  
  const {
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    searchTerm,
    setSearchTerm,
    sortField,
    sortOrder,
    requestSort,
    paginatedData: paginatedAdjustments,
    totalPages,
    totalRecords,
    startIndex,
    endIndex
  } = useDataTable({
    data: adjustments,
    searchFields: ['usuario_nombre', 'usuario_apellido', 'usuario_email', 'admin_nombre', 'admin_apellido', 'admin_email', 'motivo'],
    defaultSortField: 'creado_en',
    defaultSortOrder: 'desc'
  })

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-bold text-xl text-text-primary flex items-center gap-2">
          <Landmark className="w-6 h-6 text-cyan" /> Historial de Ajustes Manuales de Saldo
        </h2>
        <p className="text-text-secondary text-sm">
          Bitácora completa y auditoría de todas las acreditaciones manuales realizadas por los administradores a los usuarios.
        </p>
      </div>

      <Card className="p-4">
        <DataTableControls
          pageSize={pageSize}
          setPageSize={setPageSize}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Buscar por usuario, administrador o motivo..."
        />

        {isLoading ? (
          <div className="p-8 space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : totalRecords === 0 ? (
          <div className="p-8 text-center text-text-muted">
            <Landmark className="w-12 h-12 mx-auto mb-3 opacity-30 text-cyan animate-pulse" />
            <p>No se registran ajustes manuales de saldo.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-navy-dark/40 border-b border-border-subtle text-text-muted">
                  <SortableHeader field="id" currentField={sortField} sortOrder={sortOrder} label="ID" onSort={requestSort} className="px-6 py-4 font-semibold font-display uppercase tracking-wider text-xs" />
                  <SortableHeader field="usuario_nombre" currentField={sortField} sortOrder={sortOrder} label="Usuario Beneficiario" onSort={requestSort} className="px-6 py-4 font-semibold font-display uppercase tracking-wider text-xs" />
                  <SortableHeader field="admin_nombre" currentField={sortField} sortOrder={sortOrder} label="Administrador" onSort={requestSort} className="px-6 py-4 font-semibold font-display uppercase tracking-wider text-xs" />
                  <SortableHeader field="monto" currentField={sortField} sortOrder={sortOrder} label="Monto Acreditado" onSort={requestSort} className="px-6 py-4 font-semibold font-display uppercase tracking-wider text-xs" />
                  <SortableHeader field="motivo" currentField={sortField} sortOrder={sortOrder} label="Motivo" onSort={requestSort} className="px-6 py-4 font-semibold font-display uppercase tracking-wider text-xs" />
                  <SortableHeader field="creado_en" currentField={sortField} sortOrder={sortOrder} label="Fecha y Hora" onSort={requestSort} className="px-6 py-4 font-semibold font-display uppercase tracking-wider text-xs" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle bg-surface-card/10">
                {paginatedAdjustments.map((adj) => (
                  <tr key={adj.id} className="hover:bg-surface-card/30 transition-colors">
                    <td className="px-6 py-4 font-mono text-xs text-text-muted">
                      #{adj.id}
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-text-primary">{adj.usuario_nombre} {adj.usuario_apellido}</p>
                      <p className="text-xs text-text-muted font-mono">{adj.usuario_email}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-text-secondary">{adj.admin_nombre} {adj.admin_apellido}</p>
                      <p className="text-xs text-text-muted font-mono">{adj.admin_email}</p>
                    </td>
                    <td className="px-6 py-4 font-mono font-bold text-emerald-400">
                      +{Number(adj.monto).toFixed(2)} Bs
                    </td>
                    <td className="px-6 py-4 text-text-primary max-w-xs truncate font-display font-medium" title={adj.motivo}>
                      {adj.motivo}
                    </td>
                    <td className="px-6 py-4 text-xs text-text-secondary font-mono">
                      {new Date(adj.creado_en.replace(' ', 'T')).toLocaleString('es-BO')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DataTablePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          startIndex={startIndex}
          endIndex={endIndex}
          totalRecords={totalRecords}
        />
      </Card>
    </div>
  )
}

export default function AdminPanel() {
  const { user: loggedInUser } = useAuthStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') || 'dashboard'
  const setActiveTab = (tab) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('tab', tab)
    setSearchParams(newParams)
  }

  const hasAccessToTab = (tab) => {
    if (tab === 'dashboard') return true
    if (loggedInUser?.rol === 'superadmin' || loggedInUser?.rol === 'admin') {
      return true
    }
    
    if (tab === 'permisos' || tab === 'historial-apuestas') {
      return false
    }

    const permissionMap = {
      'resultados': 'gestionar_resultados',
      'partidos': 'gestionar_partidos',
      'equipos': 'gestionar_equipos',
      'jugadores': 'gestionar_jugadores',
      'usuarios': 'gestionar_usuarios',
      'recargas': 'aprobar_recargas',
      'retiros': 'aprobar_retiros',
      'finanzas': 'gestionar_finanzas',
      'ajustes-saldo': 'gestionar_finanzas',
      'apuestas': 'gestionar_apuestas',
      'transmisiones': 'gestionar_canales',
      'publicidad': 'gestionar_publicidad',
      'configuracion': 'gestionar_configuracion',
    }

    const requiredPermission = permissionMap[tab]
    return requiredPermission && loggedInUser?.permisos && loggedInUser.permisos[requiredPermission] === 1
  }

  React.useEffect(() => {
    if (loggedInUser && !hasAccessToTab(activeTab)) {
      setActiveTab('dashboard')
    }
  }, [activeTab, loggedInUser])

  // Obtener equipos para dropdowns en cascada
  const { data: teamsData } = useQuery({
    queryKey: ['admin-all-teams'],
    queryFn: async () => {
      const response = await client.get('/teams')
      return response.data?.datos || []
    }
  })
  const teams = teamsData || []

  // Obtener grupos para dropdowns
  const { data: groupsData } = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      const response = await client.get('/groups')
      return response.data?.datos || []
    }
  })
  const groups = groupsData || []

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Contenido según pestaña */}
      <ErrorBoundary>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'dashboard' && <DashboardSection setActiveTab={setActiveTab} />}
            {activeTab === 'resultados' && <ResultadosSection />}
            {activeTab === 'partidos' && <PartidosSection teams={teams} groups={groups} />}
            {activeTab === 'equipos' && <EquiposSection groups={groups} />}
            {activeTab === 'jugadores' && <JugadoresSection teams={teams} />}
            {activeTab === 'usuarios' && <UsuariosSection />}
            {activeTab === 'permisos' && <PermisosSection />}
            {activeTab === 'recargas' && <RecargasSection />}
            {activeTab === 'apuestas' && <ApuestasSection />}
            {activeTab === 'retiros' && <RetirosSection />}
            {activeTab === 'publicidad' && <PublicidadSection />}
            {activeTab === 'transmisiones' && <TransmisionesSection />}
            {activeTab === 'configuracion' && <ConfiguracionSection />}
            {activeTab === 'finanzas' && <FinanzasSection />}
            {activeTab === 'ajustes-saldo' && <AjustesSaldoSection />}
            {activeTab === 'historial-apuestas' && <HistorialApuestasSection />}
          </motion.div>

        </AnimatePresence>
      </ErrorBoundary>
    </div>
  )
}
